import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckIcon from '@mui/icons-material/Check';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  CircularProgress,
  Skeleton,
  Snackbar,
  Alert,
  Divider,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  InputAdornment,
  Chip,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PercentIcon from '@mui/icons-material/Percent';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import LockClockIcon from '@mui/icons-material/LockClock';

import config from '../../config';
import { getBrand } from '../brand';
import { requireUserId } from './userSession';
import { buildLogoEscPos, buildQrEscPos, buildUpiQrEscPos } from './escposLogo';
import { usePrinterRouting } from './printerService';
import ProcessPaymentDialog from './ProcessPaymentDialog';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '₹';
const token = sessionStorage.getItem('token');

/** Stable id for API + localStorage (session payload shape varies). */
const getStoredCompanyId = () =>
  company?.id ?? company?.companyId ?? company?.CompanyId ?? null;

// ── Color palette ──────────────────────────────────────────────────────────────
const C = {
  bg: '#F4F6FB',
  sidebar: '#FFFFFF',
  header: '#1C1F2E',
  accent: '#4361EE',
  accentLight: '#EEF1FD',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  text: '#1C1F2E',
  muted: '#8B92A5',
  border: '#E4E7F0',
  cardBg: '#FFFFFF',
  pill: '#F0F2FA',
  Tablebilled: 'rgb(79, 83, 99)',
  Tablebooked: '#121a2e',
};

// ══════════════════════════════════════════════════════════════════════════════
// LIQUOR / PEG HELPERS
// ══════════════════════════════════════════════════════════════════════════════
const LIQUOR_CATEGORY_KEYWORDS = ['liquor', 'beer', 'daru', 'wine', 'bar'];

const isLiquorCategoryName = (name) => {
  if (!name) return false;
  const s = String(name).toLowerCase();
  return LIQUOR_CATEGORY_KEYWORDS.some((kw) => s.includes(kw));
};

const parsePegMl = (label) => {
  if (label == null) return null;
  const m = String(label).match(/(\d+)\s*(ml|ML)?/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
};

/** Peg filter: full bottle vs ml peg size (liquor categories only). */
const PEG_FILTER_FULL = 'full';

/** Frontend-only cart row identity (product + variant / full bottle). */
const makeCartKey = (productId, variantId) => {
  const pid = Number(productId);
  if (variantId == null || variantId === 0 || variantId === '') {
    return `${pid}_full`;
  }
  return `${pid}_${Number(variantId)}`;
};

const parseCartKey = (cartKey) => {
  const s = String(cartKey);
  const idx = s.indexOf('_');
  if (idx < 0) {
    return {
      productId: parseInt(s, 10),
      orderType: 'full',
      variantId: null,
    };
  }
  const productId = parseInt(s.slice(0, idx), 10);
  const suffix = s.slice(idx + 1);
  if (suffix === 'full') {
    return { productId, orderType: 'full', variantId: null };
  }
  const variantId = parseInt(suffix, 10);
  return {
    productId,
    orderType: 'peg',
    variantId: Number.isFinite(variantId) ? variantId : null,
  };
};

const formatCartLineLabel = (item, variant, variantsByItem) => {
  if (variant?.subcategoryName) {
    const ml = parsePegMl(variant.subcategoryName);
    if (ml != null) return `${item.name} ${ml} ml`;
    return `${item.name} ${variant.subcategoryName}`;
  }
  const vars = variantsByItem?.[item.id] || [];
  const hasPegVariants = vars.some(
    (v) => parsePegMl(v.subcategoryName) != null,
  );
  if (hasPegVariants) return `${item.name} Full Bottle`;
  return item.name;
};

/** Apple-style GST / VAT switch */
const GST_SWITCH_SX = {
  width: 51,
  height: 31,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    top: 2,
    left: 2,
    transition: 'transform 320ms cubic-bezier(0.34, 1.25, 0.64, 1)',
    '&.Mui-checked': {
      transform: 'translateX(21px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        background: `linear-gradient(180deg, ${alpha(C.accent, 0.44)} 0%, ${alpha(C.accent, 0.3)} 100%)`,
        border: `1px solid ${alpha(C.accent, 0.2)}`,
        boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.42)}, 0 0 0 0.5px ${alpha(C.accent, 0.1)}`,
      },
      '& .MuiSwitch-thumb': {
        boxShadow:
          '0 2px 8px rgba(28, 31, 46, 0.2), 0 1px 3px rgba(28, 31, 46, 0.14)',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 26,
    height: 26,
    backgroundColor: '#fff',
    color: '#fff',
    boxShadow:
      '0 2px 5px rgba(28, 31, 46, 0.16), 0 1px 2px rgba(28, 31, 46, 0.1)',
    transition: 'box-shadow 240ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#E8EAEF',
    border: `1px solid ${alpha(C.text, 0.08)}`,
    boxShadow: `inset 0 1px 2px ${alpha('#fff', 0.85)}`,
    transition:
      'background 280ms cubic-bezier(0.4, 0, 0.2, 1), border-color 280ms ease, box-shadow 280ms ease',
  },
};

const getCurrency = (symbol) => {
  switch (symbol) {
    case '₹':
      return 'Rs.';
    case '$':
      return '$';
    case '€':
      return 'EUR';
    default:
      return symbol;
  }
};
const currency = getCurrency(Companycurrency);

// ── Helpers ────────────────────────────────────────────────────────────────────

// Getorderdtlbytable returns one row per ITEM across ALL open orders of the
// table/room, repeating each order's header on its own rows. Reading row[0]
// billed only the FIRST order, so the printed bill came out lower than the
// table grid (which totals every open order) — the guest was under-charged.
// Group by orderId and count each header exactly once.
//
// Headers are summed (not the item rows) on purpose: the header is what
// AddDiscount writes, so discounts stay honoured. On a single-order table the
// sum collapses to the old value, leaving every existing flow unchanged.
const billTotalsFor = (rows, applyGst) => {
  const heads = Array.from(
    (rows || [])
      .reduce((m, r) => {
        const oid = r.orderId ?? r.OrderId ?? 0;
        if (!m.has(oid)) m.set(oid, r);
        return m;
      }, new Map())
      .values(),
  );
  const sum = (pick) => heads.reduce((s, h) => s + Number(pick(h) || 0), 0);

  const total = sum((h) => h.finalPrice);
  const vatAmount = sum((h) => h.vataMount ?? h.vatAmount);
  // Rates are never summed and never hardcoded — recover the blended rate from
  // the stored amounts (prices are VAT/GST-inclusive).
  const vatBase = total - vatAmount;

  return {
    subtotal: sum((h) => h.price),
    total,
    cgst: applyGst ? sum((h) => h.cGst ?? h.cgst) : 0,
    sgst: applyGst ? sum((h) => h.sGst ?? h.sgst) : 0,
    gstAmount: applyGst ? sum((h) => h.gstAmount ?? h.gstamount) : 0,
    discount: sum((h) => h.dicountamount ?? h.discount),
    vatAmount,
    vatRate: vatBase > 0 ? Number(((vatAmount / vatBase) * 100).toFixed(2)) : 0,
  };
};

const getTableColor = (status) => {
  if (status === 0)
    return {
      bg: C.Tablebooked,
      text: '#fff',
      label: 'Booked',
      fontSize: '15px',
    };
  if (status === 1) return { bg: C.Tablebilled, text: '#fff', label: 'Billed' };
  return { bg: '#fff', text: C.text, label: 'Free' };
};

const LAYOUT_STORAGE_PREFIX = 'floorTableLayout_v1';
const layoutStorageKey = (companyId, areaId) =>
  `${LAYOUT_STORAGE_PREFIX}_${String(companyId)}_${String(areaId)}`;
const genLayoutId = () =>
  `id_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const normalizeLinkedTableId = (lid) => {
  if (lid === '' || lid == null) return null;
  if (typeof lid === 'number' && Number.isFinite(lid)) return lid;
  const s = String(lid).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return Number(s);
  return lid;
};

const normalizeFloorLayout = (layout) => {
  if (!layout?.rows?.length) return layout;
  return {
    rows: layout.rows.map((row) => ({
      id: row.id || genLayoutId(),
      slots: (row.slots || []).map((slot) => ({
        ...slot,
        id: slot.id || genLayoutId(),
        tableNumber: slot.tableNumber != null ? String(slot.tableNumber) : '',
        capacity:
          slot.capacity != null && slot.capacity !== ''
            ? Math.max(0, Number(slot.capacity))
            : 4,
        tableType: slot.tableType || 'Standard',
        linkedTableId: normalizeLinkedTableId(slot.linkedTableId),
      })),
    })),
  };
};

const loadFloorLayout = (companyId, areaId) => {
  if (!companyId || !areaId) return null;
  try {
    const raw = localStorage.getItem(layoutStorageKey(companyId, areaId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows || !Array.isArray(parsed.rows)) return null;
    if (
      !parsed.rows.length ||
      !parsed.rows.some((r) => Array.isArray(r.slots) && r.slots.length)
    )
      return null;
    return normalizeFloorLayout(parsed);
  } catch {
    return null;
  }
};

const saveFloorLayoutToStorage = (companyId, areaId, layout) => {
  if (!companyId || !areaId || !layout) return;
  try {
    localStorage.setItem(
      layoutStorageKey(companyId, areaId),
      JSON.stringify(layout),
    );
  } catch (e) {
    console.error('Floor layout save failed', e);
  }
};

const TABLE_TYPE_OPTIONS = ['Standard', 'Booth', 'Bar', 'Outdoor', 'VIP'];

const buildLayoutFromPattern = (pattern, apiTables) => {
  const counts = String(pattern)
    .split(/[-,\s]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => n > 0);
  if (!counts.length) return null;
  let ti = 0;
  const rows = counts.map(() => ({ id: genLayoutId(), slots: [] }));
  counts.forEach((n, ri) => {
    for (let j = 0; j < n; j += 1) {
      const t = apiTables[ti] || null;
      ti += 1;
      rows[ri].slots.push({
        id: genLayoutId(),
        tableNumber: t?.tableName != null ? String(t.tableName) : `T${ti}`,
        capacity: t?.capacity != null ? Number(t.capacity) : 4,
        tableType: 'Standard',
        linkedTableId: t?.tableId != null ? t.tableId : null,
      });
    }
  });
  return { rows };
};

const layoutFromApiTablesGrid = (apiTables, perRow = 4) => {
  if (!apiTables?.length) return null;
  const rows = [];
  for (let i = 0; i < apiTables.length; i += perRow) {
    const slice = apiTables.slice(i, i + perRow);
    rows.push({
      id: genLayoutId(),
      slots: slice.map((t) => ({
        id: genLayoutId(),
        tableNumber: String(t.tableName ?? ''),
        capacity: t.capacity != null ? Number(t.capacity) : 4,
        tableType: 'Standard',
        linkedTableId: t.tableId,
      })),
    });
  }
  return { rows };
};

// ── Payment method constants ──────────────────────────────────────────────────
const PAYMENT_METHODS = [
  { id: 'Cash', label: 'Cash', icon: PaymentsIcon, color: '#10B981' },
  { id: 'Card', label: 'Card', icon: CreditCardIcon, color: '#4361EE' },
  { id: 'UPI', label: 'UPI / Online', icon: QrCode2Icon, color: '#8B5CF6' },
  { id: 'Split', label: 'Split', icon: CallSplitIcon, color: '#F59E0B' },
  // Online payment gateway (CC Avenue / AllUPI) — opens the gateway for the
  // guest to pay; the order is marked paid by the gateway callback.
  { id: 'Online', label: 'Pay Online', icon: QrCode2Icon, color: '#0EA5E9' },
  { id: 'Due', label: 'Due / Later', icon: LockClockIcon, color: '#EF4444' },
];

const CARD_TYPES = ['Visa', 'MasterCard', 'RuPay', 'Amex', 'Other'];

// Default customer values when user skips entry
const DEFAULT_CUSTOMER = {
  name: 'Guest',
  mobile: '0000000000',
  address: 'Guest Address',
};

// ══════════════════════════════════════════════════════════════════════════════
// UPI QR PANEL — builds a UPI deep-link and renders a scannable QR. The QR
// regenerates whenever amount or UPI id changes because the image src URL is
// derived from them. Uses a public QR rendering service so no new dependency.
// ══════════════════════════════════════════════════════════════════════════════
const buildUpiUrl = (upiId, payeeName, amount) => {
  if (!upiId) return null;
  const safeName = String(payeeName || 'Restaurant').replace(/[#&?=]/g, '');
  const amt = Number(amount);
  const parts = [
    `pa=${encodeURIComponent(upiId)}`,
    `pn=${encodeURIComponent(safeName)}`,
    Number.isFinite(amt) && amt > 0
      ? `am=${encodeURIComponent(amt.toFixed(2))}`
      : null,
    `cu=INR`,
  ].filter(Boolean);
  return `upi://pay?${parts.join('&')}`;
};

const UpiQRPanel = ({
  upiId,
  payeeName,
  amount,
  Companycurrency,
  size = 200,
}) => {
  if (!upiId) {
    return (
      <Alert severity='warning' sx={{ borderRadius: 1.8 }}>
        UPI ID is not configured for this company. Add <code>upiId</code> in
        company settings to enable Scan &amp; Pay.
      </Alert>
    );
  }
  const upiUrl = buildUpiUrl(upiId, payeeName, amount);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&format=png&margin=10&data=${encodeURIComponent(
    upiUrl,
  )}`;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${C.border}`,
        bgcolor: '#F8FAFC',
      }}>
      <Box
        sx={{
          flexShrink: 0,
          p: 1,
          bgcolor: '#fff',
          borderRadius: 2,
          border: `1px solid ${C.border}`,
        }}>
        <img
          key={qrSrc}
          src={qrSrc}
          alt='UPI Scan and Pay QR'
          width={size}
          height={size}
          style={{ display: 'block' }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1rem',
            color: C.text,
            mb: 0.4,
            fontFamily: '"DM Sans", sans-serif',
          }}>
          Scan &amp; Pay
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', color: C.muted, mb: 1 }}>
          Open any UPI app, scan the code — the amount will pre-fill from this
          bill.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 0.5,
            borderTop: `1px dashed ${C.border}`,
          }}>
          <Typography
            sx={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600 }}>
            UPI ID
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              color: C.text,
              fontFamily: 'monospace',
            }}>
            {upiId}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.5,
            borderTop: `1px dashed ${C.border}`,
          }}>
          <Typography
            sx={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600 }}>
            Amount
          </Typography>
          <Typography
            sx={{
              fontSize: '1.05rem',
              fontWeight: 900,
              color: C.accent,
              fontFamily: '"DM Sans", sans-serif',
            }}>
            {Companycurrency}
            {Number(amount || 0).toFixed(2)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// VARIANT SELECTOR DIALOG (legacy — kept for "Select Variant" button path)
// ══════════════════════════════════════════════════════════════════════════════
const VariantSelectorDialog = ({
  open,
  parentItem,
  companyId,
  token,
  onSelect,
  onClose,
}) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !parentItem) return;
    setLoading(true);
    fetch(
      `${config.API_BASE_URL}/home/GetSubcategoriesByParent/${parentItem.id}/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => r.json())
      .then((d) => {
        setVariants(d || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [open, parentItem]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        Select Variant
        <Typography variant='body2' color='text.secondary'>
          {parentItem?.name}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box textAlign='center' py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List disablePadding>
            {variants.map((v) => (
              <ListItem key={v.id} disablePadding>
                <Box
                  onClick={() =>
                    onSelect({ ...v, isVariant: true, variantId: v.id })
                  }
                  sx={{
                    width: '100%',
                    px: 3,
                    py: 1.8,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${C.border}`,
                    '&:hover': { bgcolor: C.accentLight },
                  }}>
                  <Box>
                    <Typography fontWeight={700} fontSize='0.9rem'>
                      {v.subcategoryName}
                    </Typography>
                    <Typography fontSize='0.72rem' color='text.secondary'>
                      Consumes {v.consumptionQty} {v.consumptionUnit}
                    </Typography>
                  </Box>
                  <Typography
                    fontWeight={900}
                    fontSize='1rem'
                    color='primary.main'>
                    {Companycurrency}
                    {v.sellingPrice}
                  </Typography>
                </Box>
              </ListItem>
            ))}
            <ListItem disablePadding>
              <Box
                onClick={() => onSelect({ ...parentItem, isVariant: false })}
                sx={{
                  width: '100%',
                  px: 3,
                  py: 1.8,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': { bgcolor: '#F0FDF4' },
                }}>
                <Typography
                  fontWeight={600}
                  fontSize='0.85rem'
                  color='text.secondary'>
                  Order as regular item (no variant)
                </Typography>
                <Typography fontWeight={700} color='success.main'>
                  {Companycurrency}
                  {parentItem?.price}
                </Typography>
              </Box>
            </ListItem>
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPACT ORDER SIDEBAR
// Single-row item layout: [Name + variant chip] [- qty +] [Price input]
// Existing items are visually flagged with a small "EXISTING" pill but remain
// fully editable so the user can change qty / price for already-placed lines.
// ══════════════════════════════════════════════════════════════════════════════
const OrderSidebar = ({
  cartEntries,
  customPrices,
  handlePriceChange,
  handleQtyChange,
  clearQuantities,
  targetLabel,
  notes,
  setNotes,
  itemNotes,
  setItemNote,
  applyGst,
  setApplyGst,
  calculateTotal,
  handleNextFromItems,
  getEffectivePriceForCartKey,
  Companycurrency,
  getCartLineLabel,
  existingCartKeys,
  existingQtyMap,
  existingOrderLoaded,
  // Single-screen (PetPooja) extras — payment + place order live right here on
  // desktop so the user never leaves the items screen.
  paymentMethod,
  setPaymentMethod,
  paidAmount,
  setPaidAmount,
  splitPayments,
  setSplitPayments,
  onPlaceOrder,
  onMoreOptions,
  // Quick Bill collects the money now, so the deferred tenders are hidden.
  quickBill = false,
  onMarkPaid,
  submitting,
}) => {
  const [activeRowId, setActiveRowId] = useState(null);
  const selectedItems = cartEntries;
  const newCount = selectedItems.filter(
    (e) => !existingCartKeys.has(e.cartKey),
  ).length;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: C.sidebar,
      }}>
      {/* Sidebar header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.95rem',
                color: C.text,
                fontFamily: '"DM Sans", sans-serif',
                lineHeight: 1.1,
              }}>
              Current Order
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', color: C.muted, mt: 0.2 }}>
              {selectedItems.length} item{selectedItems.length === 1 ? '' : 's'}
              {existingOrderLoaded && existingCartKeys.size > 0 && (
                <>
                  {' · '}
                  <span style={{ color: C.accent, fontWeight: 700 }}>
                    {existingCartKeys.size} existing
                  </span>
                  {newCount > 0 && (
                    <>
                      {' · '}
                      <span style={{ color: C.green, fontWeight: 700 }}>
                        +{newCount} new
                      </span>
                    </>
                  )}
                </>
              )}
            </Typography>
          </Box>
          {selectedItems.length > 0 && (
            <Button
              size='small'
              onClick={clearQuantities}
              sx={{
                color: C.red,
                fontSize: '0.66rem',
                minWidth: 0,
                p: 0.4,
                '&:hover': { bgcolor: '#FEF2F2' },
              }}>
              Clear all
            </Button>
          )}
        </Box>
        {targetLabel && (
          <Box
            sx={{
              mt: 0.7,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: C.accentLight,
              borderRadius: 1.2,
              px: 1,
              py: 0.3,
            }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: C.accent,
              }}
            />
            <Typography
              sx={{ fontSize: '0.68rem', color: C.accent, fontWeight: 700 }}>
              {targetLabel}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Order items — COMPACT ROWS */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.2,
          py: 0.8,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 2 },
        }}>
        {selectedItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <ReceiptLongIcon sx={{ fontSize: 40, color: C.border, mb: 1 }} />
            <Typography sx={{ fontSize: '0.78rem', color: C.muted }}>
              No items added yet
            </Typography>
          </Box>
        ) : (
          selectedItems.map(({ cartKey, qty, item, variant }) => {
            const effPrice = getEffectivePriceForCartKey(
              cartKey,
              item,
              variant,
            );
            const isCustom =
              customPrices[cartKey] !== undefined &&
              Number(customPrices[cartKey]) !==
                (variant?.sellingPrice ?? item.price);
            const isExisting = existingCartKeys.has(cartKey);
            // existingQ = qty already on the running order (read-only).
            // added    = NEW qty the user is adding now (the stepper controls
            //            only this). qty (total) = existingQ + added.
            const existingQ = existingQtyMap?.[cartKey] || 0;
            const added = Math.max(0, qty - existingQ);
            const lineLabel = getCartLineLabel(item, variant);
            // Two-line display for ANY variant line (bar + food alike): variant
            // name on TOP, product name BELOW — e.g. "Half Plate" / "Pestro Paneer"
            // or "60 ml" / "Old Monk". Non-variant lines stay single-line.
            const variantTop = variant?.subcategoryName
              ? parsePegMl(variant.subcategoryName) != null
                ? `${parsePegMl(variant.subcategoryName)} ml`
                : variant.subcategoryName
              : null;

            const isActive = activeRowId === cartKey;
            return (
              <Box
                key={cartKey}
                onClick={() => setActiveRowId(cartKey)}
                sx={{
                  mb: 0.6,
                  px: 1,
                  py: 0.7,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  bgcolor: isActive
                    ? alpha(C.accent, 0.12)
                    : isExisting
                      ? alpha(C.accent, 0.04)
                      : '#fff',
                  border: `${isActive ? 2 : 1}px solid ${
                    isActive
                      ? C.accent
                      : isExisting
                        ? alpha(C.accent, 0.2)
                        : C.border
                  }`,
                  boxShadow: isActive
                    ? `0 2px 10px ${alpha(C.accent, 0.25)}`
                    : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isActive
                      ? alpha(C.accent, 0.14)
                      : isExisting
                        ? alpha(C.accent, 0.06)
                        : '#FAFBFF',
                  },
                }}>
                {/* MAIN ROW: name | qty | price */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                  }}>
                  {/* Name + meta */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                      }}>
                      {variantTop ? (
                        <Box title={lineLabel}>
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              color: C.accent,
                              lineHeight: 1.15,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                            {variantTop}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 700,
                              color: C.text,
                              lineHeight: 1.2,
                              fontFamily: '"DM Sans", sans-serif',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}>
                            {item.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: C.text,
                            lineHeight: 1.2,
                            fontFamily: '"DM Sans", sans-serif',
                            // Allow up to 2 lines so long names ("TestCold",
                            // "Ice Cream", "Red Label") don't get cut to "Ic…".
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                          title={lineLabel}>
                          {lineLabel}
                        </Typography>
                      )}
                      {isExisting && (
                        <Box
                          sx={{
                            flexShrink: 0,
                            fontSize: '0.5rem',
                            fontWeight: 800,
                            px: 0.5,
                            py: 0.05,
                            borderRadius: 0.6,
                            bgcolor: alpha(C.accent, 0.12),
                            color: C.accent,
                            letterSpacing: 0.3,
                          }}>
                          EXISTING
                        </Box>
                      )}
                      {isCustom && !variant && (
                        <Box
                          sx={{
                            flexShrink: 0,
                            fontSize: '0.5rem',
                            fontWeight: 800,
                            px: 0.5,
                            py: 0.05,
                            borderRadius: 0.6,
                            bgcolor: '#FFF7ED',
                            color: C.orange,
                            letterSpacing: 0.3,
                          }}>
                          MOD
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Existing (already-placed) qty — read-only, own colour, so
                      it's clearly separate from the new additions. */}
                  {existingQ > 0 && (
                    <Box
                      title='Already on the running order'
                      sx={{
                        flexShrink: 0,
                        mr: 0.5,
                        px: 0.7,
                        py: 0.2,
                        borderRadius: 1,
                        textAlign: 'center',
                        bgcolor: alpha(C.accent, 0.12),
                        border: `1px solid ${alpha(C.accent, 0.3)}`,
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.46rem',
                          fontWeight: 800,
                          letterSpacing: 0.3,
                          color: C.accent,
                          lineHeight: 1,
                        }}>
                        OLD
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 900,
                          color: C.accent,
                          lineHeight: 1.1,
                        }}>
                        {existingQ}
                      </Typography>
                    </Box>
                  )}
                  {/* Qty stepper — controls ONLY the new additions (not the
                      existing qty, which stays read-only above). */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: '#F9FAFB',
                      borderRadius: 5,
                      border: `1px solid ${C.border}`,
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}>
                    <IconButton
                      onClick={() =>
                        handleQtyChange(cartKey, Math.max(existingQ, qty - 1))
                      }
                      disabled={added <= 0}
                      size='small'
                      sx={{
                        width: 22,
                        height: 22,
                        color: C.red,
                        p: 0,
                        borderRadius: 0,
                      }}>
                      <RemoveIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        minWidth: 22,
                        textAlign: 'center',
                        color: added > 0 ? C.green : C.text,
                      }}>
                      {existingQ > 0 ? `+${added}` : qty}
                    </Typography>
                    <IconButton
                      onClick={() => handleQtyChange(cartKey, qty + 1)}
                      size='small'
                      sx={{
                        width: 22,
                        height: 22,
                        color: C.green,
                        p: 0,
                        borderRadius: 0,
                      }}>
                      <AddIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>

                  {/* Editable unit price — bold, with the currency symbol
                      built into the input via startAdornment. This is the
                      ONLY price control on the row; line total = qty × price
                      is shown in the tooltip on hover. Keeping it on one line
                      in the 320px sidebar. */}
                  <TextField
                    value={
                      customPrices[cartKey] !== undefined
                        ? customPrices[cartKey]
                        : (variant?.sellingPrice ?? item.price)
                    }
                    onChange={(e) => handlePriceChange(cartKey, e.target.value)}
                    size='small'
                    type='number'
                    title={`Unit price (editable). Line total: ${Companycurrency}${(qty * effPrice).toFixed(0)}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position='start'
                          sx={{
                            ml: 0,
                            mr: 0.2,
                            '& .MuiTypography-root': {
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: isCustom ? C.orange : C.muted,
                              lineHeight: 1,
                            },
                          }}>
                          {Companycurrency}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 78,
                      flexShrink: 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.2,
                        height: 26,
                        bgcolor: '#fff',
                        pl: 0.6,
                        '& fieldset': {
                          borderColor: isCustom ? C.orange : C.border,
                        },
                      },
                      '& input': {
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        color: isCustom ? C.orange : C.text,
                        p: '2px 4px',
                        textAlign: 'right',
                        MozAppearance: 'textfield',
                      },
                      '& input::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    inputProps={{ min: 0, step: 1 }}
                  />

                  {/* Delete — hidden for EXISTING (already-placed) items: those
                      must be removed via Cancel (with remark + KOT), not silently
                      dropped from this cart view (#5). New cart lines keep it. */}
                  {!isExisting && (
                    <IconButton
                      onClick={() => handleQtyChange(cartKey, 0)}
                      size='small'
                      sx={{
                        width: 22,
                        height: 22,
                        color: C.muted,
                        p: 0,
                        flexShrink: 0,
                        '&:hover': { color: C.red, bgcolor: '#FEF2F2' },
                      }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Item-wise note — prints on the KOT under this item */}
                <TextField
                  value={itemNotes?.[cartKey] || ''}
                  onChange={(e) => setItemNote(cartKey, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder='Item note (prints on KOT)'
                  size='small'
                  fullWidth
                  sx={{
                    mt: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.2,
                      bgcolor: '#fff',
                    },
                    '& input': { fontSize: '0.7rem', p: '4px 8px' },
                  }}
                />
              </Box>
            );
          })
        )}
      </Box>

      {/* Notes — compact */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          multiline
          rows={1}
          placeholder='Notes / Special Instructions'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size='small'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <NotesIcon sx={{ fontSize: 14, color: C.muted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: '#F9FAFB',
              py: 0.4,
              '& textarea': { fontSize: '0.72rem' },
            },
          }}
        />
      </Box>

      {/* GST toggle — compact */}
      <Box
        sx={{
          px: 1.5,
          pb: 0.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <PercentIcon
            sx={{ fontSize: 14, color: applyGst ? C.accent : C.muted }}
          />
          <Typography
            sx={{ fontSize: '0.74rem', fontWeight: 600, color: C.text }}>
            Apply GST / VAT
          </Typography>
        </Box>
        <Switch
          checked={applyGst}
          onChange={(e) => setApplyGst(e.target.checked)}
          disableRipple
          disableFocusRipple
          size='small'
          sx={GST_SWITCH_SX}
        />
      </Box>

      {/* Total + Payment + Place Order — single screen (PetPooja) */}
      <Box
        sx={{ px: 1.5, pb: 1.5, pt: 0.8, borderTop: `1px solid ${C.border}` }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}>
          <Typography
            sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.text }}>
            Total
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 900,
              color: C.accent,
              fontFamily: '"DM Sans", sans-serif',
            }}>
            {Companycurrency}
            {calculateTotal().toFixed(2)}
          </Typography>
        </Box>

        {/* Payment method pills */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1 }}>
          {PAYMENT_METHODS.filter(
            (m) => !(quickBill && (m.id === 'Due' || m.id === 'Online')),
          ).map(({ id, label, color }) => {
            const active = paymentMethod === id;
            return (
              <Box
                key={id}
                onClick={() => setPaymentMethod && setPaymentMethod(id)}
                sx={{
                  flex: '1 1 calc(33% - 6px)',
                  minWidth: 0,
                  cursor: 'pointer',
                  textAlign: 'center',
                  py: 0.55,
                  px: 0.4,
                  borderRadius: 1.4,
                  border: `1.5px solid ${active ? color : C.border}`,
                  bgcolor: active ? alpha(color, 0.1) : '#fff',
                  color: active ? color : C.text,
                  userSelect: 'none',
                }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800 }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* UPI / Online → scannable QR right here in the cart panel. */}
        {paymentMethod === 'UPI' && (
          <Box sx={{ mb: 1 }}>
            <UpiQRPanel
              upiId={company?.upiId}
              payeeName={company?.name}
              amount={
                Number(paidAmount) > 0 ? Number(paidAmount) : calculateTotal()
              }
              Companycurrency={Companycurrency}
              size={150}
            />
          </Box>
        )}

        {/* Paid amount — for non-Due, non-Split methods */}
        {(paymentMethod === 'Cash' ||
          paymentMethod === 'Card' ||
          paymentMethod === 'UPI') && (
          <TextField
            fullWidth
            size='small'
            type='number'
            placeholder='Paid amount'
            value={paidAmount}
            onChange={(e) => setPaidAmount && setPaidAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {Companycurrency}
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': { borderRadius: 1.4, height: 34 },
            }}
          />
        )}

        {/* Split → multiple payment legs inline (e.g. ₹100 Cash + ₹100 UPI).
            The Place Order flow already reads splitPayments for the Split
            method, so entering legs here is all that's needed. */}
        {paymentMethod === 'Split' && splitPayments && setSplitPayments && (
          <Box sx={{ mb: 1 }}>
            {splitPayments.map((sp, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  mb: 0.6,
                  alignItems: 'center',
                }}>
                <FormControl size='small' sx={{ minWidth: 82 }}>
                  <Select
                    value={sp.method}
                    onChange={(e) => {
                      const next = [...splitPayments];
                      next[idx] = { ...next[idx], method: e.target.value };
                      setSplitPayments(next);
                    }}
                    sx={{ fontSize: '0.72rem', height: 34, borderRadius: 1.4 }}>
                    <MenuItem value='Cash'>Cash</MenuItem>
                    <MenuItem value='Card'>Card</MenuItem>
                    <MenuItem value='UPI'>UPI</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size='small'
                  type='number'
                  placeholder='Amount'
                  value={sp.amount}
                  onChange={(e) => {
                    const next = [...splitPayments];
                    next[idx] = { ...next[idx], amount: e.target.value };
                    setSplitPayments(next);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        {Companycurrency}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: 1.4, height: 34 },
                  }}
                />
                <IconButton
                  size='small'
                  onClick={() => {
                    if (splitPayments.length <= 1) return;
                    setSplitPayments(splitPayments.filter((_, i) => i !== idx));
                  }}
                  sx={{ color: C.red, p: 0.3 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.2,
              }}>
              <Button
                size='small'
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() =>
                  setSplitPayments([
                    ...splitPayments,
                    {
                      method: 'Cash',
                      amount: '',
                      referenceNo: '',
                      cardType: 'Visa',
                      cardNumber: '',
                    },
                  ])
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.66rem',
                  minWidth: 0,
                  p: 0.3,
                }}>
                Add payment
              </Button>
              <Typography
                sx={{ fontSize: '0.7rem', fontWeight: 800, color: C.text }}>
                Paid: {Companycurrency}
                {splitPayments
                  .reduce((s, p) => s + (Number(p.amount) || 0), 0)
                  .toFixed(0)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Pay the EXISTING running order (no new items needed) — opens the
            multiple/split payment dialog with the pending amount. */}
        {existingOrderLoaded && onMarkPaid && (
          <Button
            fullWidth
            variant='outlined'
            onClick={onMarkPaid}
            startIcon={<PaymentsIcon sx={{ fontSize: 16 }} />}
            sx={{
              mb: 1,
              py: 0.95,
              borderRadius: 1.8,
              borderColor: C.green,
              color: C.green,
              fontWeight: 800,
              fontSize: '0.8rem',
              textTransform: 'none',
              '&:hover': { bgcolor: '#F0FDF4', borderColor: '#059669' },
            }}>
            Mark as Paid (pay existing order)
          </Button>
        )}

        <Button
          fullWidth
          variant='contained'
          disabled={submitting}
          onClick={onPlaceOrder}
          startIcon={
            submitting ? (
              <CircularProgress size={15} color='inherit' />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            py: 1.1,
            borderRadius: 1.8,
            bgcolor: C.green,
            fontWeight: 800,
            fontSize: '0.82rem',
            textTransform: 'none',
            boxShadow: 'none',
            letterSpacing: 0.3,
            '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
            '&:disabled': { bgcolor: '#6EE7B7' },
          }}>
          {submitting ? 'Placing...' : 'Place Order'}
        </Button>

        {/* Split tender / customer details / KOT preview live on the full
            review step — one tap away without leaving the data entered. */}
        <Button
          fullWidth
          size='small'
          onClick={onMoreOptions}
          sx={{
            mt: 0.6,
            textTransform: 'none',
            color: C.muted,
            fontSize: '0.7rem',
            fontWeight: 700,
          }}>
          Customer / Split / KOT options →
        </Button>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function RoomServiceOrder({ quickBill = false }) {
  // Quick Bill = exactly this screen minus the table/room picker. The order is
  // booked against a fixed "Takeaway" pseudo-table so it still surfaces in
  // Table Orders + Kitchen Pending (a real table is never chosen).
  const QUICK_TABLE = 'Takeaway';
  // Restaurant-only company (propertType 'R') has no rooms — hide the "By Room"
  // ordering tab so the screen never offers room-service in restaurant mode.
  const isRestaurantOnly = sessionStorage.getItem('propertType') === 'R';
  const [step, setStep] = useState(quickBill ? 1 : 0);
  const [mainTab, setMainTab] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [customPrices, setCustomPrices] = useState({});
  const [notes, setNotes] = useState('');
  // Per-item KOT notes, keyed by cartKey. Printed under each item on the KOT.
  const [itemNotes, setItemNotes] = useState({});
  const setItemNote = (cartKey, val) =>
    setItemNotes((prev) => ({ ...prev, [cartKey]: val }));
  const [itemSearch, setItemSearch] = useState('');
  // ── Quick-add (create new item / category from this screen) ──
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState('item'); // 'item' | 'category'
  const [qaSaving, setQaSaving] = useState(false);
  const [qaCatName, setQaCatName] = useState('');
  const [qaItemName, setQaItemName] = useState('');
  const [qaItemCat, setQaItemCat] = useState('');
  const [qaItemPrice, setQaItemPrice] = useState('');
  const [qaItemGst, setQaItemGst] = useState('5');
  const [qaDepartments, setQaDepartments] = useState([]);
  const [qaDept, setQaDept] = useState('');
  // Quick-add a new DEPARTMENT from the order screen (same as New Item /
  // New Category — just adds a department, no mapping).
  const [qaNewDeptName, setQaNewDeptName] = useState('');
  const [qaNewDeptPrinter, setQaNewDeptPrinter] = useState('');
  const [qaNewDeptMobile, setQaNewDeptMobile] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [actionType, setActionType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  // "All" tab — shows every area's tables together (one combined grid). When on,
  // the per-area fetch is skipped and we load all areas at once.
  const [allAreasMode, setAllAreasMode] = useState(false);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const tablesFetchSeqRef = useRef(0);
  const [selectedTable, setSelectedTable] = useState(null);
  const [finalOrderData, setFinalOrderData] = useState(null);
  const [allItems, setAllItems] = useState([]);
  // Centralised printer routing (Department → mapped printer, + bill printer).
  const { resolveKotPrinter, resolveBillPrinter } = usePrinterRouting();
  // "Mark as Paid" dialog for settling a loaded running order.
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  // Per-area (floor) price overrides — applied to item base price in the POS.
  const [areaPriceRows, setAreaPriceRows] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [roomApiFailed, setRoomApiFailed] = useState(false);
  const [applyGst, setApplyGst] = useState(true);
  const [floorLayout, setFloorLayout] = useState(null);
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [layoutPatternInput, setLayoutPatternInput] = useState('4-4-2');
  const layoutStorageTargetKeyRef = useRef(null);
  const [orderSidebarCollapsed, setOrderSidebarCollapsed] = useState(false);

  const [variantDialog, setVariantDialog] = useState({
    open: false,
    item: null,
  });
  const [variantSelections, setVariantSelections] = useState({});
  const [variantsByItem, setVariantsByItem] = useState({});

  // Peg pre-selection (Beer / Liquor / Daru)
  const [selectedPeg, setSelectedPeg] = useState(PEG_FILTER_FULL);
  const [focusedProductId, setFocusedProductId] = useState(null);

  // ── NEW: Existing-order state ──────────────────────────────────────────────
  // existingOrderItems holds the raw items already on this table (so we can
  // distinguish them in the sidebar and warn the user if they edit them).
  // existingItemIds = Set of itemIds for fast lookup.
  // existingOrderInfo holds metadata (orderId, kotNumber, etc) for reference.
  const [existingOrderItems, setExistingOrderItems] = useState([]);
  const [existingOrderInfo, setExistingOrderInfo] = useState(null);

  // ── NEW: Running totals per table (for table picker) ──────────────────────
  // Map of tableId -> running amount, fetched alongside tables.
  const [tableRunningTotals, setTableRunningTotals] = useState({});
  // Map of roomNo -> running restaurant-order amount, so an occupied room with
  // a live order gets a coloured card + amount exactly like the table grid.
  const [roomRunningTotals, setRoomRunningTotals] = useState({});

  // ── NEW: Payment state ─────────────────────────────────────────────────────
  // Quick Bill starts with NO tender selected so the user must consciously
  // pick one — defaulting to 'Due' on a pay-now screen invited a miss.
  const [paymentMethod, setPaymentMethod] = useState(quickBill ? '' : 'Due'); // Cash/Card/UPI/Split/Due/Online
  const [paidAmount, setPaidAmount] = useState(''); // user-entered
  // Online payment gateway (CC Avenue / AllUPI) — opened after the order saves.
  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [gatewayInfo, setGatewayInfo] = useState(null); // { orderId, amount }
  const [cardType, setCardType] = useState('Visa');
  const [cardNumber, setCardNumber] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  // Split payment: multiple sub-payments
  const [splitPayments, setSplitPayments] = useState([
    {
      method: 'Cash',
      amount: '',
      referenceNo: '',
      cardType: 'Visa',
      cardNumber: '',
    },
  ]);

  // ── NEW: Customer details ──────────────────────────────────────────────────
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  // #11: order fulfilment type + delivery rider.
  const [orderType, setOrderType] = useState('DineIn'); // DineIn|Takeaway|Pickup|Delivery
  const [riderName, setRiderName] = useState('');
  // #8: Rider Assignment popup (opens after a Delivery order is placed).
  const [riderDialog, setRiderDialog] = useState({
    open: false,
    tableno: '',
    name: '',
    status: 'Assigned',
    eta: '',
  });
  const saveRiderAssignment = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/restaurantorder/assign-rider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CompanyId: company?.id,
          Tableno: riderDialog.tableno,
          RiderName: riderDialog.name,
          DeliveryStatus: riderDialog.status,
          ExpectedDeliveryTime: riderDialog.eta,
        }),
      });
      if (!res.ok) throw new Error();
      setSnackbar?.({
        open: true,
        message: 'Rider assigned to delivery order.',
        severity: 'success',
      });
    } catch {
      setSnackbar?.({
        open: true,
        message: 'Could not assign rider (you can set it later).',
        severity: 'warning',
      });
    } finally {
      setRiderDialog((d) => ({ ...d, open: false }));
    }
  };
  // #3 Bill-to-company (restaurant bill in company name w/ GST)
  const [billToCompany, setBillToCompany] = useState(false);
  const [custCompanyName, setCustCompanyName] = useState('');
  const [custGstNo, setCustGstNo] = useState('');
  const [custCompanyAddress, setCustCompanyAddress] = useState('');

  const existingCartKeys = useMemo(
    () => new Set(existingOrderItems.map((ln) => ln.cartKey).filter(Boolean)),
    [existingOrderItems],
  );

  // Original (already-placed) qty per cart row. Shown SEPARATELY from the newly
  // added qty so the user isn't confused: the +/- stepper controls ONLY the new
  // additions; this existing qty is read-only and rendered in its own colour.
  // (Summed in case the running order has more than one row for an item.)
  const existingQtyMap = useMemo(() => {
    const m = {};
    existingOrderItems.forEach((ln) => {
      if (!ln.cartKey) return;
      m[ln.cartKey] = (m[ln.cartKey] || 0) + Number(ln.qty || 0);
    });
    return m;
  }, [existingOrderItems]);

  // ── Load / save floor layout ───────────────────────────────────────────────
  useEffect(() => {
    const cid = getStoredCompanyId();
    if (!cid || !selectedArea) return;
    setFloorLayout(loadFloorLayout(cid, selectedArea));
  }, [selectedArea]);

  useEffect(() => {
    const cid = getStoredCompanyId();
    if (!cid || !selectedArea) return;
    const key = `${cid}:${selectedArea}`;
    if (!floorLayout) {
      layoutStorageTargetKeyRef.current = key;
      return;
    }
    if (layoutStorageTargetKeyRef.current !== key) {
      layoutStorageTargetKeyRef.current = key;
      return;
    }
    saveFloorLayoutToStorage(cid, selectedArea, floorLayout);
  }, [floorLayout, selectedArea]);

  // ── Fetch Rooms ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetOccupiedRooms/${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error('Room API Failed');
        const data = await res.json();
        setRooms(Array.isArray(data) ? data : []);
      } catch {
        setRooms([]);
        setRoomApiFailed(true);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetallItemsAsync/${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        setAllItems(data || []);
      } catch (err) {
        console.error('Error fetching all items:', err);
      }
    };
    fetchAllItems();
  }, []);

  // Load per-area price overrides once; the active area decides which apply.
  useEffect(() => {
    fetch(`${config.API_BASE_URL}/areapricing/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setAreaPriceRows(Array.isArray(d) ? d : []))
      .catch(() => setAreaPriceRows([]));
  }, []);

  // itemId → price for the selected AREA (table/takeaway). Area rows only
  // (roomNumber null).
  const areaPriceMap = useMemo(() => {
    const m = {};
    areaPriceRows
      .filter(
        (p) =>
          Number(p.areaId) === Number(selectedArea) && p.roomNumber == null,
      )
      .forEach((p) => {
        m[p.itemId] = Number(p.price);
      });
    return m;
  }, [areaPriceRows, selectedArea]);

  // itemId → price for the selected ROOM (room service).
  const roomPriceMap = useMemo(() => {
    const m = {};
    if (selectedRoom)
      areaPriceRows
        .filter((p) => Number(p.roomNumber) === Number(selectedRoom))
        .forEach((p) => {
          m[p.itemId] = Number(p.price);
        });
    return m;
  }, [areaPriceRows, selectedRoom]);

  // itemId → price for the SPECIFIC selected table (most specific override).
  const tablePriceMap = useMemo(() => {
    const m = {};
    const tName = tables.find((t) => t.tableId === selectedTable)?.tableName;
    if (tName)
      areaPriceRows
        .filter((p) => (p.tableNo || '') === String(tName))
        .forEach((p) => {
          m[p.itemId] = Number(p.price);
        });
    return m;
  }, [areaPriceRows, selectedTable, tables]);

  // Room order → room price; table/takeaway → table price (if set) else area
  // price; else the item's base price. Variants keep their own selling price.
  const applyAreaPrice = useCallback(
    (item) => {
      if (!item) return item;
      let scoped;
      if (mainTab === 1 && selectedRoom) {
        scoped = roomPriceMap[item.id];
      } else {
        // A table-specific price beats the area price.
        scoped =
          tablePriceMap[item.id] != null
            ? tablePriceMap[item.id]
            : areaPriceMap[item.id];
      }
      return scoped != null ? { ...item, price: scoped } : item;
    },
    [areaPriceMap, roomPriceMap, tablePriceMap, mainTab, selectedRoom],
  );

  // Pre-load every active variant once per company.
  useEffect(() => {
    const cid = company?.id;
    if (!cid) return;
    (async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/home/GetSubcategories/${cid}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        (data || [])
          .filter((v) => v.activeStatus !== false)
          .forEach((v) => {
            const pid = v.parentItemId;
            if (!map[pid]) map[pid] = [];
            map[pid].push(v);
          });
        setVariantsByItem(map);
      } catch {
        /* optional */
      }
    })();
  }, [company?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tableIdFromQR = params.get('tableId');
    if (tableIdFromQR && tables.length > 0) {
      const id = parseInt(tableIdFromQR);
      if (tables.find((t) => t.tableId === id)) {
        setMainTab(0);
        setSelectedTable(id);
        setTimeout(() => setStep(1), 200);
      }
    }
  }, [location.search, tables]);

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/Home/GetAllCategory/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        console.log('API Raw Response:', d);

        const sorted = (d || [])
          .slice()
          .sort((a, b) =>
            (a.category_name || '').localeCompare(
              b.category_name || '',
              undefined,
              { sensitivity: 'base' },
            ),
          );

        console.log('Sorted Categories:', sorted);

        setCategories(sorted);

        if (sorted.length) {
          console.log('Selected Category ID:', sorted[0].id);
          setSelectedCategory(sorted[0].id);
        }
      })
      .catch((err) => {
        console.error('Category Fetch Error:', err);
      });
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    setLoading(true);
    fetch(`${config.API_BASE_URL}/Home/GetItems/${selectedCategory}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const sorted = (d || []).slice().sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          }),
        );
        setItems(sorted);
      })
      .finally(() => setLoading(false));
  }, [selectedCategory]);

  // Refresh helpers so a freshly-created category/item shows up immediately.
  const reloadCategories = (selectId) => {
    fetch(`${config.API_BASE_URL}/Home/GetAllCategory/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const sorted = (d || []).slice().sort((a, b) =>
          (a.category_name || '').localeCompare(
            b.category_name || '',
            undefined,
            {
              sensitivity: 'base',
            },
          ),
        );
        setCategories(sorted);
        if (selectId) setSelectedCategory(selectId);
      })
      .catch(() => {});
  };

  const reloadItems = (catId) => {
    const id = catId || selectedCategory;
    if (!id) return;
    fetch(`${config.API_BASE_URL}/Home/GetItems/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const sorted = (d || []).slice().sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          }),
        );
        setItems(sorted);
      })
      .catch(() => {});
  };

  // Refresh the GLOBAL item list (allItems). The cart (cartEntries), KOT and the
  // SaveOrder payload all resolve item id/price from `allItems` — NOT the
  // per-category `items` list. So a freshly Quick-Added item that isn't in
  // allItems yet got dropped from the cart and its FIRST order saved at ₹0 /
  // with no line; only after a later reload (which repopulated allItems) did the
  // SAME item order correctly. Refetching allItems here fixes that first order.
  const reloadAllItems = () => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/Home/GetallItemsAsync/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setAllItems(d || []))
      .catch(() => {});
  };

  const openQuickAdd = (mode) => {
    setQuickAddMode(mode);
    setQaCatName('');
    setQaItemName('');
    setQaItemPrice('');
    setQaItemGst('5');
    setQaItemCat(selectedCategory || '');
    setQaNewDeptName('');
    setQaNewDeptPrinter('');
    setQaNewDeptMobile('');
    setQuickAddOpen(true);
    if (mode === 'item') {
      fetch(`${config.API_BASE_URL}/Home/GetDepartments/${company?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((d) => {
          const list = Array.isArray(d) ? d : [];
          setQaDepartments(list);
          if (list.length) setQaDept(list[0].id ?? list[0].Id ?? '');
        })
        .catch(() => setQaDepartments([]));
    }
  };

  const saveQuickCategory = async () => {
    const name = qaCatName.trim();
    if (!name) return;
    setQaSaving(true);
    try {
      const fd = new FormData();
      fd.append('CategoryName', name);
      fd.append('CompanyId', company?.id);
      const res = await fetch(`${config.API_BASE_URL}/Home/CreateCategory`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      setSnackbar({
        open: true,
        message: 'Category created.',
        severity: 'success',
      });
      reloadCategories();
      setQuickAddOpen(false);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to create category.',
        severity: 'error',
      });
    } finally {
      setQaSaving(false);
    }
  };

  const saveQuickDepartment = async () => {
    const name = qaNewDeptName.trim();
    if (!name) {
      setSnackbar({
        open: true,
        message: 'Department name is required.',
        severity: 'warning',
      });
      return;
    }
    setQaSaving(true);
    try {
      const fd = new FormData();
      fd.append('Department', name);
      fd.append('Printername', qaNewDeptPrinter.trim());
      fd.append('Mobile', qaNewDeptMobile.trim());
      fd.append('Companyid', company?.id);
      fd.append('IsBillPrinter', 'false');
      const res = await fetch(`${config.API_BASE_URL}/home/AddDepartment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Failed');
      setSnackbar({
        open: true,
        message: 'Department created.',
        severity: 'success',
      });
      // Refresh the department list so it's available for new items.
      fetch(`${config.API_BASE_URL}/Home/GetDepartments/${company?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((d) => setQaDepartments(Array.isArray(d) ? d : []))
        .catch(() => {});
      setQuickAddOpen(false);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to create department.',
        severity: 'error',
      });
    } finally {
      setQaSaving(false);
    }
  };

  const saveQuickItem = async () => {
    if (!qaItemCat || !qaItemName.trim()) {
      setSnackbar({
        open: true,
        message: 'Category and item name are required.',
        severity: 'warning',
      });
      return;
    }
    // Guard against accidentally creating a duplicate item (same name).
    const dup = allItems.some(
      (i) =>
        (i.name || '').trim().toLowerCase() === qaItemName.trim().toLowerCase(),
    );
    if (dup) {
      setSnackbar({
        open: true,
        message: 'An item with this name already exists.',
        severity: 'warning',
      });
      return;
    }
    const dept = qaDepartments.find((d) => (d.id ?? d.Id) === qaDept);
    const printerName = dept?.Printername || dept?.printername || '';
    setQaSaving(true);
    try {
      const payload = {
        categoryId: qaItemCat,
        itemName: qaItemName.trim(),
        hsnCode: '',
        itemPrice: qaItemPrice ? parseFloat(qaItemPrice) : 0,
        gst: qaItemGst ? parseFloat(qaItemGst) : 0,
        vatRate: 0,
        isVatApplicable: false,
        isSellable: true,
        department: printerName,
        companyid: company?.id,
      };
      const res = await fetch(`${config.API_BASE_URL}/Home/CreateItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      setSnackbar({
        open: true,
        message: 'Item created.',
        severity: 'success',
      });
      // Show the new item: switch to its category and reload that list AND the
      // global allItems (so the cart/KOT/SaveOrder can resolve it on the very
      // first order — not just after a page reload).
      setSelectedCategory(qaItemCat);
      reloadItems(qaItemCat);
      reloadAllItems();
      setQuickAddOpen(false);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to create item.',
        severity: 'error',
      });
    } finally {
      setQaSaving(false);
    }
  };

  useEffect(() => {
    const cid = getStoredCompanyId();
    if (!cid) return;
    fetch(`${config.API_BASE_URL}/Home/GetAreas/${cid}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setAreas(d || []);
        if (d?.length) setSelectedArea(d[0].id);
      });
  }, []);

  useEffect(() => {
    // "All" tab loads every area together via loadAllAreasTables — skip the
    // single-area fetch so it doesn't overwrite the combined grid.
    if (allAreasMode) return;
    if (!selectedArea) return;
    const cid = getStoredCompanyId();
    if (!cid) return;

    const seq = ++tablesFetchSeqRef.current;
    setTables([]);
    setTableRunningTotals({});
    setTablesLoading(true);

    fetch(`${config.API_BASE_URL}/Home/GetTables/${selectedArea}/${cid}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (tablesFetchSeqRef.current !== seq) return;
        setTables(d || []);
      })
      .catch(() => {
        if (tablesFetchSeqRef.current !== seq) return;
        setTables([]);
      })
      .finally(() => {
        if (tablesFetchSeqRef.current === seq) setTablesLoading(false);
      });
  }, [selectedArea, allAreasMode]);

  // "All" tab — fetch every area's tables and show them in one combined grid.
  const loadAllAreasTables = useCallback(async () => {
    const cid = getStoredCompanyId();
    if (!cid || !areas?.length) return;
    const seq = ++tablesFetchSeqRef.current;
    setTables([]);
    setTableRunningTotals({});
    setTablesLoading(true);
    try {
      const lists = await Promise.all(
        areas.map((a) =>
          fetch(`${config.API_BASE_URL}/Home/GetTables/${a.id}/${cid}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => []),
        ),
      );
      if (tablesFetchSeqRef.current === seq) setTables(lists.flat());
    } finally {
      if (tablesFetchSeqRef.current === seq) setTablesLoading(false);
    }
  }, [areas]);

  // When All mode turns on (or areas load while it's on), load the combined grid.
  useEffect(() => {
    if (allAreasMode) loadAllAreasTables();
  }, [allAreasMode, loadAllAreasTables]);

  // ── NEW: Fetch running totals for booked/billed tables ──────────────────────
  // We hit GetTableDetails for each active table to derive a running total.
  // This is a best-effort secondary call — failures are silent so the table
  // grid still renders. The fetcher is extracted into a stable ref so we can
  // re-trigger it from the post-save handler AND from a periodic timer.
  const fetchTableRunningTotals = useCallback(
    async (opts = {}) => {
      const list = opts.tables ?? tables;
      if (!list?.length) return;
      const activeTables = list.filter((t) => t.status === 0 || t.status === 1);
      if (!activeTables.length) {
        setTableRunningTotals({});
        return;
      }
      // ONE call returns the running total for EVERY open table — replaces the
      // old per-table GetTableDetails loop (an N+1 that made the table grid slow
      // to load). The backend sums OrderDetail.FinalPrice, so no client-side
      // price resolution is needed.
      const totals = {};
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/restaurantorder/runningtotals/${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        const byTableNo = {};
        (Array.isArray(data) ? data : []).forEach((r) => {
          byTableNo[String(r.tableno).trim()] = Number(r.total) || 0;
        });
        activeTables.forEach((t) => {
          const key = String(t.tableName).trim();
          if (byTableNo[key] != null) totals[t.tableId] = byTableNo[key];
        });
      } catch (err) {
        console.warn('[CreateOrder] runningtotals failed:', err);
      }
      setTableRunningTotals(totals);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables, company?.id, token, allItems],
  );

  // Initial + tables-changed refresh
  useEffect(() => {
    if (!tables?.length) return;
    fetchTableRunningTotals();
  }, [tables, fetchTableRunningTotals]);

  // Periodic refresh — only while the user is on the table picker (step 0).
  // 30s cadence keeps the UI fresh without hammering the API.
  useEffect(() => {
    if (step !== 0 || !tables?.length) return;
    const id = setInterval(() => {
      fetchTableRunningTotals();
    }, 30000);
    return () => clearInterval(id);
  }, [step, tables, fetchTableRunningTotals]);

  // Per-room running restaurant-order totals — the room equivalent of
  // fetchTableRunningTotals. ONE call returns every room with a live order.
  const fetchRoomRunningTotals = useCallback(async () => {
    if (!company?.id) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/roomrunningtotals/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      const totals = {};
      (Array.isArray(data) ? data : []).forEach((row) => {
        totals[String(row.roomno).trim()] = Number(row.total) || 0;
      });
      setRoomRunningTotals(totals);
    } catch (err) {
      console.warn('[CreateOrder] roomrunningtotals failed:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, token]);

  // Initial + rooms-changed refresh, and a 30s poll while on the picker.
  useEffect(() => {
    fetchRoomRunningTotals();
  }, [rooms, fetchRoomRunningTotals]);

  useEffect(() => {
    if (step !== 0) return;
    const id = setInterval(() => {
      fetchRoomRunningTotals();
    }, 30000);
    return () => clearInterval(id);
  }, [step, fetchRoomRunningTotals]);

  // Reset peg when category changes
  useEffect(() => {
    setSelectedPeg(null);
  }, [selectedCategory]);

  useEffect(() => {
    setFocusedProductId(null);
  }, [selectedCategory, itemSearch]);

  // ── Layout helpers (unchanged) ──────────────────────────────────────────────
  const resolveApiTableForSlot = (slot) => {
    if (slot?.linkedTableId != null && slot.linkedTableId !== '') {
      return tables.find(
        (t) =>
          t.tableId === slot.linkedTableId ||
          String(t.tableId) === String(slot.linkedTableId),
      );
    }
    return tables.find((t) => String(t.tableName) === String(slot.tableNumber));
  };

  const hasFloorLayout = Boolean(
    floorLayout?.rows?.length && floorLayout.rows.some((r) => r.slots?.length),
  );

  const updateFloorSlot = (rowIndex, slotIndex, patch) => {
    setFloorLayout((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          ...r,
          slots: r.slots.map((s, si) =>
            si === slotIndex ? { ...s, ...patch } : s,
          ),
        };
      });
      return { rows };
    });
  };

  const addFloorRow = () => {
    setFloorLayout((prev) => {
      const slot = {
        id: genLayoutId(),
        tableNumber: 'New',
        capacity: 4,
        tableType: 'Standard',
        linkedTableId: null,
      };
      const row = { id: genLayoutId(), slots: [slot] };
      if (!prev?.rows?.length) return { rows: [row] };
      return { rows: [...prev.rows, row] };
    });
  };

  const removeFloorRow = (rowIndex) => {
    setFloorLayout((prev) => {
      if (!prev?.rows || prev.rows.length <= 1) return prev;
      return { rows: prev.rows.filter((_, i) => i !== rowIndex) };
    });
  };

  const addTableToRow = (rowIndex) => {
    setFloorLayout((prev) => {
      const slot = {
        id: genLayoutId(),
        tableNumber: 'New',
        capacity: 4,
        tableType: 'Standard',
        linkedTableId: null,
      };
      if (!prev?.rows?.length)
        return { rows: [{ id: genLayoutId(), slots: [slot] }] };
      const rows = prev.rows.map((r, ri) =>
        ri === rowIndex ? { ...r, slots: [...r.slots, slot] } : r,
      );
      return { rows };
    });
  };

  const removeSlotFromRow = (rowIndex, slotIndex) => {
    setFloorLayout((prev) => {
      if (!prev?.rows) return prev;
      const row = prev.rows[rowIndex];
      if (!row) return prev;
      if (row.slots.length > 1) {
        return {
          rows: prev.rows.map((r, ri) =>
            ri === rowIndex
              ? { ...r, slots: r.slots.filter((_, si) => si !== slotIndex) }
              : r,
          ),
        };
      }
      if (prev.rows.length > 1)
        return { rows: prev.rows.filter((_, ri) => ri !== rowIndex) };
      return prev;
    });
  };

  const handleSlotDragStart = (e, fromRow, fromSlot) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ fromRow, fromSlot }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSlotDragOver = (e) => {
    e.preventDefault();
  };

  const handleSlotDrop = (e, toRow, toSlot) => {
    e.preventDefault();
    let parsed;
    try {
      parsed = JSON.parse(e.dataTransfer.getData('text/plain'));
    } catch {
      return;
    }
    const { fromRow, fromSlot } = parsed;
    if (fromRow == null || fromSlot == null) return;
    setFloorLayout((prev) => {
      if (!prev?.rows) return prev;
      const rows = prev.rows.map((r) => ({ ...r, slots: [...r.slots] }));
      if (!rows[fromRow]?.slots[fromSlot]) return prev;
      const [moved] = rows[fromRow].slots.splice(fromSlot, 1);
      let ins = toSlot;
      if (fromRow === toRow && fromSlot < toSlot) ins -= 1;
      ins = Math.max(0, Math.min(ins, rows[toRow].slots.length));
      rows[toRow].slots.splice(ins, 0, moved);
      return { rows };
    });
  };

  const clearStoredFloorLayout = () => {
    const cid = getStoredCompanyId();
    if (cid && selectedArea) {
      try {
        localStorage.removeItem(layoutStorageKey(cid, selectedArea));
      } catch (_) {
        /* ignore */
      }
    }
    setFloorLayout(null);
    setSnackbar({
      open: true,
      message: 'Saved layout cleared. Default grid restored.',
      severity: 'info',
    });
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = itemSearch.toLowerCase().trim();
    const list = q
      ? allItems.filter((item) =>
          (item.name || '').toLowerCase().includes(q) ||
          (item.hsncode || item.Hsncode || '').toLowerCase().includes(q) ||
          (item.categoryName || item.department || '').toLowerCase().includes(q),
        )
      : items;
    // Defensive de-dup: if the DB has duplicate item rows (same name), show
    // each item only once so the POS doesn't display "2-2 naam". Keeps the
    // first occurrence. (Real fix is to clean the duplicate rows in the DB.)
    const seen = new Set();
    const deduped = list.filter((it) => {
      const key = (it.name || '').trim().toLowerCase();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return deduped.map(applyAreaPrice).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
      }),
    );
  }, [itemSearch, items, allItems, applyAreaPrice]);

  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) =>
        (a.category_name || '').localeCompare(
          b.category_name || '',
          undefined,
          { sensitivity: 'base' },
        ),
      ),
    [categories],
  );

  const activeCategoryName =
    categories.find((c) => c.id === selectedCategory)?.category_name || '';
  const activeCategoryIsLiquor =
    !itemSearch.trim() && isLiquorCategoryName(activeCategoryName);

  const findVariantForPeg = (itemId, pegMl) => {
    if (!pegMl) return null;
    const list = variantsByItem[itemId] || [];
    return list.find((v) => parsePegMl(v.subcategoryName) === pegMl) || null;
  };

  const pegOptions = (() => {
    if (!activeCategoryIsLiquor) return [];
    const set = new Set();
    visibleItems.forEach((item) => {
      const list = variantsByItem[item.id] || [];
      list.forEach((v) => {
        const ml = parsePegMl(v.subcategoryName);
        if (ml) set.add(ml);
      });
    });
    return Array.from(set).sort((a, b) => a - b);
  })();

  const filteredVisibleItems = (() => {
    if (!activeCategoryIsLiquor || selectedPeg === PEG_FILTER_FULL) {
      return visibleItems;
    }
    if (typeof selectedPeg === 'number') {
      return visibleItems.filter(
        (item) => findVariantForPeg(item.id, selectedPeg) != null,
      );
    }
    return visibleItems;
  })();

  const getCartLineLabel = useCallback(
    (item, variant) => formatCartLineLabel(item, variant, variantsByItem),
    [variantsByItem],
  );

  const cartEntries = useMemo(() => {
    return Object.entries(quantities)
      .filter(([, q]) => Number(q) > 0)
      .map(([cartKey, qty]) => {
        const parsed = parseCartKey(cartKey);
        // Resolve from allItems, but FALL BACK to the current category's `items`
        // — a just-Quick-Added item isn't in allItems yet, so without this it got
        // filtered out of the cart and its first order saved empty / at ₹0.
        const item = applyAreaPrice(
          allItems.find((i) => i.id === parsed.productId) ||
            items.find((i) => i.id === parsed.productId),
        );
        const variant = variantSelections[cartKey] || null;
        return { cartKey, qty: Number(qty), item, variant, parsed };
      })
      .filter((e) => e.item)
      .sort((a, b) =>
        getCartLineLabel(a.item, a.variant).localeCompare(
          getCartLineLabel(b.item, b.variant),
          undefined,
          { sensitivity: 'base' },
        ),
      );
  }, [
    quantities,
    allItems,
    items,
    variantSelections,
    getCartLineLabel,
    applyAreaPrice,
  ]);

  const getDisplayPriceForItem = useCallback(
    (item, qty, variant) => {
      if (activeCategoryIsLiquor && selectedPeg === PEG_FILTER_FULL) {
        return Number(item.price).toFixed(0);
      }
      if (activeCategoryIsLiquor && typeof selectedPeg === 'number') {
        const pegVariant = findVariantForPeg(item.id, selectedPeg);
        if (pegVariant) return Number(pegVariant.sellingPrice).toFixed(0);
      }
      if (qty > 0 && variant) {
        return Number(variant.sellingPrice).toFixed(0);
      }
      return Number(item.price).toFixed(0);
    },
    [activeCategoryIsLiquor, selectedPeg, variantsByItem],
  );

  useEffect(() => {
    setSelectedPeg(PEG_FILTER_FULL);
  }, [selectedCategory]);

  const getItemName = (id) => {
    const item = allItems.find((i) => i.id === id || i.ItemId === id);
    return item?.name || 'Item';
  };

  const getCartKeyForPegFilter = useCallback(
    (productId) => {
      if (selectedPeg === PEG_FILTER_FULL) {
        return makeCartKey(productId, null);
      }
      if (typeof selectedPeg === 'number') {
        const matched = findVariantForPeg(productId, selectedPeg);
        if (!matched) return null;
        return makeCartKey(productId, matched.variantId ?? matched.id);
      }
      return makeCartKey(productId, null);
    },
    [selectedPeg, variantsByItem],
  );

  const getEffectivePriceForCartKey = useCallback(
    (cartKey, item, variant) => {
      if (customPrices[cartKey] !== undefined) {
        return Number(customPrices[cartKey]) || item?.price || 0;
      }
      if (variant?.sellingPrice != null) return Number(variant.sellingPrice);
      return item?.price || 0;
    },
    [customPrices],
  );

  const handleQtyChange = (cartKey, value) => {
    const num = value === '' ? 0 : Math.max(0, Number(value));
    setQuantities((prev) => ({ ...prev, [cartKey]: num }));
    if (num === 0) {
      setVariantSelections((prev) => {
        if (!(cartKey in prev)) return prev;
        const next = { ...prev };
        delete next[cartKey];
        return next;
      });
      setCustomPrices((prev) => {
        if (!(cartKey in prev)) return prev;
        const next = { ...prev };
        delete next[cartKey];
        return next;
      });
    }
  };

  const handlePriceChange = (cartKey, value) =>
    setCustomPrices((prev) => ({ ...prev, [cartKey]: value }));

  const calculateTotal = () =>
    cartEntries.reduce(
      (sum, { cartKey, qty, item, variant }) =>
        sum + qty * getEffectivePriceForCartKey(cartKey, item, variant),
      0,
    );

  // Running total of just the EXISTING (already-placed) order — the amount
  // pending to settle when the user taps "Mark as Paid" without adding items.
  const calcExistingTotal = () =>
    cartEntries.reduce((sum, { cartKey, item, variant }) => {
      const exQty = existingQtyMap[cartKey] || 0;
      return sum + exQty * getEffectivePriceForCartKey(cartKey, item, variant);
    }, 0);

  const clearQuantities = () => {
    setQuantities({});
    setCustomPrices({});
    setNotes('');
    setItemSearch('');
    setVariantSelections({});
    setExistingOrderItems([]);
    setExistingOrderInfo(null);
  };

  const selectedItemsCount = Object.values(quantities).filter(
    (q) => q > 0,
  ).length;

  const targetLabel = quickBill
    ? QUICK_TABLE
    : selectedTable
      ? `Table ${tables.find((t) => t.tableId === selectedTable)?.tableName || ''}`
      : selectedRoom
        ? `Room ${selectedRoom}`
        : '';

  const getItemsByDepartment = () => {
    const grouped = {};
    cartEntries.forEach(({ cartKey, qty, item, variant }) => {
      // Only the NEWLY ADDED qty goes on the KOT / new-order ticket — the
      // existing (already-placed) qty was already printed on its original KOT,
      // so we never re-send it. "Add 4 to an existing 3" → KOT shows 4.
      const existingQ = existingQtyMap[cartKey] || 0;
      const addQty = Number(qty) - existingQ;
      if (addQty <= 0) return;
      const dept = item.department || 'General';
      grouped[dept] = grouped[dept] || [];
      grouped[dept].push({
        ...item,
        _cartKey: cartKey,
        _qty: addQty,
        _variant: variant,
        _note: itemNotes[cartKey] || '',
      });
    });
    return grouped;
  };

  const getKotNumber = () => {
    const today = new Date().toLocaleDateString('en-GB');
    const lastDate = localStorage.getItem('kotDate');
    if (lastDate !== today) {
      localStorage.setItem('kotNo', 1);
      localStorage.setItem('kotDate', today);
    }
    let kotNo = parseInt(localStorage.getItem('kotNo') || 1);
    localStorage.setItem('kotNo', kotNo + 1);
    return kotNo;
  };

  // ── Variant handlers ───────────────────────────────────────────────────────
  const handleAddItemClick = (item) => {
    if (activeCategoryIsLiquor) {
      if (selectedPeg === PEG_FILTER_FULL) {
        const cartKey = makeCartKey(item.id, null);
        handleQtyChange(cartKey, (quantities[cartKey] || 0) + 1);
        return;
      }
      if (typeof selectedPeg === 'number') {
        const matched = findVariantForPeg(item.id, selectedPeg);
        if (matched) {
          pickVariant(item.id, matched, { increment: true });
          return;
        }
        setSnackbar({
          open: true,
          message: `This item has no ${selectedPeg} ml peg.`,
          severity: 'warning',
        });
        return;
      }
    }
    const variantsForItem = variantsByItem[item.id] || [];
    if (variantsForItem.length > 0) {
      setVariantDialog({ open: true, item });
      return;
    }
    const cartKey = makeCartKey(item.id, null);
    handleQtyChange(cartKey, (quantities[cartKey] || 0) + 1);
  };

  const pickVariant = (parentId, variant, options = {}) => {
    const { increment = false } = options;
    const variantId = variant.variantId ?? variant.id;
    const cartKey = makeCartKey(parentId, variantId);
    const normalized = {
      ...variant,
      isVariant: true,
      variantId,
    };
    setVariantSelections((prev) => ({ ...prev, [cartKey]: normalized }));
    setCustomPrices((prev) => ({
      ...prev,
      [cartKey]: variant.sellingPrice,
    }));
    setQuantities((prev) => ({
      ...prev,
      [cartKey]: increment
        ? (prev[cartKey] || 0) + 1
        : Math.max(prev[cartKey] || 0, 1),
    }));
  };

  const handleVariantSelect = (variant) => {
    const parentId = variantDialog.item?.id;
    if (parentId == null) return;
    if (variant.isVariant) {
      pickVariant(parentId, variant, { increment: true });
    } else {
      const cartKey = makeCartKey(parentId, null);
      handleQtyChange(cartKey, (quantities[cartKey] || 0) + 1);
    }
    setVariantDialog({ open: false, item: null });
  };

  // POST a WhatsApp "restaurant_order_update_document" — to the CUSTOMER (mobile)
  // or to a KOT DEPARTMENT (backend resolves its number from `department`).
  // Best-effort: a failure never blocks the order / KOT. Server skips silently
  // when there's no number (e.g. a walk-in order with no customer mobile).
  const sendOrderWhatsApp = async ({ mobile, department, orderNo, tableNo }) => {
    try {
      const now = new Date();
      await fetch(`${config.API_BASE_URL}/Home/SendOrderWhatsApp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Mobile: (mobile || '').toString().trim(),
          Department: department || '',
          CompanyId: company?.id,
          OrderNo: String(orderNo ?? ''),
          TableNo: tableNo || targetLabel || '',
          Date: now.toLocaleDateString('en-GB'),
          Time: now.toLocaleTimeString(),
        }),
      });
    } catch (e) {
      console.warn('SendOrderWhatsApp failed:', e);
    }
  };

  // ── KOT Printer (unchanged) ─────────────────────────────────────────────────
  const sendKotToPrinterAPI = async (kotNo) => {
    const grouped = getItemsByDepartment();
    const machineId = window.location.hostname;
    // Print the SAME KOT number the order was saved with (kotNo). The old
    // "kotNo + 1" made the printed ticket show a different number than what was
    // stored — so a reprint never matched. (getKotNumber already advanced the
    // counter, so don't bump it again here.)
    const newKot = kotNo;
    for (const [dept, deptItems] of Object.entries(grouped)) {
      // Items with no department used to be SKIPPED (so they never printed).
      // Route them to a default "Kitchen" printer instead so EVERY item appears
      // on a KOT.
      // Centralised: Department → mapped printer. Prefer the printername the API
      // stamped on the item, then the department mapping, then fallback.
      const itemPrinter = deptItems
        .map((it) => it.printername)
        .find((p) => p && String(p).trim());
      const printerName = resolveKotPrinter(dept, { itemPrinter });
      const ITEM_W = 42;
      const QTY_W = 6;
      const itemsText = deptItems
        .map((item) => {
          const variant = item._variant;
          const subPrefix = variant?.subcategoryName
            ? `${variant.subcategoryName} `
            : '';
          const full = String(`${subPrefix}${item.name || ''}`).trim();
          const q = String(item._qty || 0).padStart(QTY_W);
          let ln = full.slice(0, ITEM_W).padEnd(ITEM_W) + q;
          for (let i = ITEM_W; i < full.length; i += ITEM_W) {
            ln += '\n' + full.slice(i, i + ITEM_W);
          }
          // Item-wise note prints on its own indented line under the item.
          if (item._note && item._note.trim()) {
            ln += '\n  * ' + item._note.trim();
          }
          return ln;
        })
        .join('\n');
      const totalKotQty = deptItems.reduce(
        (s, it) => s + Number(it._qty || 0),
        0,
      );
      const now = new Date();
      const date = now.toLocaleDateString('en-GB');
      const time = now.toLocaleTimeString();
      const W = 48;
      const solid = '-'.repeat(W) + '\n';
      const dbl = '='.repeat(W) + '\n';
      let data = '\x1B\x40\x1B\x61\x01\x1B\x45\x01';
      data += (company?.name || 'HOTEL') + '\n\x1B\x45\x00';
      if (company?.address1) data += company.address1 + '\n';
      data += dbl + '\x1B\x45\x01KITCHEN ORDER TICKET\n\x1B\x45\x00' + dbl;
      data += '\x1B\x61\x00';
      data += `KOT #: ${newKot}   ${targetLabel || 'DINE-IN'}\n`;
      data += `Dept : ${dept}\n`;
      data += `Date : ${date}  ${time}\n`;
      data += solid;
      data +=
        '\x1B\x45\x01' +
        'ITEM'.padEnd(ITEM_W) +
        'QTY'.padStart(QTY_W) +
        '\n\x1B\x45\x00';
      data += solid;
      // Item lines double-height + bold for quick kitchen reading.
      data +=
        '\x1D\x21\x01\x1B\x45\x01' +
        itemsText +
        '\n' +
        '\x1D\x21\x00\x1B\x45\x00';
      data += solid;
      data +=
        'Total Qty'.padEnd(ITEM_W) + String(totalKotQty).padStart(QTY_W) + '\n';
      if (notes && notes.trim()) data += 'Notes: ' + notes.trim() + '\n';
      data += dbl;
      data += '\n\n\n\x1D\x56\x00';
      try {
        const res = await fetch(`${config.API_BASE_URL}/Service/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'KOT',
            content: data,
            machineId,
            companyid: company?.id,
            printerName: printerName,
          }),
        });
        // Surface a non-OK (e.g. 400 "no printer/machine registered") instead of
        // silently swallowing it — the kitchen never gets the ticket otherwise.
        if (!res.ok) {
          const m = await res.text().catch(() => '');
          console.error('KOT print failed:', res.status, m);
          setSnackbar({
            open: true,
            message:
              'Order saved, but KOT print failed (printer/machine not registered).',
            severity: 'warning',
          });
        }
      } catch (err) {
        console.error('KOT print error:', err);
        setSnackbar({
          open: true,
          message: 'Order saved, but KOT could not be sent to the printer.',
          severity: 'warning',
        });
      }
      // Phase 5: WhatsApp this KOT to the department number.
      await sendOrderWhatsApp({
        department: dept,
        orderNo: newKot,
        tableNo: targetLabel,
      });
    }
  };

  // ── Cancel / Edit KOT (#2) ──────────────────────────────────────────────────
  // When an already-placed item is CANCELLED or EDITED, the kitchen needs a
  // ticket so they stop / adjust cooking. Prints a single-item KOT marked
  // CANCELLED or EDITED. Best-effort — never blocks the cancel/edit itself.
  const printChangeKot = async (item, qty, mode, remark) => {
    try {
      const machineId = window.location.hostname;
      // The active-order row has no department/printer on it. Resolve the MENU
      // item from allItems (the API stamps its real printer there) and route the
      // SAME way the order-time KOT does — so the printerName field carries the
      // actual PRINTER name, not the department label.
      const menuItem = allItems.find(
        (i) => String(i.id) === String(item?.itemid ?? item?.itemId ?? item?.id),
      );
      const printerName = resolveKotPrinter(menuItem?.department || '', {
        itemPrinter: menuItem?.printername,
      });
      const now = new Date();
      const date = now.toLocaleDateString('en-GB');
      const time = now.toLocaleTimeString();
      const divider = '--------------------------------\n';
      const nm = String(item?.name || '')
        .substring(0, 26)
        .padEnd(26, ' ');
      const q = String(qty ?? item?.qty ?? 0).padStart(4, ' ');
      let data = '\x1B\x40\x1B\x61\x01\x1B\x45\x01';
      data += (company?.name || 'HOTEL') + '\n\x1B\x45\x00';
      data += divider + `\x1B\x45\x01KOT - ${mode}\n\x1B\x45\x00` + divider;
      data += '\x1B\x61\x00';
      data += `KOT   : ${item?.kotNumber ?? ''}\nTYPE  : ${targetLabel || 'DINE-IN'}\nDATE  : ${date}\nTIME  : ${time}\n`;
      data +=
        divider +
        '\x1B\x45\x01ITEM                      QTY\n\x1B\x45\x00' +
        divider;
      data += `${nm}${q}\n` + divider;
      data += `REMARK: ${remark || '-'}\n` + divider;
      data +=
        '\x1B\x61\x01\x1B\x45\x01***** ' +
        mode +
        ' *****\n\x1B\x45\x00\n\n\n\n\x1D\x56\x00';
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'KOT',
          content: data,
          machineId,
          companyid: company?.id,
          printerName,
        }),
      });
      // Phase 5: WhatsApp the cancel/edit KOT to the department too.
      await sendOrderWhatsApp({
        department: menuItem?.department,
        orderNo: item?.kotNumber,
        tableNo: targetLabel,
      });
    } catch (e) {
      console.error('change-KOT print error:', e);
    }
  };

  // ── Bill Printer (unchanged) ────────────────────────────────────────────────
  const sendBillToPrinterAPI = async (order, customer) => {
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString('en-GB');
    const grandTotal = Number(order.finalPrice || 0);
    const roundedTotal = Math.round(grandTotal);
    const roundOff = Number((roundedTotal - grandTotal).toFixed(2));
    let actualOrderId = order.id;
    try {
      const orderRes = await fetch(
        `${config.API_BASE_URL}/home/Getordernobytable/${company?.id}/${order?.tableno?.replace(/table\s*/i, '').trim()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (orderRes.ok) {
        const orderData = await orderRes.json();
        if (orderData) actualOrderId = orderData;
      }
    } catch (err) {
      console.error('Order ID fetch error:', err);
    }
    let itemsForPrint = [];
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GettableOrderDetails/${actualOrderId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        itemsForPrint = Array.isArray(data) ? data : [data];
      }
    } catch (err) {
      console.error('Error fetching bill items:', err);
    }
    if (itemsForPrint.length === 0) {
      toast.error('Items could not be loaded. Please try again.');
      return;
    }
    // ── 80mm thermal invoice: 48-col Font A, full paper width, GST layout. ──
    const W = 48;
    const rule = (ch) => ch.repeat(W) + '\n';
    const solid = rule('-');
    const dbl = rule('=');
    const amt = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;
    const kv = (label, value, bold) => {
      const v = amt(value);
      const gap = Math.max(1, W - label.length - v.length);
      const s = label + ' '.repeat(gap) + v + '\n';
      return bold ? '\x1B\x45\x01' + s + '\x1B\x45\x00' : s;
    };
    const kvText = (label, value) => {
      const v = String(value);
      const gap = Math.max(1, W - label.length - v.length);
      return label + ' '.repeat(gap) + v + '\n';
    };
    // Item columns: Name 22 | Qty 5 | Rate 9 | Amount 12 = 48.
    const NAME_W = 22;
    const QTY_W = 5;
    const RATE_W = 9;
    const AMT_W = 12;
    const header =
      'Item'.padEnd(NAME_W) +
      'Qty'.padStart(QTY_W) +
      'Rate'.padStart(RATE_W) +
      'Amount'.padStart(AMT_W) +
      '\n';
    let totalQty = 0;
    const itemsText = itemsForPrint
      .map((item) => {
        const qty = Number(item.qty || 1);
        totalQty += qty;
        // itemFinalPrice holds the authoritative line total; derive unit from it.
        const apiFinal = Number(
          item.itemFinalPrice ?? item.ItemFinalPrice ?? item.finalPrice ?? 0,
        );
        const apiUnit = Number(item.itemPrice || item.price || 0);
        const totalPrice =
          Number.isFinite(apiFinal) && apiFinal > 0 ? apiFinal : apiUnit * qty;
        const unitPrice =
          totalPrice > 0 && qty > 0 ? totalPrice / qty : apiUnit;
        // Prefix the variant name so the bill matches the KOT.
        const subPrefix =
          item.subcategoryName ||
          item.SubcategoryName ||
          item.subCategoryName ||
          '';
        const rawName = item.itemName || item.name || 'Item';
        const display = subPrefix ? `${subPrefix} ${rawName}` : rawName;
        const fullName = String(display || 'Item').trim();
        let line =
          fullName.slice(0, NAME_W).padEnd(NAME_W) +
          String(qty).padStart(QTY_W) +
          unitPrice.toFixed(2).padStart(RATE_W) +
          totalPrice.toFixed(2).padStart(AMT_W);
        for (let i = NAME_W; i < fullName.length; i += NAME_W) {
          line += '\n' + fullName.slice(i, i + NAME_W);
        }
        return line;
      })
      .join('\n');
    const totalItems = itemsForPrint.length;
    const vatAmount = Number(order.vataMount || 0);
    const vatRate = Number(order.vatrate || 0);
    const cgstAmount = applyGst ? Number(order.cGst || 0) : 0;
    const sgstAmount = applyGst ? Number(order.sGst || 0) : 0;
    const totalGstAmount = applyGst ? Number(order.gstAmount || 0) : 0;
    const hasVat = vatAmount > 0 || vatRate > 0;
    const hasGst =
      (cgstAmount > 0 || sgstAmount > 0 || totalGstAmount > 0) && applyGst;

    // ── Professional GST-inclusive flow ──
    // Gross -> Discount -> Grand Total (net incl.) -> Taxable -> CGST/SGST -> Payable
    const discount = Number(order.dicountamount || 0);
    const grossAmount = grandTotal + discount;
    const gstTaxable =
      Number(order.price || 0) > 0
        ? Number(order.price)
        : Number((grandTotal - totalGstAmount).toFixed(2));
    const vatTaxable = Number((grandTotal - vatAmount).toFixed(2));
    // CGST/SGST half-rate derived from stored amounts, never hardcoded.
    const halfRate = gstTaxable > 0 ? (totalGstAmount / gstTaxable) * 50 : 0;
    const rl = (r) => (r > 0 ? ` ${Math.round(r * 100) / 100}%` : '');

    let totals = solid;
    totals += kv('Gross Amount', grossAmount);
    if (discount > 0) totals += kv('Discount (-)', discount);
    totals += solid;
    totals += kv('Grand Total', grandTotal, true);
    if (hasVat) {
      totals += 'VAT Included\n';
      totals += kv('Taxable Amount', vatTaxable);
      totals += kv(`VAT${rl(vatRate)}`, vatAmount);
      if (hasGst) {
        totals += kv('CGST' + rl(halfRate), cgstAmount);
        totals += kv('SGST' + rl(halfRate), sgstAmount);
        totals += kv('Total GST', totalGstAmount);
      }
    } else if (hasGst) {
      totals += 'GST Included\n';
      totals += kv('Taxable Amount', gstTaxable);
      totals += kv('CGST' + rl(halfRate), cgstAmount);
      totals += kv('SGST' + rl(halfRate), sgstAmount);
      totals += kv('Total GST', totalGstAmount);
    }
    if (roundOff !== 0) totals += kv('Round Off', roundOff);
    totals += dbl;
    totals += kv('PAYABLE AMOUNT', roundedTotal, true);
    totals += dbl;

    // Logo (raster, wider for 80mm) + optional UPI QR.
    const logo = await buildLogoEscPos(company?.logo || company?.Logo, 512);
    let upiQr = '';
    if (company?.upiId) {
      const upi = `upi://pay?pa=${encodeURIComponent(company.upiId)}&pn=${encodeURIComponent(company?.name || 'Restaurant')}&am=${encodeURIComponent(roundedTotal)}&cu=INR`;
      try {
        upiQr =
          '\x1B\x61\x01' +
          buildUpiQrEscPos(upi) +
          'Scan & Pay (UPI)\n' +
          '\x1B\x61\x00' +
          solid;
      } catch (e) {
        upiQr = '';
      }
    }

    // Optional fields — printed only when the data exists.
    const fssai = company?.fssai || company?.fssaiNo || company?.Fssai || '';
    const orderType = order.orderType || order.ordertype || '';
    const waiter = order.waiter || order.userName || order.createdByName || '';
    const gstinNo = company?.gstNo || company?.gstno || company?.gst || '';

    const data =
      '\x1B\x40' +
      logo +
      '\x1B\x61\x01' +
      '\x1B\x45\x01' +
      (company?.name || 'HOTEL') +
      '\n' +
      '\x1B\x45\x00' +
      (company?.address1 ? company.address1 + '\n' : '') +
      (gstinNo ? 'GSTIN: ' + gstinNo + '\n' : '') +
      (fssai ? 'FSSAI: ' + fssai + '\n' : '') +
      (company?.mobile ? 'Mob: ' + company.mobile + '\n' : '') +
      solid +
      '\x1B\x45\x01' +
      'TAX INVOICE\n' +
      '\x1B\x45\x00' +
      solid +
      '\x1B\x61\x00' +
      kvText('Invoice :', order.invNumber || '-') +
      kvText('Date    :', dateTime) +
      kvText('Table   :', order.tableno || '-') +
      (orderType ? kvText('Type    :', orderType) : '') +
      (waiter ? kvText('Waiter  :', waiter) : '') +
      kvText('Customer:', customer.name || '-') +
      kvText('Mobile  :', customer.mobile || '-') +
      (customer.companyName
        ? kvText('Company :', customer.companyName) +
          kvText('Cust GST:', customer.gst || '-')
        : '') +
      solid +
      header +
      solid +
      itemsText +
      '\n' +
      solid +
      kvText('Total Items :', totalItems) +
      kvText('Total Qty   :', totalQty) +
      totals +
      upiQr +
      '\x1B\x61\x01' +
      (order.paymentMode ? 'Payment Mode: ' + order.paymentMode + '\n' : '') +
      (waiter ? 'Cashier: ' + waiter + '\n' : '') +
      '\n' +
      '\x1B\x45\x01' +
      'THANK YOU, VISIT AGAIN!\n' +
      '\x1B\x45\x00' +
      'Software by GoERP.live\n' +
      '\n\n\n\n' +
      '\x1D\x56\x00';
    try {
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'BILL',
          content: data,
          machineId,
          companyid: company?.id,
          orderId: actualOrderId,
          customerName: customer.name,
          customerMobile: customer.mobile,
          printerName: resolveBillPrinter(),
        }),
      });
      setSnackbar({
        open: true,
        message: 'Bill sent to printer!',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Bill print error',
        severity: 'error',
      });
    }
  };

  // `data` defaults to the confirmation-screen snapshot, so the two existing
  // callers behave exactly as before. Quick Bill passes its snapshot in
  // directly, because it prints the bill on the paid path where
  // finalOrderData is never set.
  const handleBillPrint = async (data = finalOrderData) => {
    try {
      const src = data || finalOrderData;
      const order = {
        id: src?.orderId || 0,
        invNumber: src?.invNumber || `INV-${Date.now()}`,
        tableno: src?.tableno || targetLabel,
        price: Number(src?.subtotal || 0),
        finalPrice: Number(src?.total || 0),
        cGst: Number(src?.cgst || 0),
        sGst: Number(src?.sgst || 0),
        gstAmount: Number(src?.gstAmount || 0),
        dicountamount: Number(src?.discount || 0),
        vatrate: Number(src?.vatRate || 0),
        vataMount: Number(src?.vatAmount || 0),
      };
      // Prefer the snapshot captured when the order was placed so the printed
      // bill keeps the right name even after the live guest fields are cleared
      // for the next order (#6).
      const snap = src?.customer;
      const customer = {
        name: snap?.Name || customerName?.trim() || DEFAULT_CUSTOMER.name,
        mobile: snap?.Mobile || customerMobile?.trim() || DEFAULT_CUSTOMER.mobile,
        address: snap?.Address || customerAddress?.trim() || DEFAULT_CUSTOMER.address,
        companyName: snap?.CompanyName || (billToCompany ? custCompanyName : ''),
        gst: snap?.GstNo || (billToCompany ? custGstNo : ''),
        companyAddress: snap?.CompanyAddress || (billToCompany ? custCompanyAddress : ''),
      };
      await sendBillToPrinterAPI(order, customer);
    } catch (err) {
      console.error('handleBillPrint error:', err);
    }
  };

  const handleRemarkSubmit = async () => {
    if (!remarkText.trim()) {
      setSnackbar({
        open: true,
        message: 'Remark is mandatory!',
        severity: 'error',
      });
      return;
    }
    setRemarkOpen(false);
    if (actionType === 'edit') handleUpdate(selectedItem, remarkText);
    if (actionType === 'cancel') await handleCancel(selectedItem, remarkText);
    setRemarkText('');
  };

  const handleUpdate = (item, remark) => {
    // #3/#4: open the item picker AT this item's OWN category with its CURRENT
    // qty pre-loaded under the CORRECT cart key. The old code seeded quantities
    // by the OrderDetail row PK (item.id) and the edit-submit then read the wrong
    // key — so changing 3→1 re-sent 3. Seed by the real menu cart key instead.
    const menuId = item.itemid ?? item.itemId ?? item.id;
    const menuItem = allItems.find((i) => String(i.id) === String(menuId));
    const variantId =
      item.subcategoryId && Number(item.subcategoryId) > 0
        ? Number(item.subcategoryId)
        : null;
    const cartKey = makeCartKey(menuId, variantId);
    // Land the picker on the item's category (#4).
    const catId =
      menuItem?.cateid ??
      menuItem?.Cateid ??
      menuItem?.categoryId ??
      menuItem?.CategoryId ??
      null;
    if (catId != null) setSelectedCategory(catId);
    setQuantities({ [cartKey]: Number(item.qty || 0) });
    // Carry the variant so the variant-required guard doesn't block place-order.
    if (variantId && item.subcategoryName) {
      setVariantSelections({
        [cartKey]: {
          isVariant: true,
          variantId,
          subcategoryName: item.subcategoryName,
        },
      });
    } else {
      setVariantSelections({});
    }
    setEditItem({ ...item, remark, cartKey, menuId });
    setStep(1);
    setOpenModal(false);
  };

  const handleEditSubmit = async (newItemId, newQty) => {
    const qtyNum = Number(newQty || 0);
    // Inactivate the old line first (isEdit=true → handleCancel prints NO
    // cancel-KOT and does NOT reload here; the edit path owns both).
    await handleCancel(editItem, editItem.remark, true);
    // Only re-add a line when the new qty is > 0. Editing down to 0 is a pure
    // removal — the cancel above already did it (#3: 3→1 now SETS 1, 3→0 removes).
    if (qtyNum > 0) {
      const payload = {
        orderId: editItem.orderId,
        // OrderDetailId = the EXACT row being edited, so editing GF in KOT #7 never
        // touches GF in KOT #6. oldItemId kept for the legacy fallback path.
        orderDetailId: editItem.id,
        oldItemId: editItem.itemid ?? editItem.itemId ?? editItem.id,
        newItemId: parseInt(newItemId),
        qty: qtyNum,
        kotNumber: editItem.kotNumber,
        remark: editItem.remark,
      };
      await fetch(`${config.API_BASE_URL}/Home/UpdateItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    }
    // #2: tell the kitchen — EDITED (new qty) or CANCELLED (reduced to 0).
    await printChangeKot(
      editItem,
      qtyNum,
      qtyNum > 0 ? 'EDITED' : 'CANCELLED',
      editItem.remark,
    );
    setEditItem(null);
    setQuantities({});
    setStep(1);
    window.location.reload();
  };

  const handleCancel = async (item, remark, isEdit = false) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/Home/CancelItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          // OrderDetailId = the EXACT row (DTO `id`) — this is what the backend now
          // cancels, so the same item in two KOTs (GF #6 vs GF #7) voids the line
          // the user clicked, not the first match. itemId/kotNumber kept for the
          // legacy fallback path.
          orderDetailId: item.id,
          orderId: item.orderId,
          itemId: item.itemid ?? item.itemId ?? item.id,
          kotNumber: item.kotNumber,
          remark,
        }),
      });
      // Only update + reload when the server actually cancelled it. Reloading
      // regardless made a failed cancel look "done" — the item came back on the
      // refresh and the amount never dropped.
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: d?.message || 'Could not cancel item.',
          severity: 'error',
        });
        return;
      }
      setOrderItems((prev) =>
        prev.map((i) => (i === item ? { ...i, activestatus: false } : i)),
      );
      if (!isEdit) {
        // #2: print a CANCELLED KOT so the kitchen stops making it. (Edit path
        // passes isEdit=true and prints its own EDITED/CANCELLED ticket.)
        await printChangeKot(item, item.qty, 'CANCELLED', remark);
        window.location.reload();
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Failed to cancel item.',
        severity: 'error',
      });
    }
  };

  const handleViewTable = async (table) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetTableDetails/${table.tableName}/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      setTableData(result);
      setOrderItems(result.data || []);
      setOpenModal(true);
    } catch (err) {
      console.error('Error fetching table data:', err);
    }
  };

  const handleViewRoom = async (roomNo) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetRoomsDetails/${roomNo}/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await res.json();
      setTableData(result);
      setOrderItems(result.data || []);
      setOpenModal(true);
    } catch (err) {
      console.error('Error fetching room data:', err);
    }
  };

  // ── NEW: Load existing items for an active table/room into the sidebar ─────
  // When user clicks a Booked or Billed table, we want to land them on Step 1
  // (item picker) with the existing items already loaded as editable rows.
  // The merge strategy: pre-fill `quantities` + `customPrices` from the
  // existing line items. The user can add more items on top, change qty, or
  // reduce existing qty. SaveOrder receives the FULL merged item list, and
  // the backend's existing append/edit logic handles the rest.
  //
  // CRITICAL: backend response fields vary by endpoint version. `ln.id` may be
  // the OrderDetail row id, NOT the menu item id. We try every plausible ID
  // field, and as a last resort match by `name` against `allItems` — that's
  // the only way the cart sidebar (keyed on allItems[].id) can render the
  // existing rows with their EXISTING badge.
  const resolveMenuItem = (ln) => {
    const candidates = [
      ln.id,
      ln.itemId,
      ln.ItemId,
      ln.itemid,
      ln.ItemID,
      ln.iId,
    ].filter((v) => v != null && v !== '');
    for (const c of candidates) {
      const m = allItems.find((i) => String(i.id) === String(c));
      if (m) return m;
    }
    const lineName = (ln.name ?? ln.itemName ?? ln.ItemName ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (lineName) {
      const m = allItems.find(
        (i) =>
          String(i.name || '')
            .trim()
            .toLowerCase() === lineName,
      );
      if (m) return m;
    }
    return null;
  };

  const buildExistingFromLines = (lines) => {
    const newQty = {};
    const newCustom = {};
    const newVariants = {};
    const stored = [];
    let unresolved = 0;
    lines.forEach((ln) => {
      // Only count active items — cancelled lines are skipped.
      if (ln.activestatus === false || ln.activeStatus === false) return;
      const q = Number(ln.qty || 0);
      if (q <= 0) return;
      const menuItem = resolveMenuItem(ln);
      if (!menuItem) {
        unresolved += 1;
        console.warn(
          '[CreateOrder] existing line could not be matched to a menu item — skipping:',
          ln,
        );
        return;
      }
      const id = menuItem.id;
      let variantId = Number(
        ln.subcategoryId ??
          ln.SubcategoryId ??
          ln.subCategoryId ??
          ln.variantId ??
          0,
      );
      if (!variantId || variantId <= 0) {
        const subName = (
          ln.subcategoryName ??
          ln.SubcategoryName ??
          ''
        ).toString();
        if (subName) {
          const list = variantsByItem[id] || [];
          const matched = list.find(
            (v) =>
              String(v.subcategoryName || '')
                .trim()
                .toLowerCase() === subName.trim().toLowerCase(),
          );
          if (matched) variantId = matched.variantId ?? matched.id;
        }
      }
      const cartKey = makeCartKey(id, variantId > 0 ? variantId : null);
      newQty[cartKey] = (newQty[cartKey] || 0) + q;

      const finalLine = Number(
        ln.itemFinalPrice ?? ln.ItemFinalPrice ?? ln.finalPrice,
      );
      const rawUnit = Number(ln.itemPrice ?? ln.ItemPrice ?? ln.price);
      let unit = NaN;
      if (Number.isFinite(finalLine) && finalLine > 0 && q > 0) {
        unit = finalLine / q;
      } else if (Number.isFinite(rawUnit) && rawUnit > 0) {
        unit = rawUnit;
      } else if (Number.isFinite(Number(menuItem.price))) {
        unit = Number(menuItem.price);
      }
      if (Number.isFinite(unit) && unit > 0) {
        newCustom[cartKey] = Number(unit.toFixed(2));
      }
      if (variantId > 0) {
        const list = variantsByItem[id] || [];
        const v = list.find((x) => (x.variantId ?? x.id) === variantId);
        if (v) {
          newVariants[cartKey] = {
            ...v,
            isVariant: true,
            variantId: v.variantId ?? v.id,
          };
        }
      }
      stored.push({ ...ln, id, cartKey });
    });
    return { newQty, newCustom, newVariants, stored, unresolved };
  };

  const loadExistingTableOrder = async (tableId, tableName) => {
    if (!tableName) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetTableDetails/${tableName}/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const result = await res.json();
      const lines = Array.isArray(result) ? result : result?.data || [];
      if (!lines.length) {
        setExistingOrderItems([]);
        setExistingOrderInfo(null);
        return;
      }
      const { newQty, newCustom, newVariants, stored, unresolved } =
        buildExistingFromLines(lines);
      setQuantities(newQty);
      setCustomPrices(newCustom);
      setVariantSelections(newVariants);
      setExistingOrderItems(stored);
      const firstLine = lines.find((l) => l) || {};
      setExistingOrderInfo({
        orderId:
          result?.orderId ??
          result?.OrderId ??
          firstLine.orderId ??
          firstLine.OrderId,
        kotNumber:
          result?.kotNumber ??
          result?.KotNumber ??
          result?.kotnumber ??
          firstLine.kotNumber ??
          firstLine.KotNumber ??
          firstLine.kotnumber,
        tableNo: result?.tableNo || tableName,
        status: result?.status,
      });
      const skippedNote =
        unresolved > 0 ? ` (${unresolved} skipped — name not in menu)` : '';
      setSnackbar({
        open: true,
        message: `Loaded ${stored.length} existing item${stored.length === 1 ? '' : 's'} from this table${skippedNote}. Add more or adjust quantities.`,
        severity: unresolved > 0 ? 'warning' : 'info',
      });
    } catch (err) {
      console.error('loadExistingTableOrder error:', err);
    }
  };

  const loadExistingRoomOrder = async (roomNo) => {
    if (roomNo == null) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetRoomsDetails/${roomNo}/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const result = await res.json();
      const lines = Array.isArray(result) ? result : result?.data || [];
      if (!lines.length) {
        setExistingOrderItems([]);
        setExistingOrderInfo(null);
        return;
      }
      const { newQty, newCustom, newVariants, stored, unresolved } =
        buildExistingFromLines(lines);
      setQuantities(newQty);
      setCustomPrices(newCustom);
      setVariantSelections(newVariants);
      setExistingOrderItems(stored);
      const firstLine = lines.find((l) => l) || {};
      setExistingOrderInfo({
        orderId:
          result?.orderId ??
          result?.OrderId ??
          firstLine.orderId ??
          firstLine.OrderId,
        kotNumber:
          result?.kotNumber ??
          result?.KotNumber ??
          result?.kotnumber ??
          firstLine.kotNumber ??
          firstLine.KotNumber ??
          firstLine.kotnumber,
        roomNo,
        status: result?.status,
      });
      const skippedNote =
        unresolved > 0 ? ` (${unresolved} skipped — name not in menu)` : '';
      setSnackbar({
        open: true,
        message: `Loaded ${stored.length} existing item${stored.length === 1 ? '' : 's'} from this room${skippedNote}. Add more or adjust quantities.`,
        severity: unresolved > 0 ? 'warning' : 'info',
      });
    } catch (err) {
      console.error('loadExistingRoomOrder error:', err);
    }
  };

  // Updated target selector — clears any prior existing order, then loads new.
  const handleTargetSelect = async (type, value) => {
    // Reset working state for clean slate
    setQuantities({});
    setCustomPrices({});
    setVariantSelections({});
    setNotes('');
    setExistingOrderItems([]);
    setExistingOrderInfo(null);

    if (type === 'table') {
      setSelectedTable(value);
      setSelectedRoom(null);
      const tbl = tables.find((t) => t.tableId === value);
      // If table is Booked (status 0) or Billed (status 1), load existing items.
      if (tbl && (tbl.status === 0 || tbl.status === 1)) {
        await loadExistingTableOrder(tbl.tableId, tbl.tableName);
      }
    } else {
      setSelectedRoom(value);
      setSelectedTable(null);
      // For rooms, attempt to load existing too (silently no-op if empty)
      await loadExistingRoomOrder(value);
    }
    setTimeout(() => setStep(1), 200);
  };

  const handleNextFromItems = (submitInline) => {
    const hasItems = Object.values(quantities).some((q) => q > 0);
    const hasExistingChanges =
      existingOrderItems.length > 0 &&
      existingOrderItems.some((ln) => {
        const originalQty = Number(ln.qty || 0);
        const currentQty = Number(quantities[ln.cartKey] || 0);
        return currentQty !== originalQty;
      });
    // In edit mode the user may legitimately set qty to 0 (remove the item), so
    // don't block on "no items" — the editItem branch below handles 0 as a delete.
    if (!hasItems && !hasExistingChanges && !editItem) {
      setSnackbar({
        open: true,
        message: 'Please add at least one item',
        severity: 'warning',
      });
      return;
    }
    // If the table has a running order and the user only changed existing
    // items (no new items added), skip the payment step — the diff is
    // applied directly to the running order on submit.
    if (existingOrderItems.length > 0 && !editItem) {
      const hasNewItem = Object.entries(quantities).some(
        ([cartKey, qty]) => qty > 0 && !existingCartKeys.has(cartKey),
      );
      if (!hasNewItem) {
        // Trigger submit directly — no payment screen for a pure edit.
        handleSubmit();
        return;
      }
    }
    const missingVariant = Object.entries(quantities).find(([cartKey, qty]) => {
      if (qty <= 0) return false;
      if (existingCartKeys.has(cartKey)) return false;
      const { productId, orderType } = parseCartKey(cartKey);
      const variantsForItem = variantsByItem[productId] || [];
      if (variantsForItem.length === 0 || variantSelections[cartKey]) {
        return false;
      }
      if (orderType === 'full') {
        const allPegStyle = variantsForItem.every(
          (v) => parsePegMl(v.subcategoryName) != null,
        );
        if (allPegStyle) return false;
      }
      return true;
    });
    if (missingVariant) {
      const [missCartKey] = missingVariant;
      const { productId } = parseCartKey(missCartKey);
      const missName =
        allItems.find((i) => i.id === productId)?.name || 'an item';
      setSnackbar({
        open: true,
        message: `Please select a size/variant for ${missName}.`,
        severity: 'warning',
      });
      return;
    }
    if (editItem) {
      // Use the SPECIFIC cart key seeded for the edited item — NOT "first key
      // with qty>0" (that picked a stale/other line and re-sent the old qty, so
      // 3→1 stayed 3). Read the qty the user set (0 = remove the item). (#3)
      const cartKey =
        editItem.cartKey ||
        Object.keys(quantities).find((k) => quantities[k] > 0);
      const { productId } = parseCartKey(cartKey || '');
      handleEditSubmit(productId, Number(quantities[cartKey] || 0));
      return;
    }
    // Desktop single-screen: place the order directly from the cart panel
    // instead of navigating to the separate review step. (=== true guard so
    // the mobile button — which passes a click event — still goes to step 2.)
    if (submitInline === true) {
      handleSubmit();
      return;
    }
    setStep(2);
  };

  // ── NEW: Payment calculations ──────────────────────────────────────────────
  // These are derived from current quantities + GST/VAT. We approximate tax
  // using the same logic the backend will use later (Subtotal + 5% as a
  // heuristic for live UI feedback). When the order saves, the server's
  // actual tax math wins — the UI just shows a sensible preview here.
  // ── GST math — INCLUSIVE ─────────────────────────────────────────────────
  // Item prices already include GST. We must NOT add tax on top — the
  // grand total equals the subtotal. CGST/SGST/GST amount are EXTRACTED
  // from the subtotal for display only.
  //
  //   subtotal      = sum(qty × unitPrice)   ← GST-inclusive
  //   base          = subtotal / (1 + rate/100)
  //   gstAmount     = subtotal − base
  //   cgst = sgst   = gstAmount / 2
  //   grandTotal    = subtotal                ← unchanged
  //
  // Single helper exposed via priceMath() so item lines, bill preview,
  // confirmation screen, and printers all read from the same source.
  const TAX_RATE_PREVIEW = Number(company?.gstRate ?? company?.gstrate ?? 5);

  const priceMath = () => {
    const subtotalInclusive = Number(calculateTotal() || 0);
    const rate = applyGst ? TAX_RATE_PREVIEW : 0;
    const base =
      rate > 0
        ? Number((subtotalInclusive / (1 + rate / 100)).toFixed(2))
        : subtotalInclusive;
    const gstAmount = Number((subtotalInclusive - base).toFixed(2));
    const half = Number((gstAmount / 2).toFixed(2));
    return {
      subtotalInclusive, // raw qty×unitPrice sum (GST in)
      subtotalBase: base, // taxable base (GST stripped out)
      cgst: half,
      sgst: half,
      gstAmount,
      grandTotal: subtotalInclusive, // === subtotal; no on-top addition
    };
  };

  // Backwards-compatible wrappers — kept so any caller using these names
  // (handleSubmit / SavePayment payload / paid-amount default) keeps working.
  const calcSubtotal = () => priceMath().subtotalInclusive;
  const calcTaxPreview = () => priceMath().gstAmount; // for display only
  const calcGrandTotal = () => priceMath().grandTotal;

  // Sum of split payments (for Split method)
  const sumSplitPayments = () =>
    splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  // Effective paid amount based on selected payment method
  const getEffectivePaidAmount = () => {
    if (paymentMethod === 'Due') return 0;
    if (paymentMethod === 'Split') return sumSplitPayments();
    const v = Number(paidAmount);
    if (!Number.isFinite(v) || v < 0) return 0;
    return v;
  };

  const getDueAmount = () => {
    const grand = calcGrandTotal();
    const paid = getEffectivePaidAmount();
    return Number(Math.max(0, grand - paid).toFixed(2));
  };

  const getPaymentStatus = () => {
    const grand = calcGrandTotal();
    const paid = getEffectivePaidAmount();
    if (paid <= 0) return 'Pending';
    if (paid >= grand - 0.01) return 'Paid';
    return 'Due';
  };

  // ── Record the order payment ─────────────────────────────────────────────
  // POSTs to /Home/SavePayment, which writes the CR row(s) to Orderledger and
  // flips the order's paid flags (it reuses the same backend logic MarkAsPaid
  // uses, so the table card frees up). A Split tender sends one leg per method
  // so each is recorded separately. A "Due" order sends PaidAmount 0 and the
  // backend records nothing. Non-blocking — the order is already saved even if
  // this call fails.
  const savePaymentRecord = async (orderId) => {
    if (orderId == null || orderId === '' || orderId === 0) {
      console.warn('[CreateOrder] SavePayment skipped — missing orderId');
      return;
    }

    // Map UI method names to the backend's lowercase ids (matches the existing
    // ledger rows MarkAsPaid writes, so reports stay consistent).
    const methodMap = {
      Cash: 'cash',
      Card: 'card',
      UPI: 'online',
      Split: 'split',
      Due: 'due',
    };
    const backendMethod = methodMap[paymentMethod] || 'cash';

    // For a non-Due method where the cashier didn't type an amount, assume the
    // full grand total was collected.
    let paid = getEffectivePaidAmount();
    if (paymentMethod !== 'Due' && paid <= 0) paid = calcGrandTotal();

    // Never write a payment row with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    const payload = {
      OrderId: orderId,
      PaymentMethod: backendMethod,
      PaidAmount: paymentMethod === 'Due' ? 0 : paid,
      ReferenceNo: paymentMethod === 'UPI' ? referenceNo || '' : '',
      CardType: paymentMethod === 'Card' ? cardType || '' : '',
      CardNumber: paymentMethod === 'Card' ? cardNumber || '' : '',
      CompanyId: company?.id ?? null,
      Userid: uid,
      SplitDetails:
        paymentMethod === 'Split'
          ? splitPayments.map((p) => ({
              Method: methodMap[p.method] || (p.method || '').toLowerCase(),
              Amount: Number(p.amount) || 0,
              ReferenceNo: p.referenceNo || '',
              CardType: p.method === 'Card' ? p.cardType || '' : '',
              CardNumber: p.method === 'Card' ? p.cardNumber || '' : '',
            }))
          : null,
    };

    try {
      const res = await fetch(`${config.API_BASE_URL}/Home/SavePayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) console.error('[CreateOrder] SavePayment HTTP', res.status);
    } catch (err) {
      console.error('SavePayment error (non-blocking):', err);
    }
  };

  // ── Main Submit ──────────────────────────────────────────────────────────────
  // PRESERVED: Existing SaveOrder payload shape, kotnumber, stock deduction
  // flow, and KOT printing are unchanged. NEW: customer fields + paid/due
  // amounts are passed alongside in the same payload so the backend can
  // persist them on the order row. A secondary SavePayment call is fired
  // post-save and is intentionally non-blocking — if the dedicated payment
  // endpoint isn't deployed yet, the order still saves successfully.
  // Auto-submit a hidden form to CC Avenue in a NEW TAB (keeps the POS on
  // screen while the guest pays on the gateway).
  const submitCCAvenueForm = (gatewayUrl, encRequest, accessCode) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = gatewayUrl;
    form.target = '_blank';
    const add = (n, v) => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = n;
      i.value = v;
      form.appendChild(i);
    };
    add('encRequest', encRequest);
    add('access_code', accessCode);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Start the chosen online gateway for the just-saved order. Opens in a new
  // tab; the gateway callback marks the order paid server-side.
  const startOrderGateway = async (gateway) => {
    const info = gatewayInfo;
    if (!info?.orderId) {
      setSnackbar({
        open: true,
        message: 'Order id missing for payment.',
        severity: 'error',
      });
      return;
    }
    try {
      const returnUrl = `${window.location.origin}/admin/TableOrders`;
      const body = JSON.stringify({
        CompanyId: company?.id,
        OrderId: info.orderId,
        // Charge the amount shown on the POS (this order's grand total).
        Amount: Number(info.amount || 0),
        ReturnUrl: returnUrl,
      });
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      if (gateway === 'ccavenue') {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/ccavenue/initiate`,
          { method: 'POST', headers, body },
        );
        const d = await res.json();
        if (!d?.success) throw new Error(d?.message || 'Payment could not start.');
        submitCCAvenueForm(d.gatewayUrl, d.encRequest, d.accessCode);
      } else {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/allupi/initiate`,
          { method: 'POST', headers, body },
        );
        const d = await res.json();
        if (!d?.success || !d?.paymentUrl)
          throw new Error(d?.message || 'Payment could not start.');
        window.open(d.paymentUrl, '_blank');
      }
      setGatewayOpen(false);
      setSnackbar({
        open: true,
        message:
          'Payment opened in a new tab — the order is marked paid once the guest pays.',
        severity: 'info',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Payment failed to start',
        severity: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    // ── Existing-line diffs ────────────────────────────────────────────────
    // Mirrors the single-item edit pattern in handleEditSubmit / handleCancel:
    //   • Qty changed → CancelItem the old line, then UpdateItem with the
    //     same id + new qty (so the backend records the change exactly the
    //     way the existing remark-dialog edit flow does).
    //   • Removed     → CancelItem only.
    //   • Unchanged   → skip (must NEVER be re-sent — that's the duplicate
    //     bug the user originally reported).
    const pickField = (obj, ...keys) => {
      for (const k of keys) {
        if (obj && obj[k] != null && obj[k] !== '') return obj[k];
      }
      return undefined;
    };
    // The cart now only ADDS to a running order — the existing (already-placed)
    // qty is read-only and shown separately, and the +/- controls only the NEW
    // additions. So we generate NO edit/cancel ops here; we simply APPEND the
    // newly-added qty (qty − existing) as fresh line(s). This is exactly how a
    // real KOT works (each round of additions is its own ticket), and it cannot
    // re-inflate the existing qty. Removing/cancelling an already-placed item is
    // done from the Active Order dialog. (pickField kept for that dialog path.)
    void pickField;
    const editOps = [];

    // Append payload: for EVERY cart row, send only the NEWLY ADDED quantity
    // (qty − existing). Brand-new items have existing = 0, so their full qty is
    // sent. "Add 4 to an existing 3" → sends 4 (KOT prints 4), running total
    // becomes 7; the existing 3 is never re-sent.
    const orderItemsPayload = Object.entries(quantities)
      .map(([cartKey, totalQty]) => {
        const existingQ = existingQtyMap[cartKey] || 0;
        return { cartKey, addQty: Number(totalQty) - existingQ };
      })
      .filter(({ addQty }) => addQty > 0)
      .map(({ cartKey, addQty }) => {
        const { productId, orderType } = parseCartKey(cartKey);
        // Apply the area price so the saved line matches what was shown. Fall back
        // to the current category's `items` so a just-Quick-Added item (not yet in
        // allItems) still resolves its price on its FIRST order instead of ₹0.
        const item = applyAreaPrice(
          allItems.find((i) => i.id === productId) ||
            items.find((i) => i.id === productId),
        );
        const variant = variantSelections[cartKey];
        return {
          ItemId: productId,
          SubcategoryId:
            orderType === 'full' ? 0 : (variant?.variantId ?? variant?.id ?? 0),
          // Persist the variant label so it survives save/reload (food + bar).
          SubcategoryName: variant?.subcategoryName ?? '',
          Qty: addQty,
          Price:
            customPrices[cartKey] !== undefined
              ? Number(customPrices[cartKey])
              : item?.price || 0,
          IsExisting: false,
        };
      });

    if (orderItemsPayload.length === 0 && editOps.length === 0) {
      setSubmitting(false);
      setSnackbar({
        open: true,
        message: 'No changes to submit.',
        severity: 'warning',
      });
      return;
    }

    // Apply existing-line edits sequentially, mirroring handleEditSubmit
    // (Cancel → Update). Any failed call is logged but doesn't block the
    // rest — same forgiving behaviour as the single-item edit flow.
    console.log('[CreateOrder] editOps to apply', editOps);
    let editFailed = false;
    for (const op of editOps) {
      const endpoint = op.kind === 'cancel' ? 'CancelItem' : 'UpdateItem';
      try {
        const res = await fetch(`${config.API_BASE_URL}/Home/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(op.payload),
        });
        const text = await res.text();
        if (!res.ok) {
          editFailed = true;
          console.error(
            `[CreateOrder] ${op.label} HTTP ${res.status}`,
            text,
            op.payload,
          );
        } else {
          console.log(`[CreateOrder] ${op.label} OK`, text);
        }
      } catch (err) {
        editFailed = true;
        console.error(`[CreateOrder] ${op.label} failed`, err, op.payload);
      }
    }

    // Pure-edit path — only existing items were modified, no new items to
    // place. Skip SaveOrder / KOT / payment flow, refresh totals, exit.
    if (orderItemsPayload.length === 0) {
      try {
        await fetchTableRunningTotals();
      } catch {
        /* non-blocking */
      }
      // Only claim success when every edit/cancel actually persisted — a false
      // "updated" message previously hid that the change never saved.
      if (editFailed) {
        setSnackbar({
          open: true,
          message: 'Some changes could not be saved. Please try again.',
          severity: 'error',
        });
        setSubmitting(false);
        return;
      }
      setSnackbar({
        open: true,
        message: 'Existing order updated.',
        severity: 'success',
      });
      setSubmitting(false);
      // Reset back to the table picker so the user sees the refreshed state.
      setStep(0);
      setSelectedTable(null);
      setSelectedRoom(null);
      setQuantities({});
      setCustomPrices({});
      setVariantSelections({});
      setExistingOrderItems([]);
      setExistingOrderInfo(null);
      return;
    }

    // Quick Bill takes the money at the counter, so payment is mandatory and
    // must settle the bill in FULL. A partial amount is rejected here because
    // the backend only flips IsPaid once the credited total covers the debit
    // (UpdateOrdertopaid), which would otherwise leave a "paid" Quick Bill
    // sitting open. Deferring tenders make no sense on this screen at all.
    if (quickBill) {
      if (!paymentMethod || paymentMethod === 'Due' || paymentMethod === 'Online') {
        setSubmitting(false);
        setSnackbar({
          open: true,
          message: 'Quick Bill needs the payment taken from the customer. Pick how they paid.',
          severity: 'warning',
        });
        return;
      }
      const qbPaid =
        paymentMethod === 'Split' ? sumSplitPayments() : Number(paidAmount);
      const qbDueTotal = calcGrandTotal();
      // Same 0.01 tolerance the rounding on the bill uses, so a legitimate
      // full payment is never rejected over a rounding cent.
      if (!Number.isFinite(qbPaid) || qbPaid < qbDueTotal - 0.01) {
        setSubmitting(false);
        setSnackbar({
          open: true,
          message: `Quick Bill must be paid in full — ${currency} ${qbDueTotal.toFixed(2)} due.`,
          severity: 'warning',
        });
        return;
      }
    }

    // Payment-amount guard: if anything other than "Due" is selected, the
    // user MUST enter a paid amount. Earlier the code silently defaulted to
    // the grand total which hid mistakes (wrong tender, missed cash count).
    // "Online" is exempt — the amount is collected by the payment gateway, not
    // typed in here.
    if (paymentMethod !== 'Due' && paymentMethod !== 'Online') {
      const enteredAmount =
        paymentMethod === 'Split' ? sumSplitPayments() : Number(paidAmount);
      if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) {
        setSubmitting(false);
        setSnackbar({
          open: true,
          message:
            'Please enter the paid amount, or select "Due" if you want to collect later.',
          severity: 'warning',
        });
        return;
      }
    }

    const customerPayload = {
      Name: customerName?.trim() || DEFAULT_CUSTOMER.name,
      Mobile: customerMobile?.trim() || DEFAULT_CUSTOMER.mobile,
      Address: customerAddress?.trim() || DEFAULT_CUSTOMER.address,
      CompanyName: billToCompany ? (custCompanyName || '').trim() : '',
      GstNo: billToCompany ? (custGstNo || '').trim() : '',
      CompanyAddress: billToCompany ? (custCompanyAddress || '').trim() : '',
    };

    const kotNo = getKotNumber();

    // Payment summary fields go directly on the order payload too — backend
    // can either read them here or pull from the SavePayment record. Both
    // approaches are supported.
    const grand = calcGrandTotal();
    const paid = getEffectivePaidAmount();
    const due = Number(Math.max(0, grand - paid).toFixed(2));
    const pStatus = getPaymentStatus();

    // Never write an order row with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    // #8: sequential virtual table for Pickup/Delivery (Pickup-01, Delivery-03…)
    // from the backend; falls back to a unique timestamp name if unreachable.
    let virtualTable = '';
    if (orderType === 'Pickup' || orderType === 'Delivery') {
      try {
        const vr = await fetch(
          `${config.API_BASE_URL}/restaurantorder/next-virtual-table/${company?.id}/${orderType}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (vr.ok) {
          const vd = await vr.json();
          virtualTable = vd?.tableno || '';
        }
      } catch {
        /* fallback below */
      }
      if (!virtualTable) virtualTable = `${orderType}-${String(Date.now()).slice(-6)}`;
    }

    const payload = {
      BookingId: 0,
      RoomNo: quickBill ? 0 : mainTab === 1 ? selectedRoom : 0,
      // #11: Pickup/Delivery are counter orders on a UNIQUE temporary table so
      // each stays separate (and vanishes from the live table view once settled).
      // Dine-In + Takeaway behaviour is unchanged.
      Tableno:
        orderType === 'Pickup' || orderType === 'Delivery'
          ? virtualTable
          : quickBill
            ? // A per-order table id, NOT the bare QUICK_TABLE constant. SaveOrder
              // MERGES a new order into any unpaid order sharing a Tableno, so a
              // fixed "Takeaway" meant two counter bills open at once became one
              // combined bill — and if a payment ever failed, every later Quick
              // Bill merged into that stranded order. Same shape as the
              // Pickup/Delivery fallback. Stays "Takeaway"-prefixed so the
              // takeaway counters keep matching it.
              `${QUICK_TABLE}-${String(Date.now()).slice(-6)}`
            : mainTab === 0
              ? tables.find((t) => t.tableId === selectedTable)?.tableName || ''
              : '',
      Items: orderItemsPayload,
      Notes: notes.trim() || '',
      CompanyId: company?.id,
      userid: uid,
      kotnumber: kotNo,
      applyGst,
      // #11: order type + Pickup/Delivery customer + delivery rider.
      OrderType: orderType,
      RiderName: riderName || '',
      CustomerName: customerName || '',
      CustomerMobile: customerMobile || '',
      CustomerAddress: customerAddress || '',
      // Additive fields — existing backend code can ignore these safely.
      ExistingOrderId: existingOrderInfo?.orderId ?? 0,
      Customer: customerPayload,
      PaymentMethod: paymentMethod,
      PaidAmount: paid,
      DueAmount: due,
      PaymentStatus: pStatus,
      ReferenceNo:
        paymentMethod === 'UPI'
          ? referenceNo
          : paymentMethod === 'Card'
            ? cardNumber
            : '',
      CardType: paymentMethod === 'Card' ? cardType : '',
      CardNumber: paymentMethod === 'Card' ? cardNumber : '',
    };

    try {
      const res = await fetch(`${config.API_BASE_URL}/Home/SaveOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = 'Order failed. Please try again.';
        try {
          const err = await res.json();
          if (err?.message) msg = err.message;
        } catch {
          /* ignore */
        }
        // Room orders fail when the room has no active booking — make that clear
        // instead of a generic failure (backend now rejects these explicitly).
        if (mainTab === 1 && selectedRoom) {
          msg =
            'Room order failed — this room has no active booking (not booked or already checked out).';
        }
        throw new Error(msg);
      }
      const savedOrder = await res.json();

      // #8: a Delivery order → open the Rider Assignment popup so staff can set
      // rider + delivery status + expected time on the just-created order.
      if (orderType === 'Delivery') {
        setRiderDialog({
          open: true,
          tableno: payload.Tableno,
          name: riderName || '',
          status: 'Assigned',
          eta: '',
        });
      }

      // KOT print (UNCHANGED behavior — but only print KOT for items the
      // kitchen hasn't seen before. If we're appending to an existing order,
      // we still print so the kitchen sees the new lines on top.)
      await sendKotToPrinterAPI(kotNo);

      // Persist the payment + flip the order's paid flags via /Home/SavePayment
      // (writes the Orderledger CR row and frees the table card). Records
      // nothing for a Due / zero order.
      console.log('[CreateOrder] SaveOrder response', savedOrder);
      let orderIdForPayment =
        savedOrder?.id ?? savedOrder?.orderId ?? existingOrderInfo?.orderId;

      // Hard fallback: if SaveOrder didn't echo back the new id (older
      // controller), look it up by table number — same endpoint the bill
      // printer uses. Works for both fresh and appended orders.
      if (
        (orderIdForPayment == null || orderIdForPayment === 0) &&
        payload.Tableno
      ) {
        try {
          const cleanTable = String(payload.Tableno)
            .replace(/table\s*/i, '')
            .trim();
          const lookupRes = await fetch(
            `${config.API_BASE_URL}/home/Getordernobytable/${company?.id}/${cleanTable}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (lookupRes.ok) {
            const lookupData = await lookupRes.json();
            if (lookupData) {
              orderIdForPayment =
                typeof lookupData === 'object'
                  ? (lookupData?.id ?? lookupData?.orderId ?? lookupData)
                  : lookupData;
            }
          }
        } catch (err) {
          console.warn('[CreateOrder] Getordernobytable lookup failed', err);
        }
      }
      console.log(
        '[CreateOrder] orderIdForPayment resolved →',
        orderIdForPayment,
      );
      // Records the payment + flips the order's paid flags via /Home/SavePayment.
      // Single source of truth — do NOT also call MarkAsPaid here or the
      // payment would be written to the ledger twice.
      if (paymentMethod === 'Online') {
        // Online gateway: DON'T record a local payment — the order stays open
        // (like "Due") and gets marked paid by the gateway callback. Open the
        // gateway choice so the guest can pay now.
        setGatewayInfo({ orderId: orderIdForPayment, amount: calcGrandTotal() });
        setGatewayOpen(true);
      } else {
        await savePaymentRecord(orderIdForPayment);
      }

      // Phase 4: WhatsApp order update to customer (server skips if no mobile).
      await sendOrderWhatsApp({
        mobile: customerMobile,
        orderNo: orderIdForPayment || kotNo,
        tableNo: payload.Tableno,
      });

      // Paid flow short-circuit: when the user actually collected payment
      // (anything except "Due"), the table is already freed by MarkAsPaid.
      // Skip the "Order Placed → Print Bill" confirmation screen and drop
      // straight back to the table picker so they can take the next order.
      // "Online" behaves like "Due" here — payment lands via the gateway
      // callback, so the order stays open until then.
      if (paymentMethod !== 'Due' && paymentMethod !== 'Online') {
        try {
          const cid = getStoredCompanyId();
          if (cid && selectedArea) {
            const r = await fetch(
              `${config.API_BASE_URL}/Home/GetTables/${selectedArea}/${cid}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (r.ok) setTables((await r.json()) || []);
          }
        } catch {
          /* non-blocking — running totals refresh below still fires */
        }
        fetchTableRunningTotals().catch(() => {});
        // Quick Bill prints the KOT *and* the bill on Place Order. The KOT has
        // already gone to the kitchen above; this is the bill. It has to happen
        // here because the paid path returns before setFinalOrderData, which is
        // why a paid Quick Bill previously printed a KOT and no bill at all.
        if (quickBill) {
          let qbDtl = [];
          try {
            const qbRes = await fetch(
              `${config.API_BASE_URL}/Home/Getorderdtlbytable/${company?.id}/${payload.Tableno || 'NA'}/0`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (qbRes.ok) qbDtl = await qbRes.json();
          } catch {
            /* non-blocking: the bill still prints from the totals below */
          }
          try {
            await handleBillPrint({
              items: qbDtl || [],
              ...billTotalsFor(qbDtl, applyGst),
              target: targetLabel,
              notes,
              time: new Date().toLocaleString('en-GB'),
              tableno: payload.Tableno,
              orderId: savedOrder?.id || savedOrder?.orderId || 0,
              invNumber:
                qbDtl?.[0]?.invNumber ||
                savedOrder?.invNumber ||
                `INV-${Date.now()}`,
              applyGst,
              customer: customerPayload,
            });
          } catch (e) {
            console.error('Quick Bill bill print failed:', e);
            setSnackbar({
              open: true,
              message: 'Order saved & paid, but the bill did not print.',
              severity: 'warning',
            });
          }
        }
        setSnackbar({
          open: true,
          message: quickBill
            ? 'Order placed & paid. KOT + bill printed.'
            : `Order placed & paid. Table freed.`,
          severity: 'success',
        });
        setStep(quickBill ? 1 : 0);
        setSelectedTable(null);
        setSelectedRoom(null);
        setQuantities({});
        setCustomPrices({});
        setVariantSelections({});
        setExistingOrderItems([]);
        setExistingOrderInfo(null);
        setFinalOrderData(null);
        setPaidAmount('');
        setPaymentMethod(quickBill ? '' : 'Due');
        setCustomerName('');
        setCustomerMobile('');
        setCustomerAddress('');
        setBillToCompany(false);
        setCustCompanyName('');
        setCustGstNo('');
        setCustCompanyAddress('');
        setSubmitting(false);
        return;
      }

      let orderDtl = [];
      try {
        const roomNo =
          payload.RoomNo && payload.RoomNo !== '' ? payload.RoomNo : '0';
        const orderDtlRes = await fetch(
          `${config.API_BASE_URL}/Home/Getorderdtlbytable/${company?.id}/${payload.Tableno || 'NA'}/${roomNo}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const responseText = await orderDtlRes.text();
        if (orderDtlRes.ok) orderDtl = JSON.parse(responseText);
      } catch (err) {
        console.error('Order detail fetch error:', err);
      }
      setSnackbar({
        open: true,
        message: 'Order placed & sent to kitchen!',
        severity: 'success',
      });
      // Refresh table running totals so the table picker reflects the new state
      // the next time the user returns. Non-blocking — confirmation screen
      // still renders immediately.
      fetchTableRunningTotals().catch(() => {});
      setFinalOrderData({
        items: orderDtl || [],
        ...billTotalsFor(orderDtl, applyGst),
        target: targetLabel,
        notes,
        time: new Date().toLocaleString('en-GB'),
        tableno: payload.Tableno,
        orderId:
          savedOrder?.id ||
          savedOrder?.orderId ||
          existingOrderInfo?.orderId ||
          0,
        invNumber:
          orderDtl?.[0]?.invNumber ||
          savedOrder?.invNumber ||
          `INV-${Date.now()}`,
        applyGst,
        // NEW: payment + customer fields surfaced on confirmation screen
        paidAmount: paid,
        dueAmount: due,
        paymentStatus: pStatus,
        paymentMethod,
        customer: customerPayload,
      });
      // Show the confirmation (Print Bill / Orders / New Order). setStep(2)
      // makes it work whether the order was placed from the desktop
      // single-screen cart panel (step 1) OR the mobile review step.
      setStep(2);
      // The right-column action buttons flip to
      // Print Bill / Orders / New Order via the `finalOrderData` guard.
      // #6: clear the live guest fields so the NEXT order starts fresh. The
      // confirmation + Print Bill use the finalOrderData.customer snapshot,
      // so the just-placed order still shows the correct name.
      setCustomerName('');
      setCustomerMobile('');
      setCustomerAddress('');
      setBillToCompany(false);
      setCustCompanyName('');
      setCustGstNo('');
      setCustCompanyAddress('');
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to submit order',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  // Step model:
  //   0 - Target (pick table/room)
  //   1 - Items (browse + sidebar; PAYMENT lives at end of sidebar via "Review")
  //   2 - Review + Customer + Payment (NEW: payment moved here from old step 2)
  // 3-step flow now — "Bill" step removed; Print Bill / Orders / New Order
  // appear inline on Review after the order is placed.
  const STEP_LABELS = ['Target', 'Items', 'Review'];

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: C.bg,
      }}>
      {/* ── TOPBAR ── */}
      <Box
        sx={{
          bgcolor: C.header,
          px: 2.5,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          zIndex: 10,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: C.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <RestaurantIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.92rem',
              color: '#fff',
              fontFamily: '"DM Sans", sans-serif',
              letterSpacing: 0.3,
            }}>
            {getBrand().name} POS
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {STEP_LABELS.map((label, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor:
                      step === i
                        ? C.accent
                        : step > i
                          ? C.green
                          : 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: step === i ? `2px solid ${C.accent}` : 'none',
                  }}>
                  {step > i ? (
                    <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        color: step === i ? '#fff' : 'rgba(255,255,255,0.5)',
                        fontWeight: 700,
                      }}>
                      {i + 1}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: step === i ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontWeight: step === i ? 700 : 400,
                  }}>
                  {label}
                </Typography>
              </Box>
              {i < STEP_LABELS.length - 1 && (
                <Box
                  sx={{
                    width: 22,
                    height: 1,
                    bgcolor: step > i ? C.green : 'rgba(255,255,255,0.15)',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {targetLabel && (
            <Box
              sx={{
                bgcolor: 'rgba(67,97,238,0.3)',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.4,
                border: `1px solid ${C.accent}`,
              }}>
              <Typography
                sx={{ fontSize: '0.7rem', color: '#fff', fontWeight: 700 }}>
                {targetLabel}
              </Typography>
            </Box>
          )}
          {/* Mark as Paid — settle the loaded running order (pending amount)
              right from the order screen, same flow as Table Orders. */}
          {existingOrderInfo?.orderId && step === 1 && (
            <Button
              onClick={() => setPayDialogOpen(true)}
              size='small'
              variant='contained'
              color='success'
              sx={{
                fontSize: '0.72rem',
                textTransform: 'none',
                fontWeight: 700,
                py: 0.4,
                boxShadow: 'none',
              }}>
              Mark as Paid
            </Button>
          )}
          {step > (quickBill ? 1 : 0) && step < 3 && (
            <Button
              onClick={() => setStep((s) => Math.max(quickBill ? 1 : 0, s - 1))}
              startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
              size='small'
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.72rem',
                textTransform: 'none',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}>
              Back
            </Button>
          )}
        </Box>
      </Box>

      {/* Mark as Paid — shared Process Payment dialog (multiple/split payments,
          defaults to the running order's pending amount). */}
      <ProcessPaymentDialog
        open={payDialogOpen}
        onClose={() => setPayDialogOpen(false)}
        orderId={existingOrderInfo?.orderId}
        orderLabel={
          existingOrderInfo?.orderId ? `ORD_${existingOrderInfo.orderId}` : ''
        }
        pendingAmount={calcExistingTotal()}
        currency={Companycurrency}
        onPaid={() => {
          setStep(quickBill ? 1 : 0);
          setSelectedTable(null);
          setSelectedRoom(null);
          clearQuantities();
          fetchTableRunningTotals().catch(() => {});
        }}
      />

      {/* ════════════ STEP 0: TARGET ════════════ */}
      {step === 0 && (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: C.border,
              borderRadius: 2,
            },
          }}>
          {/* Unified target tabs — All | each area | Rooms (no dropdown, no
              separate By Table/By Room toggle). One row, one page. */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              mb: 2.5,
            }}>
            {[
              { key: 'all', label: 'All' },
              ...areas.map((a) => ({ key: a.id, label: a.areaName })),
              ...(!roomApiFailed && !isRestaurantOnly
                ? [{ key: 'rooms', label: 'Rooms' }]
                : []),
            ].map((t) => {
              const active =
                t.key === 'rooms'
                  ? mainTab === 1
                  : t.key === 'all'
                    ? mainTab === 0 && allAreasMode
                    : mainTab === 0 &&
                      !allAreasMode &&
                      selectedArea === t.key;
              return (
                <Button
                  key={String(t.key)}
                  onClick={() => {
                    setSelectedTable(null);
                    setSelectedRoom(null);
                    if (t.key === 'rooms') {
                      setMainTab(1);
                      setAllAreasMode(false);
                    } else if (t.key === 'all') {
                      setMainTab(0);
                      setAllAreasMode(true);
                    } else {
                      setMainTab(0);
                      setAllAreasMode(false);
                      setSelectedArea(t.key);
                    }
                  }}
                  startIcon={
                    t.key === 'rooms' ? (
                      <HotelIcon sx={{ fontSize: 15 }} />
                    ) : t.key === 'all' ? undefined : (
                      <TableRestaurantIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  sx={{
                    borderRadius: 24,
                    px: 2.4,
                    py: 1,
                    minHeight: 42,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    border: '1.5px solid',
                    bgcolor: active ? C.accent : '#fff',
                    color: active ? '#fff' : C.text,
                    borderColor: active ? C.accent : C.border,
                    boxShadow: active ? `0 3px 10px ${C.accent}40` : 'none',
                    '&:hover': { bgcolor: active ? C.accent : C.accentLight },
                  }}>
                  {t.label}
                </Button>
              );
            })}
          </Box>

          {mainTab === 0 ? (
            <>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 2,
                }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    {
                      color: '#fff',
                      border: `1px solid ${C.border}`,
                      label: 'Free',
                    },
                    { color: C.Tablebooked, label: 'Booked' },
                    { color: C.Tablebilled, label: 'Bill Printed' },
                  ].map(({ color, border, label }) => (
                    <Box
                      key={label}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: color,
                          border: border || 'none',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.7rem', color: C.muted }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<SettingsIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setLayoutEditorOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderColor: C.border,
                    color: C.text,
                  }}>
                  Manage Table Layout
                </Button>
              </Box>

              {tablesLoading ? (
                hasFloorLayout ? (
                  <Box>
                    {[0, 1, 2].map((row) => (
                      <Box
                        key={row}
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1.5,
                          mb: 1.5,
                        }}>
                        {[0, 1, 2, 3].map((col) => (
                          <Skeleton
                            key={col}
                            variant='rounded'
                            sx={{
                              flex: '1 1 140px',
                              minWidth: 120,
                              height: 118,
                              borderRadius: 3,
                            }}
                          />
                        ))}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Grid item xs={6} sm={4} md={3} key={i}>
                        <Skeleton
                          variant='rounded'
                          height={140}
                          sx={{ borderRadius: 3 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : tables.length === 0 ? (
                <Alert severity='info'>No tables in this area</Alert>
              ) : hasFloorLayout && !allAreasMode ? (
                <Box>
                  {floorLayout.rows.map((row, ri) => (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 1.5,
                      }}>
                      {row.slots.map((slot, si) => {
                        const apiTable = resolveApiTableForSlot(slot);
                        const t = apiTable || {
                          tableId: slot.linkedTableId,
                          tableName: slot.tableNumber,
                          status: 2,
                        };
                        const statusVal = apiTable ? apiTable.status : 2;
                        const isSelected =
                          t.tableId != null &&
                          String(selectedTable) === String(t.tableId);
                        const colors = getTableColor(statusVal);
                        const billed = statusVal === 1;
                        const isActive = statusVal === 0 || statusVal === 1;
                        const runningTotal =
                          t.tableId != null
                            ? tableRunningTotals[t.tableId]
                            : null;
                        return (
                          <Box
                            key={slot.id}
                            sx={{
                              flex: '1 1 140px',
                              minWidth: 120,
                              maxWidth: {
                                xs: '100%',
                                sm: `calc(${100 / Math.max(row.slots.length, 1)}% - 12px)`,
                              },
                            }}>
                            <Card
                              onClick={() => {
                                if (t.tableId == null) {
                                  setSnackbar({
                                    open: true,
                                    message:
                                      'Link this seat to a system table in Manage Table Layout.',
                                    severity: 'warning',
                                  });
                                  return;
                                }
                                // CHANGED: booked/billed tables now also open
                                // — they load existing items.
                                handleTargetSelect('table', t.tableId);
                              }}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 3,
                                p: 1.5,
                                bgcolor: isSelected ? C.header : colors.bg,
                                border: isSelected
                                  ? `2px solid ${C.accent}`
                                  : statusVal === 2
                                    ? `1px solid ${C.border}`
                                    : 'none',
                                boxShadow: isSelected
                                  ? `0 6px 20px ${C.header}30`
                                  : '0 2px 8px rgba(0,0,0,0.06)',
                                transition: 'all 0.18s ease',
                                '&:hover': {
                                  transform: 'translateY(-3px)',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                },
                              }}>
                              <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                                <TableRestaurantIcon
                                  sx={{
                                    fontSize: 36,
                                    color: isSelected
                                      ? '#fff'
                                      : colors.text === '#fff'
                                        ? 'rgba(255,255,255,0.9)'
                                        : C.accent,
                                    opacity: 0.85,
                                  }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: '0.95rem',
                                  fontWeight: 800,
                                  textAlign: 'center',
                                  color: isSelected ? '#fff' : colors.text,
                                  fontFamily: '"DM Sans", sans-serif',
                                }}>
                                {slot.tableNumber || t.tableName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textAlign: 'center',
                                  fontFamily: '"DM Sans", sans-serif',
                                  color: isSelected
                                    ? 'rgba(255,255,255,0.9)'
                                    : colors.text === '#fff'
                                      ? 'rgba(255,255,255,0.7)'
                                      : C.muted,
                                  mt: 0.2,
                                }}>
                                {slot.tableType} · cap {slot.capacity}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  mt: 0.8,
                                  gap: 0.5,
                                }}>
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: 1,
                                    bgcolor: isSelected
                                      ? 'rgba(255,255,255,0.15)'
                                      : 'rgba(0,0,0,0.06)',
                                  }}>
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      fontWeight: 700,
                                      color: isSelected ? '#fff' : colors.text,
                                    }}>
                                    {colors.label}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const view = apiTable || {
                                      tableName: slot.tableNumber,
                                    };
                                    handleViewTable(view);
                                  }}
                                  sx={{
                                    p: 0.3,
                                    color: isSelected
                                      ? 'rgba(255,255,255,0.8)'
                                      : C.muted,
                                  }}>
                                  <VisibilityIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                              {/* NEW: Running total for booked/billed tables —
                                  always render so the user knows the system
                                  is tracking; placeholder "…" while fetching. */}
                              {isActive && (
                                <Box
                                  sx={{
                                    mt: 0.8,
                                    py: 0.5,
                                    px: 0.8,
                                    borderRadius: 1.2,
                                    textAlign: 'center',
                                    bgcolor: isSelected
                                      ? 'rgba(67,97,238,0.5)'
                                      : 'rgba(255,255,255,0.12)',
                                    border: `1px dashed ${isSelected ? alpha('#fff', 0.4) : alpha('#fff', 0.18)}`,
                                  }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.95rem',
                                      fontWeight: 900,
                                      color: '#fff',
                                      lineHeight: 1.1,
                                      fontFamily: '"DM Sans", sans-serif',
                                    }}>
                                    {runningTotal == null
                                      ? '…'
                                      : `${Companycurrency}${Number(runningTotal).toFixed(0)}`}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '0.55rem',
                                      fontWeight: 700,
                                      color: 'rgba(255,255,255,0.8)',
                                      letterSpacing: 0.4,
                                      textTransform: 'uppercase',
                                    }}>
                                    Running order
                                  </Typography>
                                </Box>
                              )}
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Grid container spacing={1.5}>
                  {tables.map((t) => {
                    const isSelected = selectedTable === t.tableId;
                    const colors = getTableColor(t.status);
                    const isActive = t.status === 0 || t.status === 1;
                    const runningTotal = tableRunningTotals[t.tableId];
                    return (
                      <Grid item xs={6} sm={4} md={3} key={t.tableId}>
                        <Card
                          onClick={() => handleTargetSelect('table', t.tableId)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 3,
                            p: 1.5,
                            bgcolor: isSelected ? C.header : colors.bg,
                            border: isSelected
                              ? `2px solid ${C.accent}`
                              : t.status === 2
                                ? `1px solid ${C.border}`
                                : 'none',
                            boxShadow: isSelected
                              ? `0 6px 20px ${C.header}30`
                              : '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'all 0.18s ease',
                            '&:hover': {
                              transform: 'translateY(-3px)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            },
                          }}>
                          <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <TableRestaurantIcon
                              sx={{
                                fontSize: 36,
                                color: isSelected
                                  ? '#fff'
                                  : colors.text === '#fff'
                                    ? 'rgba(255,255,255,0.9)'
                                    : C.accent,
                                opacity: 0.85,
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: 800,
                              textAlign: 'center',
                              color: isSelected ? '#fff' : colors.text,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                            {t.tableName}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mt: 0.8,
                              gap: 0.5,
                            }}>
                            <Box
                              sx={{
                                px: 1,
                                py: 0.2,
                                borderRadius: 1,
                                bgcolor: isSelected
                                  ? 'rgba(255,255,255,0.15)'
                                  : 'rgba(0,0,0,0.06)',
                              }}>
                              <Typography
                                sx={{
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  color: isSelected ? '#fff' : colors.text,
                                }}>
                                {colors.label}
                              </Typography>
                            </Box>
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewTable(t);
                              }}
                              sx={{
                                p: 0.3,
                                color: isSelected
                                  ? 'rgba(255,255,255,0.8)'
                                  : C.muted,
                              }}>
                              <VisibilityIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                          {/* NEW: Running total — always render for active tables */}
                          {isActive && (
                            <Box
                              sx={{
                                mt: 0.8,
                                py: 0.5,
                                px: 0.8,
                                borderRadius: 1.2,
                                textAlign: 'center',
                                bgcolor: isSelected
                                  ? 'rgba(67,97,238,0.5)'
                                  : 'rgba(255,255,255,0.12)',
                                border: `1px dashed ${isSelected ? alpha('#fff', 0.4) : alpha('#fff', 0.18)}`,
                              }}>
                              <Typography
                                sx={{
                                  fontSize: '0.92rem',
                                  fontWeight: 900,
                                  color: '#fff',
                                  lineHeight: 1.1,
                                  fontFamily: '"DM Sans", sans-serif',
                                }}>
                                {runningTotal == null
                                  ? '…'
                                  : `${Companycurrency}${Number(runningTotal).toFixed(0)}`}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.52rem',
                                  fontWeight: 700,
                                  color: 'rgba(255,255,255,0.8)',
                                  letterSpacing: 0.4,
                                  textTransform: 'uppercase',
                                }}>
                                Running order
                              </Typography>
                            </Box>
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          ) : rooms.length === 0 ? (
            <Alert severity='info'>No occupied rooms found</Alert>
          ) : (
            <Grid container spacing={1.5}>
              {rooms.map((r) => {
                // A room with a live (unpaid) restaurant order is coloured dark
                // and shows its running amount — exactly like a "Booked" table.
                const runningTotal = roomRunningTotals[String(r).trim()];
                const hasOrder =
                  runningTotal != null && Number(runningTotal) > 0;
                const isSel = selectedRoom === r;
                const dark = isSel || hasOrder;
                return (
                  <Grid item xs={6} sm={4} md={3} key={r}>
                    <Card
                      onClick={() => handleTargetSelect('room', r)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 3,
                        p: 1.5,
                        bgcolor: isSel
                          ? C.header
                          : hasOrder
                            ? C.Tablebooked
                            : '#fff',
                        border: isSel
                          ? `2px solid ${C.accent}`
                          : hasOrder
                            ? 'none'
                            : `1px solid ${C.border}`,
                        boxShadow: dark
                          ? '0 2px 8px rgba(0,0,0,0.06)'
                          : 'none',
                        transition: 'all 0.18s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                      }}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <HotelIcon
                          sx={{
                            fontSize: 36,
                            color: dark ? '#fff' : C.accent,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 800,
                          textAlign: 'center',
                          color: dark ? '#fff' : C.text,
                          fontFamily: '"DM Sans", sans-serif',
                        }}>
                        Room {r}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: 0.8,
                        }}>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewRoom(r);
                          }}
                          sx={{
                            p: 0.3,
                            color: dark ? 'rgba(255,255,255,0.8)' : C.muted,
                          }}>
                          <VisibilityIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      {/* Running order amount — shown like the table grid. */}
                      {hasOrder && (
                        <Box
                          sx={{
                            mt: 0.8,
                            py: 0.5,
                            px: 0.8,
                            borderRadius: 1.2,
                            textAlign: 'center',
                            bgcolor: isSel
                              ? 'rgba(67,97,238,0.5)'
                              : 'rgba(255,255,255,0.12)',
                            border: `1px dashed ${alpha('#fff', 0.18)}`,
                          }}>
                          <Typography
                            sx={{
                              fontSize: '0.92rem',
                              fontWeight: 900,
                              color: '#fff',
                              lineHeight: 1.1,
                              fontFamily: '"DM Sans", sans-serif',
                            }}>
                            {`${Companycurrency}${Number(runningTotal).toFixed(0)}`}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.52rem',
                              fontWeight: 700,
                              color: 'rgba(255,255,255,0.8)',
                              letterSpacing: 0.4,
                              textTransform: 'uppercase',
                            }}>
                            Running Order
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {(selectedTable || selectedRoom) && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant='contained'
                onClick={() => setStep(1)}
                size='large'
                sx={{
                  px: 4,
                  py: 1.3,
                  borderRadius: 3,
                  bgcolor: C.accent,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  boxShadow: `0 4px 16px ${C.accent}50`,
                  '&:hover': {
                    bgcolor: '#3350D6',
                    boxShadow: `0 6px 20px ${C.accent}60`,
                  },
                }}>
                Continue to Items →
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ════════════ STEP 1: ITEMS (split layout) ════════════ */}
      {step === 1 && (
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* PetPooja-style vertical CATEGORY RAIL (desktop only). Mobile keeps
              the horizontal chips inside the item browser, so nothing changes
              on phones. order:0 keeps it leftmost. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              width: 152,
              flexShrink: 0,
              order: { md: 0 },
              borderRight: `1px solid ${C.border}`,
              bgcolor: '#fff',
              overflowY: 'auto',
            }}>
            {sortedCategories.map((c) => {
              const isActive = selectedCategory === c.id;
              return (
                <Box
                  key={c.id}
                  onClick={() => setSelectedCategory(c.id)}
                  sx={{
                    px: 1.5,
                    py: 1.3,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    lineHeight: 1.25,
                    userSelect: 'none',
                    borderLeft: isActive
                      ? `4px solid ${C.accent}`
                      : '4px solid transparent',
                    borderBottom: `1px solid ${C.border}`,
                    bgcolor: isActive ? C.accentLight : 'transparent',
                    color: isActive ? C.accent : C.text,
                    '&:hover': { bgcolor: C.pill },
                  }}>
                  {c.category_name}
                </Box>
              );
            })}
          </Box>
          {/* Compact order sidebar (collapsible) — ordered to the RIGHT now,
              PetPooja keeps the cart on the right.
              Hidden on mobile — phones get a full-width item list and do the
              cart / notes / payment on the Review step instead. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              order: { md: 2 },
              width: orderSidebarCollapsed ? 48 : 380,
              flexShrink: 0,
              borderLeft: `1px solid ${C.border}`,
              overflow: 'hidden',
              position: 'relative',
              transition: 'width 0.22s ease',
              bgcolor: C.sidebar,
            }}>
            {orderSidebarCollapsed ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pt: 2,
                  gap: 1,
                }}>
                <IconButton
                  size='small'
                  onClick={() => setOrderSidebarCollapsed(false)}
                  aria-label='Expand order summary'
                  sx={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    bgcolor: '#fff',
                  }}>
                  <ChevronRightIcon sx={{ color: C.text }} />
                </IconButton>
                {selectedItemsCount > 0 && (
                  <Chip
                    label={selectedItemsCount}
                    size='small'
                    sx={{ height: 22, fontWeight: 800, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            ) : (
              <>
                <IconButton
                  size='small'
                  onClick={() => setOrderSidebarCollapsed(true)}
                  aria-label='Collapse order summary'
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    border: `1px solid ${C.border}`,
                    bgcolor: '#fff',
                  }}>
                  <ChevronLeftIcon sx={{ color: C.text, fontSize: 20 }} />
                </IconButton>
                <OrderSidebar
                quickBill={quickBill}
                  cartEntries={cartEntries}
                  customPrices={customPrices}
                  handlePriceChange={handlePriceChange}
                  handleQtyChange={handleQtyChange}
                  clearQuantities={clearQuantities}
                  targetLabel={targetLabel}
                  notes={notes}
                  setNotes={setNotes}
                  itemNotes={itemNotes}
                  setItemNote={setItemNote}
                  applyGst={applyGst}
                  setApplyGst={setApplyGst}
                  calculateTotal={calculateTotal}
                  handleNextFromItems={handleNextFromItems}
                  getEffectivePriceForCartKey={getEffectivePriceForCartKey}
                  Companycurrency={Companycurrency}
                  getCartLineLabel={getCartLineLabel}
                  existingCartKeys={existingCartKeys}
                  existingQtyMap={existingQtyMap}
                  existingOrderLoaded={existingOrderItems.length > 0}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  paidAmount={paidAmount}
                  setPaidAmount={setPaidAmount}
                  splitPayments={splitPayments}
                  setSplitPayments={setSplitPayments}
                  submitting={submitting}
                  onPlaceOrder={() => handleNextFromItems(true)}
                  onMoreOptions={() => setStep(2)}
                  onMarkPaid={() => setPayDialogOpen(true)}
                />
              </>
            )}
          </Box>

          {/* CENTER: Item browser */}
          <Box
            sx={{
              flex: 1,
              order: { md: 1 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: '#fff',
                borderBottom: `1px solid ${C.border}`,
                flexShrink: 0,
              }}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    bgcolor: C.pill,
                    borderRadius: 2,
                    p: 0.4,
                    border: `1px solid ${C.border}`,
                  }}>
                  {[
                    {
                      icon: <TableRestaurantIcon sx={{ fontSize: 14 }} />,
                      label: 'By Table',
                      val: 0,
                    },
                    ...(!roomApiFailed && !isRestaurantOnly
                      ? [
                          {
                            icon: <HotelIcon sx={{ fontSize: 14 }} />,
                            label: 'By Room',
                            val: 1,
                          },
                        ]
                      : []),
                  ].map((t) => (
                    <Button
                      key={t.val}
                      onClick={() => {
                        setMainTab(t.val);
                        setSelectedTable(null);
                        setSelectedRoom(null);
                        setStep(0);
                      }}
                      startIcon={t.icon}
                      sx={{
                        py: 0.6,
                        px: 1.5,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        textTransform: 'none',
                        minWidth: 0,
                        bgcolor: mainTab === t.val ? C.accent : 'transparent',
                        color: mainTab === t.val ? '#fff' : C.muted,
                        '&:hover': {
                          bgcolor: mainTab === t.val ? C.accent : '#fff',
                        },
                      }}>
                      {t.label}
                    </Button>
                  ))}
                </Box>
                <TextField
                  fullWidth
                  placeholder='Search items...'
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ fontSize: 16, color: C.muted }} />
                      </InputAdornment>
                    ),
                    endAdornment: itemSearch && (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          onClick={() => setItemSearch('')}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: C.pill,
                      '& fieldset': { borderColor: C.border },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openQuickAdd('item')}
                  sx={{ textTransform: 'none', fontWeight: 700, flex: 1 }}>
                  New Item
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openQuickAdd('category')}
                  sx={{ textTransform: 'none', fontWeight: 700, flex: 1 }}>
                  New Category
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openQuickAdd('department')}
                  sx={{ textTransform: 'none', fontWeight: 700, flex: 1 }}>
                  New Department
                </Button>
              </Box>

              {!itemSearch.trim() && (
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    gap: 0.8,
                    flexWrap: 'wrap',
                  }}>
                  {sortedCategories.map((c) => {
                    const isActive = selectedCategory === c.id;
                    return (
                      <Box
                        key={c.id}
                        onClick={() => setSelectedCategory(c.id)}
                        sx={{
                          px: 2.4,
                          py: 1,
                          borderRadius: 22,
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          border: '1.5px solid',
                          userSelect: 'none',
                          bgcolor: isActive ? C.header : '#fff',
                          color: isActive ? '#fff' : C.text,
                          borderColor: isActive ? C.header : C.border,
                          boxShadow: isActive
                            ? `0 2px 8px ${C.header}30`
                            : 'none',
                          transition: 'all 0.13s ease',
                          '&:hover': {
                            bgcolor: isActive ? C.header : C.pill,
                            borderColor: isActive ? C.header : C.accent,
                          },
                        }}>
                        {c.category_name}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* PEG SELECTOR BAR — below categories, above product grid */}
            {activeCategoryIsLiquor && (
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                  bgcolor: '#fff',
                  borderBottom: `1px solid ${C.border}`,
                  px: 2,
                  py: 1.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}>
                  <LocalBarIcon sx={{ fontSize: 16, color: C.accent }} />
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: C.text,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      fontFamily: '"DM Sans", sans-serif',
                    }}>
                    Choose peg size
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    onClick={() => setSelectedPeg(PEG_FILTER_FULL)}
                    disableElevation
                    sx={{
                      flex: { xs: '1 1 calc(33% - 8px)', sm: '0 0 auto' },
                      minWidth: { xs: 0, sm: 110 },
                      px: { xs: 1.5, sm: 2.5 },
                      py: { xs: 1.2, sm: 1.4 },
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: { xs: '0.95rem', sm: '1.05rem' },
                      letterSpacing: 0.4,
                      fontFamily: '"DM Sans", sans-serif',
                      border: `2px solid ${selectedPeg === PEG_FILTER_FULL ? C.accent : C.border}`,
                      bgcolor:
                        selectedPeg === PEG_FILTER_FULL ? C.accent : '#fff',
                      color: selectedPeg === PEG_FILTER_FULL ? '#fff' : C.text,
                      boxShadow:
                        selectedPeg === PEG_FILTER_FULL
                          ? `0 6px 18px ${C.accent}40`
                          : 'none',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        bgcolor:
                          selectedPeg === PEG_FILTER_FULL
                            ? C.accent
                            : C.accentLight,
                        borderColor: C.accent,
                        transform: 'translateY(-1px)',
                        boxShadow:
                          selectedPeg === PEG_FILTER_FULL
                            ? `0 8px 22px ${C.accent}50`
                            : `0 2px 8px ${C.accent}20`,
                      },
                      '&:active': { transform: 'translateY(0)' },
                    }}>
                    Full Bottle
                  </Button>
                  {pegOptions.map((ml) => {
                    const isActive = selectedPeg === ml;
                    return (
                      <Button
                        key={ml}
                        onClick={() => setSelectedPeg(ml)}
                        disableElevation
                        sx={{
                          flex: { xs: '1 1 calc(33% - 8px)', sm: '0 0 auto' },
                          minWidth: { xs: 0, sm: 110 },
                          px: { xs: 1.5, sm: 2.5 },
                          py: { xs: 1.2, sm: 1.4 },
                          borderRadius: 2.5,
                          textTransform: 'none',
                          fontWeight: 800,
                          fontSize: { xs: '0.95rem', sm: '1.05rem' },
                          letterSpacing: 0.4,
                          fontFamily: '"DM Sans", sans-serif',
                          border: `2px solid ${isActive ? C.accent : C.border}`,
                          bgcolor: isActive ? C.accent : '#fff',
                          color: isActive ? '#fff' : C.text,
                          boxShadow: isActive
                            ? `0 6px 18px ${C.accent}40`
                            : 'none',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            bgcolor: isActive ? C.accent : C.accentLight,
                            borderColor: C.accent,
                            transform: 'translateY(-1px)',
                            boxShadow: isActive
                              ? `0 8px 22px ${C.accent}50`
                              : `0 2px 8px ${C.accent}20`,
                          },
                          '&:active': { transform: 'translateY(0)' },
                        }}>
                        {ml} ml
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Item grid */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: C.border,
                  borderRadius: 2,
                },
              }}>
              {/* Existing-order banner */}
              {existingOrderItems.length > 0 && (
                <Alert
                  severity='info'
                  icon={<RestaurantIcon fontSize='inherit' />}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': { fontSize: '0.78rem' },
                  }}>
                  This table already has{' '}
                  <b>{existingOrderItems.length} item(s)</b> on the running
                  order. They&apos;re shown in the sidebar — add more, edit
                  quantities, or change prices as needed.
                </Alert>
              )}
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={28} sx={{ color: C.accent }} />
                </Box>
              ) : filteredVisibleItems.length === 0 ? (
                <Alert severity='info'>
                  {activeCategoryIsLiquor &&
                  selectedPeg !== PEG_FILTER_FULL &&
                  typeof selectedPeg === 'number'
                    ? `No bottles support ${selectedPeg} ml peg in this category.`
                    : 'No items found'}
                </Alert>
              ) : (
                <Grid container spacing={1.2}>
                  {filteredVisibleItems.map((item) => {
                    const activeCartKey = activeCategoryIsLiquor
                      ? getCartKeyForPegFilter(item.id)
                      : makeCartKey(item.id, null);
                    const qty = activeCartKey
                      ? quantities[activeCartKey] || 0
                      : 0;
                    // Qty already on the running order for this item. The GRID
                    // only ever shows "Add" — the existing/added counts live in
                    // the cart panel, so the user isn't confused here.
                    const existingForCard = activeCartKey
                      ? existingQtyMap[activeCartKey] || 0
                      : 0;
                    const variant = activeCartKey
                      ? variantSelections[activeCartKey]
                      : null;
                    const displayPrice = getDisplayPriceForItem(
                      item,
                      qty,
                      variant,
                    );
                    const isInOrder = Object.entries(quantities).some(
                      ([k, q]) =>
                        Number(q) > 0 && parseCartKey(k).productId === item.id,
                    );
                    const isFocused = focusedProductId === item.id;

                    return (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                        <Card
                          variant='outlined'
                          onClick={() => {
                            // #4: clicking ANYWHERE on the card adds the item (+1).
                            // The qty +/- buttons stopPropagation, so minus and the
                            // explicit +/- keep working exactly as before.
                            setFocusedProductId(item.id);
                            handleAddItemClick(item);
                          }}
                          sx={{
                            position: 'relative',
                            // Fixed size — EVERY card is identical regardless of the
                            // item name / image (POS spec). The image, 2-line title,
                            // price and Add button each get a fixed slot below.
                            height: 252,
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            borderRadius: 2.5,
                            overflow: 'hidden',
                            border:
                              isInOrder || isFocused
                                ? `2px solid ${C.accent}`
                                : `1.5px solid ${C.border}`,
                            borderLeft: isInOrder
                              ? `5px solid ${C.accent}`
                              : isFocused
                                ? `4px solid ${alpha(C.accent, 0.55)}`
                                : undefined,
                            bgcolor: isInOrder
                              ? C.accentLight
                              : isFocused
                                ? alpha(C.accent, 0.07)
                                : '#fff',
                            transition: 'all 0.15s ease',
                            boxShadow: isInOrder
                              ? `0 4px 16px ${C.accent}35`
                              : isFocused
                                ? `0 4px 14px ${C.accent}28`
                                : 'none',
                            // No scale on focus/hover — the card must NEVER resize
                            // (no layout shift). Only the shadow lifts on hover.
                            '&:hover': {
                              borderColor: C.accent,
                              boxShadow: `0 6px 18px ${C.accent}33`,
                            },
                          }}>
                          {isInOrder && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                px: 0.7,
                                py: 0.15,
                                borderRadius: 1,
                                bgcolor: C.accent,
                                color: '#fff',
                                fontSize: '0.55rem',
                                fontWeight: 800,
                                letterSpacing: 0.4,
                                lineHeight: 1.4,
                                zIndex: 1,
                              }}>
                              IN ORDER
                            </Box>
                          )}
                          {/* Category/department badge — fixed top-left corner so
                              it never widens the title or the card. */}
                          {item.department && (
                            <Chip
                              label={item.department}
                              size='small'
                              sx={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                zIndex: 1,
                                fontSize: '0.5rem',
                                height: 16,
                                bgcolor: '#EFF6FF',
                                color: '#1D4ED8',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          <CardContent
                            sx={{
                              p: '10px !important',
                              flexGrow: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: 0,
                            }}>
                            {/* Item image — uses item.image / itemimg if the
                                API provides one, otherwise renders a default
                                food-icon placeholder so every card has a
                                visual. */}
                            <Box
                              sx={{
                                width: '100%',
                                height: 86,
                                flexShrink: 0,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                mb: 1,
                                bgcolor: alpha(C.accent, 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}>
                              {(item.itemimg ||
                                item.image ||
                                item.itemImage) && (
                                <Box
                                  component='img'
                                  src={
                                    item.itemimg || item.image || item.itemImage
                                  }
                                  alt={item.name}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    position: 'absolute',
                                    inset: 0,
                                  }}
                                />
                              )}
                              <RestaurantIcon
                                sx={{
                                  fontSize: 38,
                                  color: alpha(C.accent, 0.45),
                                }}
                              />
                            </Box>
                            {/* Fixed 2-line title — identical height whether the
                                name is "Tea" or very long; long names clamp to two
                                lines with "...". Full name shows on hover (tooltip).
                                The visible text is truncated for LAYOUT only — search
                                still matches the full item.name. */}
                            <Typography
                              title={item.name}
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: C.text,
                                lineHeight: 1.3,
                                mb: 0.5,
                                fontFamily: '"DM Sans", sans-serif',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                height: '2.6em', // exactly two lines, always
                                wordBreak: 'break-word',
                              }}>
                              {item.name}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: '0.95rem',
                                fontWeight: 800,
                                color: C.accent,
                                mb: 0.4,
                                fontFamily: '"DM Sans", sans-serif',
                              }}>
                              {Companycurrency}
                              {displayPrice}
                            </Typography>

                            {/* Spacer pushes the Add button / qty controls to the
                                card BOTTOM, so their position is identical on every
                                card regardless of name length. */}
                            <Box sx={{ flexGrow: 1 }} />

                            {qty === 0 || existingForCard > 0 ? (
                              <Button
                                fullWidth
                                variant='outlined'
                                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFocusedProductId(item.id);
                                  handleAddItemClick(item);
                                }}
                                sx={{
                                  py: 0.7,
                                  borderRadius: 1.8,
                                  borderColor: C.accent,
                                  color: C.accent,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  '&:hover': { bgcolor: C.accentLight },
                                }}>
                                {existingForCard > 0 ? 'Add more' : 'Add'}
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.6,
                                }}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFocusedProductId(item.id);
                                    if (activeCartKey) {
                                      handleQtyChange(activeCartKey, qty - 1);
                                    }
                                  }}
                                  size='small'
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: '#FEF2F2',
                                    color: C.red,
                                    border: `1px solid #FECACA`,
                                    borderRadius: 1.5,
                                    p: 0,
                                  }}>
                                  <RemoveIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                                <Box
                                  sx={{
                                    flex: 1,
                                    textAlign: 'center',
                                    py: 0.4,
                                    borderRadius: 1.5,
                                    bgcolor: '#fff',
                                    border: `1px solid ${C.border}`,
                                  }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.85rem',
                                      fontWeight: 800,
                                      color: C.text,
                                    }}>
                                    {qty}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFocusedProductId(item.id);
                                    if (activeCartKey) {
                                      handleQtyChange(activeCartKey, qty + 1);
                                    }
                                  }}
                                  size='small'
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: '#F0FDF4',
                                    color: C.green,
                                    border: `1px solid #BBF7D0`,
                                    borderRadius: 1.5,
                                    p: 0,
                                  }}>
                                  <AddIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>

          {/* Mobile-only bottom bar — no sidebar on phones, so this is how you
              get to Review (where the cart, notes & payment live). */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1200,
              p: 1.2,
              gap: 1,
              alignItems: 'center',
              bgcolor: '#fff',
              borderTop: `1px solid ${C.border}`,
              boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
            }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: '0.7rem', color: C.muted, fontWeight: 700 }}>
                {selectedItemsCount} item{selectedItemsCount === 1 ? '' : 's'}
              </Typography>
              <Typography
                sx={{ fontSize: '0.95rem', fontWeight: 800, color: C.text }}>
                {Companycurrency}
                {calculateTotal().toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant='contained'
              disabled={selectedItemsCount === 0}
              onClick={handleNextFromItems}
              sx={{
                bgcolor: C.accent,
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
              }}>
              Review →
            </Button>
          </Box>
        </Box>
      )}

      {/* ════════════ STEP 2: REVIEW + CUSTOMER + PAYMENT ════════════ */}
      {/* NEW: Payment moved from confirmation screen to BEFORE Place Order.
          Order summary + customer details (optional) + payment method are
          all collected here, with a live calculation panel on the right. */}
      {step === 2 && (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: C.border,
              borderRadius: 2,
            },
          }}>
          <Grid container spacing={2} sx={{ maxWidth: 1280, mx: 'auto' }}>
            {/* LEFT COLUMN: Order summary + customer + payment method */}
            <Grid item xs={12} md={7}>
              {/* ── Customer Details (top, as requested) ── */}
              <Card
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  mb: 2,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}>
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                    }}>
                    <PersonIcon sx={{ color: C.accent, fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                      Customer Details
                    </Typography>
                    <Chip
                      label='Optional'
                      size='small'
                      sx={{
                        ml: 'auto',
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        bgcolor: C.pill,
                        color: C.muted,
                      }}
                    />
                  </Box>

                  {/* #11: order fulfilment type */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {['DineIn', 'Takeaway', 'Pickup', 'Delivery'].map((t) => (
                      <Chip
                        key={t}
                        label={t === 'DineIn' ? 'Dine In' : t}
                        color={orderType === t ? 'primary' : 'default'}
                        variant={orderType === t ? 'filled' : 'outlined'}
                        onClick={() => setOrderType(t)}
                        sx={{ fontWeight: 700 }}
                      />
                    ))}
                  </Box>
                  {orderType === 'Delivery' && (
                    <TextField
                      fullWidth
                      size='small'
                      label='Delivery Rider Name'
                      value={riderName}
                      onChange={(e) => setRiderName(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                  )}
                  {(orderType === 'Pickup' || orderType === 'Delivery') && (
                    <Typography
                      sx={{ fontSize: '0.72rem', color: '#b45309', mb: 1 }}>
                      Please fill customer name, mobile & address below for{' '}
                      {orderType}.
                    </Typography>
                  )}

                  <Typography
                    sx={{ fontSize: '0.7rem', color: C.muted, mb: 1.5 }}>
                    Leave blank to use defaults (Guest / 0000000000 / Guest
                    Address)
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size='small'
                        label='Name'
                        placeholder='Guest'
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <PersonIcon
                                sx={{ fontSize: 14, color: C.muted }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size='small'
                        label='Mobile'
                        placeholder='0000000000'
                        value={customerMobile}
                        onChange={(e) => setCustomerMobile(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <PhoneIcon
                                sx={{ fontSize: 14, color: C.muted }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size='small'
                        label='Address'
                        placeholder='Guest Address'
                        value={customerAddress}
                        onChange={(e) => setCustomerAddress(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <HomeIcon sx={{ fontSize: 14, color: C.muted }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size='small'
                            checked={billToCompany}
                            onChange={(e) => setBillToCompany(e.target.checked)}
                          />
                        }
                        label='Bill to Company (GST invoice in company name)'
                      />
                    </Grid>
                    {billToCompany && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size='small' label='Company Name'
                            value={custCompanyName}
                            onChange={(e) => setCustCompanyName(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.8 } }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size='small' label='Company GST No'
                            value={custGstNo}
                            onChange={(e) => setCustGstNo(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.8 } }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size='small' label='Company Address'
                            value={custCompanyAddress}
                            onChange={(e) => setCustCompanyAddress(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.8 } }} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* ── Order summary (compact, editable qty inline) ── */}
              <Card
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  mb: 2,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}>
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptLongIcon sx={{ color: C.accent, fontSize: 20 }} />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.95rem',
                          fontFamily: '"DM Sans", sans-serif',
                        }}>
                        Order Summary
                      </Typography>
                    </Box>
                    <Chip
                      label={targetLabel}
                      size='small'
                      sx={{
                        bgcolor: C.accentLight,
                        color: C.accent,
                        fontWeight: 700,
                        fontSize: '0.66rem',
                      }}
                    />
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  {Object.entries(getItemsByDepartment()).map(
                    ([dept, deptItems]) => (
                      <Box key={dept} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.8,
                            bgcolor: C.pill,
                            borderRadius: 1.2,
                            px: 1,
                            py: 0.4,
                            mb: 0.8,
                          }}>
                          <PrintIcon sx={{ fontSize: 11, color: C.accent }} />
                          <Typography
                            sx={{
                              fontSize: '0.66rem',
                              fontWeight: 700,
                              color: C.accent,
                            }}>
                            {dept}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.58rem',
                              color: C.muted,
                              ml: 'auto',
                            }}>
                            KOT → {dept}
                          </Typography>
                        </Box>
                        {deptItems.map((item) => {
                          const cartKey = item._cartKey;
                          const qty = item._qty || 0;
                          const variant = item._variant;
                          const effPrice = getEffectivePriceForCartKey(
                            cartKey,
                            item,
                            variant,
                          );
                          const isModified =
                            customPrices[cartKey] !== undefined &&
                            Number(customPrices[cartKey]) !==
                              (variant?.sellingPrice ?? item.price);
                          const isExisting = existingCartKeys.has(cartKey);
                          return (
                            <React.Fragment key={cartKey}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 0.5,
                                borderBottom: `1px dashed ${C.border}`,
                              }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    flexWrap: 'wrap',
                                  }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.78rem',
                                      fontWeight: 600,
                                      color: C.text,
                                    }}>
                                    {getCartLineLabel(item, variant)}
                                  </Typography>
                                  {isExisting && (
                                    <Chip
                                      label='EXISTING'
                                      size='small'
                                      sx={{
                                        fontSize: '0.5rem',
                                        height: 14,
                                        bgcolor: alpha(C.accent, 0.12),
                                        color: C.accent,
                                        fontWeight: 800,
                                      }}
                                    />
                                  )}
                                  {isModified && !variant && (
                                    <Chip
                                      label='MOD'
                                      size='small'
                                      sx={{
                                        fontSize: '0.5rem',
                                        height: 14,
                                        bgcolor: '#FFF7ED',
                                        color: C.orange,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: '0.78rem',
                                    color: C.text,
                                    fontWeight: 700,
                                  }}>
                                  {/* unit × qty = total — never multiply
                                      the line total again */}
                                  {Companycurrency}
                                  {Number(effPrice).toFixed(0)} × {qty} ={' '}
                                  {Companycurrency}
                                  {(qty * effPrice).toFixed(0)}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  bgcolor: '#F9FAFB',
                                  borderRadius: 5,
                                  border: `1px solid ${C.border}`,
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                }}>
                                <IconButton
                                  onClick={() =>
                                    // Use the cart-row key (product+variant),
                                    // NOT item.id — passing the raw id wrote to a
                                    // different quantities key and spawned a
                                    // DUPLICATE line (and double-counted the
                                    // amount) every time qty changed.
                                    handleQtyChange(cartKey, qty - 1)
                                  }
                                  size='small'
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    color: C.red,
                                    p: 0,
                                    borderRadius: 0,
                                  }}>
                                  <RemoveIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                                <Typography
                                  sx={{
                                    fontSize: '0.78rem',
                                    fontWeight: 800,
                                    minWidth: 22,
                                    textAlign: 'center',
                                  }}>
                                  {qty}
                                </Typography>
                                <IconButton
                                  onClick={() =>
                                    // Same fix as the minus button: increment the
                                    // existing cart row by its cartKey instead of
                                    // creating a phantom item.id entry.
                                    handleQtyChange(cartKey, qty + 1)
                                  }
                                  size='small'
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    color: C.green,
                                    p: 0,
                                    borderRadius: 0,
                                  }}>
                                  <AddIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                            </Box>
                            {/* Per-item KOT note — prints under this item on the
                                KOT. (Web shows it in the sidebar; on the Review
                                step it lives here so phones get it too.) */}
                            <TextField
                              fullWidth
                              size='small'
                              variant='standard'
                              placeholder='Item note (prints on KOT)'
                              value={itemNotes?.[cartKey] || ''}
                              onChange={(e) =>
                                setItemNote(cartKey, e.target.value)
                              }
                              sx={{ mb: 1, mt: 0.25 }}
                              InputProps={{ disableUnderline: false }}
                            />
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    ),
                  )}
                </CardContent>
              </Card>

              {/* ── Payment Method ── */}
              <Card
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  mb: 2,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}>
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                    }}>
                    <PaymentsIcon sx={{ color: C.accent, fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                      Payment
                    </Typography>
                    <Chip
                      label='Optional before placing order'
                      size='small'
                      sx={{
                        ml: 'auto',
                        height: 18,
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        bgcolor: C.pill,
                        color: C.muted,
                      }}
                    />
                  </Box>

                  {/* Payment method pills */}
                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    {PAYMENT_METHODS.filter(
                      (m) => !(quickBill && (m.id === 'Due' || m.id === 'Online')),
                    ).map(({ id, label, icon: Icon, color }) => {
                      const active = paymentMethod === id;
                      return (
                        <Grid item xs={6} sm={4} md={2.4} key={id}>
                          <Button
                            fullWidth
                            onClick={() => setPaymentMethod(id)}
                            sx={{
                              py: 1.1,
                              borderRadius: 2,
                              flexDirection: 'column',
                              gap: 0.3,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              border: `2px solid ${active ? color : C.border}`,
                              bgcolor: active ? alpha(color, 0.08) : '#fff',
                              color: active ? color : C.text,
                              boxShadow: active
                                ? `0 4px 12px ${alpha(color, 0.25)}`
                                : 'none',
                              '&:hover': {
                                borderColor: color,
                                bgcolor: alpha(color, 0.06),
                              },
                            }}>
                            <Icon sx={{ fontSize: 20 }} />
                            {label}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Method-specific fields */}
                  {paymentMethod === 'Cash' && (
                    <TextField
                      fullWidth
                      size='small'
                      label='Cash Received'
                      placeholder='0.00'
                      type='number'
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            {Companycurrency}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                      }}
                    />
                  )}

                  {paymentMethod === 'Card' && (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size='small'>
                          <InputLabel>Card Type</InputLabel>
                          <Select
                            label='Card Type'
                            value={cardType}
                            onChange={(e) => setCardType(e.target.value)}
                            sx={{ borderRadius: 1.8 }}>
                            {CARD_TYPES.map((c) => (
                              <MenuItem key={c} value={c}>
                                {c}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size='small'
                          label='Card Number (last 4)'
                          placeholder='XXXX'
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          inputProps={{ maxLength: 19 }}
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size='small'
                          label='Amount Charged'
                          placeholder='0.00'
                          type='number'
                          value={paidAmount}
                          onChange={(e) => setPaidAmount(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position='start'>
                                {Companycurrency}
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {paymentMethod === 'UPI' && (
                    <Box>
                      <UpiQRPanel
                        upiId={company?.upiId}
                        payeeName={company?.name}
                        amount={
                          Number(paidAmount) > 0
                            ? Number(paidAmount)
                            : calcGrandTotal()
                        }
                        Companycurrency={Companycurrency}
                        size={210}
                      />
                      <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size='small'
                            label='Reference / Transaction Number'
                            placeholder='UPI/Txn reference (after payment)'
                            value={referenceNo}
                            onChange={(e) => setReferenceNo(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <QrCode2Icon
                                    sx={{ fontSize: 16, color: C.muted }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size='small'
                            label='Amount Received'
                            placeholder='0.00'
                            type='number'
                            value={paidAmount}
                            onChange={(e) => setPaidAmount(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position='start'>
                                  {Companycurrency}
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 1.8 },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {paymentMethod === 'Split' && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: C.muted,
                          mb: 1,
                        }}>
                        Add one row per payment leg. The total paid is the sum
                        of these amounts.
                      </Typography>
                      {splitPayments.map((sp, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            mb: 1,
                            p: 1,
                            borderRadius: 1.8,
                            border: `1px solid ${C.border}`,
                            bgcolor: '#FAFBFF',
                          }}>
                          <FormControl size='small' sx={{ minWidth: 100 }}>
                            <Select
                              value={sp.method}
                              onChange={(e) => {
                                const next = [...splitPayments];
                                next[idx] = {
                                  ...next[idx],
                                  method: e.target.value,
                                };
                                setSplitPayments(next);
                              }}
                              sx={{ borderRadius: 1.5 }}>
                              <MenuItem value='Cash'>Cash</MenuItem>
                              <MenuItem value='Card'>Card</MenuItem>
                              <MenuItem value='UPI'>UPI</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size='small'
                            type='number'
                            placeholder='Amount'
                            value={sp.amount}
                            onChange={(e) => {
                              const next = [...splitPayments];
                              next[idx] = {
                                ...next[idx],
                                amount: e.target.value,
                              };
                              setSplitPayments(next);
                            }}
                            sx={{
                              width: 110,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                            }}
                          />
                          {sp.method === 'Card' && (
                            <>
                              <FormControl size='small' sx={{ minWidth: 100 }}>
                                <Select
                                  value={sp.cardType}
                                  onChange={(e) => {
                                    const next = [...splitPayments];
                                    next[idx] = {
                                      ...next[idx],
                                      cardType: e.target.value,
                                    };
                                    setSplitPayments(next);
                                  }}
                                  sx={{ borderRadius: 1.5 }}>
                                  {CARD_TYPES.map((c) => (
                                    <MenuItem key={c} value={c}>
                                      {c}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                size='small'
                                placeholder='Card #'
                                value={sp.cardNumber}
                                onChange={(e) => {
                                  const next = [...splitPayments];
                                  next[idx] = {
                                    ...next[idx],
                                    cardNumber: e.target.value,
                                  };
                                  setSplitPayments(next);
                                }}
                                sx={{
                                  flex: 1,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                  },
                                }}
                              />
                            </>
                          )}
                          {sp.method === 'UPI' && (
                            <TextField
                              size='small'
                              placeholder='Ref no.'
                              value={sp.referenceNo}
                              onChange={(e) => {
                                const next = [...splitPayments];
                                next[idx] = {
                                  ...next[idx],
                                  referenceNo: e.target.value,
                                };
                                setSplitPayments(next);
                              }}
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                },
                              }}
                            />
                          )}
                          <IconButton
                            size='small'
                            onClick={() => {
                              if (splitPayments.length <= 1) return;
                              const next = splitPayments.filter(
                                (_, i) => i !== idx,
                              );
                              setSplitPayments(next);
                            }}
                            sx={{ color: C.red }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size='small'
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={() =>
                          setSplitPayments([
                            ...splitPayments,
                            {
                              method: 'Cash',
                              amount: '',
                              referenceNo: '',
                              cardType: 'Visa',
                              cardNumber: '',
                            },
                          ])
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                        }}>
                        Add split row
                      </Button>
                    </Box>
                  )}

                  {paymentMethod === 'Due' && (
                    <Alert
                      severity='warning'
                      sx={{ borderRadius: 1.8, mt: 0.5 }}>
                      Order will be saved with status <b>Pending</b>. Full
                      amount remains due — collect later.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* ── Notes ── */}
              <Card
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  mb: 2,
                  border: `1px solid ${C.border}`,
                }}>
                <CardContent sx={{ p: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label='Notes / Special Instructions'
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    size='small'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <NotesIcon sx={{ fontSize: 16, color: C.muted }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: C.pill,
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* RIGHT COLUMN: Live calculation panel + Action buttons */}
            <Grid item xs={12} md={5}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <Card
                  sx={{
                    borderRadius: 3,
                    bgcolor: C.header,
                    color: '#fff',
                    boxShadow: '0 8px 30px rgba(28,31,46,0.2)',
                    mb: 2,
                  }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'rgba(255,255,255,0.6)',
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        mb: 0.5,
                      }}>
                      Bill Preview
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.7rem',
                        fontWeight: 900,
                        color: '#fff',
                        fontFamily: '"DM Sans", sans-serif',
                        lineHeight: 1,
                        mb: 2,
                      }}>
                      {Companycurrency}
                      {calcGrandTotal().toFixed(2)}
                    </Typography>

                    {/* GST is inclusive — Subtotal row reports the
                        GST-inclusive total. CGST/SGST/GST Amount are
                        extracted from it for display only and do NOT add
                        to the Grand Total. */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.6,
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                        Subtotal
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                        {Companycurrency}
                        {priceMath().subtotalInclusive.toFixed(2)}
                      </Typography>
                    </Box>
                    {applyGst && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.4,
                          }}>
                          <Typography
                            sx={{
                              fontSize: '0.74rem',
                              color: 'rgba(255,255,255,0.6)',
                            }}>
                            CGST ({(TAX_RATE_PREVIEW / 2).toFixed(2)}%)
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                            {Companycurrency}
                            {priceMath().cgst.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.4,
                          }}>
                          <Typography
                            sx={{
                              fontSize: '0.74rem',
                              color: 'rgba(255,255,255,0.6)',
                            }}>
                            SGST ({(TAX_RATE_PREVIEW / 2).toFixed(2)}%)
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                            {Companycurrency}
                            {priceMath().sgst.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.6,
                          }}>
                          <Typography
                            sx={{
                              fontSize: '0.74rem',
                              color: 'rgba(255,255,255,0.6)',
                              fontStyle: 'italic',
                            }}>
                            GST Amount (incl.)
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                            {Companycurrency}
                            {priceMath().gstAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.6,
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                        Discount
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                        {Companycurrency}0.00
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        my: 1,
                        borderColor: 'rgba(255,255,255,0.15)',
                        borderStyle: 'dashed',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.6,
                      }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                        Grand Total
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 900,
                          color: '#fff',
                        }}>
                        {Companycurrency}
                        {calcGrandTotal().toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.6,
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                        Paid Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          color: C.green,
                        }}>
                        {Companycurrency}
                        {getEffectivePaidAmount().toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                        Due / Pending
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 900,
                          color: getDueAmount() > 0 ? C.orange : C.green,
                        }}>
                        {Companycurrency}
                        {getDueAmount().toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Payment status badge */}
                    <Box
                      sx={{
                        mt: 1.5,
                        py: 0.8,
                        px: 1.2,
                        borderRadius: 1.5,
                        bgcolor:
                          getPaymentStatus() === 'Paid'
                            ? alpha(C.green, 0.2)
                            : getPaymentStatus() === 'Due'
                              ? alpha(C.orange, 0.2)
                              : alpha(C.red, 0.2),
                        border: `1px solid ${
                          getPaymentStatus() === 'Paid'
                            ? C.green
                            : getPaymentStatus() === 'Due'
                              ? C.orange
                              : C.red
                        }`,
                        textAlign: 'center',
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: 'rgba(255,255,255,0.7)',
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase',
                        }}>
                        Status
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 900,
                          color:
                            getPaymentStatus() === 'Paid'
                              ? C.green
                              : getPaymentStatus() === 'Due'
                                ? C.orange
                                : C.red,
                          letterSpacing: 0.8,
                        }}>
                        {getPaymentStatus().toUpperCase()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Action buttons */}
                <Box
                  sx={{ display: 'flex', gap: 1.2, flexDirection: 'column' }}>
                  {finalOrderData ? (
                    // ── Post-place actions ─────────────────────────────
                    // Order is saved. Show success banner + the three
                    // action buttons (Print Bill, Orders, New Order)
                    // right here on Step 2 so we don't need a separate
                    // confirmation screen.
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.2,
                          borderRadius: 2,
                          bgcolor: alpha(C.green, 0.12),
                          border: `1px solid ${alpha(C.green, 0.4)}`,
                        }}>
                        <CheckIcon sx={{ color: C.green, fontSize: 20 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.85rem',
                              color: C.green,
                            }}>
                            Order Placed
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.7rem', color: C.muted }}>
                            INV {finalOrderData.invNumber} · KOT sent to kitchen
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant='contained'
                        startIcon={<PrintIcon />}
                        onClick={handleBillPrint}
                        sx={{
                          py: 1.3,
                          borderRadius: 2,
                          bgcolor: C.header,
                          fontWeight: 800,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                        }}>
                        Print Bill
                      </Button>
                      <Box sx={{ display: 'flex', gap: 1.2 }}>
                        <Button
                          variant='outlined'
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            // Land on the orders page with the matching tab —
                            // Room tab for a room order, Dine-In otherwise.
                            window.location.href = `/admin/TableOrders?type=${
                              mainTab === 1 || selectedRoom ? 'Room' : 'Dinein'
                            }`;
                          }}
                          sx={{
                            flex: 1,
                            py: 1.1,
                            borderRadius: 2,
                            borderColor: C.border,
                            color: C.text,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.82rem',
                          }}>
                          Orders
                        </Button>
                        <Button
                          variant='contained'
                          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                          onClick={() => window.location.reload()}
                          sx={{
                            flex: 1,
                            py: 1.1,
                            borderRadius: 2,
                            bgcolor: C.green,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.82rem',
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: '#059669',
                              boxShadow: 'none',
                            },
                          }}>
                          New Order
                        </Button>
                      </Box>
                    </>
                  ) : (
                    // ── Pre-place actions ──────────────────────────────
                    <>
                      <Button
                        variant='contained'
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={
                          submitting ? (
                            <CircularProgress size={16} color='inherit' />
                          ) : (
                            <CheckCircleOutlineIcon />
                          )
                        }
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          bgcolor: C.green,
                          fontWeight: 800,
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          boxShadow: `0 4px 16px ${C.green}50`,
                          '&:hover': {
                            bgcolor: '#059669',
                            boxShadow: `0 6px 20px ${C.green}60`,
                          },
                          '&:disabled': { bgcolor: '#6EE7B7' },
                        }}>
                        {submitting ? 'Placing Order...' : 'Place Order'}
                      </Button>
                      <Box sx={{ display: 'flex', gap: 1.2 }}>
                        <Button
                          variant='outlined'
                          startIcon={<ArrowBackIcon />}
                          onClick={() => setStep(1)}
                          sx={{
                            flex: 1,
                            py: 1.1,
                            borderRadius: 2,
                            borderColor: C.border,
                            color: C.text,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.82rem',
                          }}>
                          Back
                        </Button>
                        <Button
                          variant='outlined'
                          startIcon={<PrintIcon />}
                          onClick={() => {
                            const rows = cartEntries
                              .map(
                                ({ qty, item, variant }) =>
                                  `${String(qty).padEnd(5)} ${getCartLineLabel(item, variant)}`,
                              )
                              .join('\n');
                            const printHtml = `<html><head><title>KOT</title><style>@page{size:72mm auto;margin:0}body{font-family:monospace;padding:8px;font-size:14px;width:72mm}.center{text-align:center}.line{border-top:1px dashed #000;margin:6px 0}pre{white-space:pre-wrap;font-size:14px;line-height:1.5}</style></head><body><div class="center"><b>${company?.name || 'HOTEL'}</b><br/>KITCHEN ORDER TICKET</div><div class="line"></div><div><b>Target:</b> ${targetLabel}<br/><b>Time:</b> ${new Date().toLocaleString('en-GB')}</div><div class="line"></div><pre>Qty   Item\n------------------------\n${rows}</pre><div class="line"></div><div style="text-align:right"><b>Total:</b> ${Companycurrency} ${calculateTotal().toFixed(2)}</div><div class="line"></div><div><b>Notes:</b><br/>${notes || 'No Notes'}</div><div class="line"></div><div class="center">***** THANK YOU *****</div></body></html>`;
                            const iframe = document.createElement('iframe');
                            iframe.style.cssText =
                              'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
                            document.body.appendChild(iframe);
                            const doc =
                              iframe.contentWindow || iframe.contentDocument;
                            doc.document.open();
                            doc.document.write(printHtml);
                            doc.document.close();
                            setTimeout(() => {
                              iframe.contentWindow.focus();
                              iframe.contentWindow.print();
                              setTimeout(
                                () => document.body.removeChild(iframe),
                                1000,
                              );
                            }, 500);
                          }}
                          sx={{
                            flex: 1,
                            py: 1.1,
                            borderRadius: 2,
                            borderColor: C.border,
                            color: C.text,
                            fontWeight: 700,
                            textTransform: 'none',
                            fontSize: '0.82rem',
                          }}>
                          Print KOT
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ════════════ STEP 3: CONFIRMATION / BILL (REMOVED) ════════════
          Post-save actions (Print Bill / Orders / New Order) now live on
          Step 2's right column under the `finalOrderData` guard. This
          entire block is permanently gated off via `false &&`.        */}
      {false && step === 3 && finalOrderData && (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: C.border,
              borderRadius: 2,
            },
          }}>
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            <Card
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                border: `1px solid ${C.border}`,
                boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
              }}>
              <Box sx={{ bgcolor: C.header, px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: C.green,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <CheckIcon sx={{ fontSize: 22, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1rem',
                        color: '#fff',
                        fontFamily: '"DM Sans", sans-serif',
                      }}>
                      Order Placed!
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.73rem',
                        color: 'rgba(255,255,255,0.6)',
                      }}>
                      KOT sent to kitchen successfully
                    </Typography>
                  </Box>
                  <Chip
                    label={finalOrderData?.applyGst ? 'With GST' : 'No GST'}
                    size='small'
                    sx={{
                      bgcolor: finalOrderData?.applyGst ? C.green : C.orange,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.62rem',
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2.5,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}>
                  {[
                    {
                      label: 'TABLE',
                      value: finalOrderData?.tableno || targetLabel,
                    },
                    { label: 'INVOICE', value: finalOrderData?.invNumber },
                    {
                      label: 'TIME',
                      value: new Date().toLocaleTimeString(),
                    },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Typography
                        sx={{
                          fontSize: '0.62rem',
                          color: C.muted,
                          mb: 0.3,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}>
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          color: C.text,
                        }}>
                        {value || '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Customer details */}
                {finalOrderData?.customer && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#F8FAFC',
                      border: `1px solid ${C.border}`,
                      mb: 2,
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        color: C.muted,
                        letterSpacing: 0.5,
                        mb: 0.6,
                      }}>
                      CUSTOMER
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: C.muted,
                          }}>
                          Name
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: C.text,
                          }}>
                          {finalOrderData.customer.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: C.muted,
                          }}>
                          Mobile
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: C.text,
                          }}>
                          {finalOrderData.customer.mobile}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: C.muted,
                          }}>
                          Address
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: C.text,
                          }}>
                          {finalOrderData.customer.address}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                <Box sx={{ mb: 2 }}>
                  {finalOrderData?.items?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        py: 1.2,
                        borderBottom:
                          index !== finalOrderData.items.length - 1
                            ? `1px dashed ${C.border}`
                            : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      {(() => {
                        // Derive unit price from the API row. `itemPrice` is
                        // unreliable on older orders (saved as 1 / 0 / null)
                        // so we always re-derive it from itemFinalPrice / qty
                        // when the math doesn't agree. This keeps the
                        // displayed `unit × qty = total` line internally
                        // consistent.
                        const qty = Number(item.qty || 0);
                        const apiFinal = Number(item.itemFinalPrice || 0);
                        const apiUnit = Number(item.itemPrice || 0);
                        const lineTotal =
                          apiFinal > 0 ? apiFinal : qty * apiUnit;
                        const unit =
                          qty > 0 && lineTotal > 0 ? lineTotal / qty : apiUnit;
                        return (
                          <>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.85rem',
                                  color: C.text,
                                }}>
                                {item.itemName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  color: C.muted,
                                  mt: 0.2,
                                }}>
                                {Companycurrency}
                                {unit.toFixed(2)} × {qty} = {Companycurrency}
                                {lineTotal.toFixed(2)}
                              </Typography>
                              {item.kotNumber && (
                                <Typography
                                  sx={{
                                    fontSize: '0.62rem',
                                    color: C.green,
                                    mt: 0.1,
                                    fontWeight: 700,
                                  }}>
                                  KOT #{item.kotNumber}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                color: C.text,
                              }}>
                              {Companycurrency}
                              {lineTotal.toFixed(2)}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  ))}
                </Box>

                {finalOrderData?.notes && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#FEF3C7',
                      mb: 2,
                    }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#92400E' }}>
                      <b>Note:</b> {finalOrderData.notes}
                    </Typography>
                  </Box>
                )}

                {finalOrderData?.applyGst && (
                  <Box
                    sx={{
                      p: 1.8,
                      borderRadius: 2.5,
                      bgcolor: C.pill,
                      border: `1px solid ${C.border}`,
                      mb: 2,
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: C.text,
                        mb: 1,
                      }}>
                      Tax Details
                    </Typography>
                    {(() => {
                      // Prefer server-computed CGST/SGST/GST; fall back to
                      // extracting from the order total when the backend
                      // returned zeros (older Room/Table flows). Inclusive
                      // math throughout: gst = total − total/(1+rate/100).
                      const apiCgst = Number(finalOrderData?.cgst || 0);
                      const apiSgst = Number(finalOrderData?.sgst || 0);
                      const apiGstAmt = Number(finalOrderData?.gstAmount || 0);
                      const total = Number(finalOrderData?.total || 0);
                      const rate = TAX_RATE_PREVIEW || 5;
                      const derivedGst =
                        total > 0
                          ? Number(
                              (total - total / (1 + rate / 100)).toFixed(2),
                            )
                          : 0;
                      const derivedHalf = Number((derivedGst / 2).toFixed(2));
                      const gstAmt = apiGstAmt > 0 ? apiGstAmt : derivedGst;
                      const cgst = apiCgst > 0 ? apiCgst : derivedHalf;
                      const sgst = apiSgst > 0 ? apiSgst : derivedHalf;
                      return [
                        {
                          label: `CGST (${(rate / 2).toFixed(2)}%)`,
                          value: cgst,
                        },
                        {
                          label: `SGST (${(rate / 2).toFixed(2)}%)`,
                          value: sgst,
                        },
                        { label: 'GST Amount (incl.)', value: gstAmt },
                        { label: 'Discount', value: finalOrderData?.discount },
                      ];
                    })().map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}>
                        <Typography
                          sx={{ fontSize: '0.74rem', color: C.muted }}>
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: C.text,
                          }}>
                          {Companycurrency}
                          {Number(value || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Payment summary */}
                <Box
                  sx={{
                    p: 1.8,
                    borderRadius: 2.5,
                    bgcolor:
                      finalOrderData?.paymentStatus === 'Paid'
                        ? '#F0FDF4'
                        : finalOrderData?.paymentStatus === 'Due'
                          ? '#FFF7ED'
                          : '#FEF2F2',
                    border: `1px solid ${
                      finalOrderData?.paymentStatus === 'Paid'
                        ? '#BBF7D0'
                        : finalOrderData?.paymentStatus === 'Due'
                          ? '#FED7AA'
                          : '#FECACA'
                    }`,
                    mb: 2,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: C.text,
                      }}>
                      Payment
                    </Typography>
                    <Chip
                      label={finalOrderData?.paymentStatus || 'Pending'}
                      size='small'
                      sx={{
                        bgcolor:
                          finalOrderData?.paymentStatus === 'Paid'
                            ? C.green
                            : finalOrderData?.paymentStatus === 'Due'
                              ? C.orange
                              : C.red,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.6rem',
                        height: 18,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}>
                    <Typography sx={{ fontSize: '0.74rem', color: C.muted }}>
                      Method
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        color: C.text,
                      }}>
                      {finalOrderData?.paymentMethod || 'Cash'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}>
                    <Typography sx={{ fontSize: '0.74rem', color: C.muted }}>
                      Paid Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        color: C.green,
                      }}>
                      {Companycurrency}
                      {Number(finalOrderData?.paidAmount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {Number(finalOrderData?.dueAmount || 0) > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                      <Typography
                        sx={{
                          fontSize: '0.74rem',
                          color: C.muted,
                        }}>
                        Due Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          color: C.orange,
                        }}>
                        {Companycurrency}
                        {Number(finalOrderData?.dueAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    pt: 2,
                    borderTop: `2px dashed ${C.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2.5,
                  }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: C.text,
                    }}>
                    Grand Total
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.6rem',
                      color: C.text,
                      fontFamily: '"DM Sans", sans-serif',
                    }}>
                    {Companycurrency}
                    {(() => {
                      // Grand Total fallback: when the backend's
                      // finalOrderData.total is 0/missing (older room flows),
                      // sum the item lines so the user sees the correct total
                      // instead of ₹0.00.
                      const apiTotal = Number(finalOrderData?.total || 0);
                      if (apiTotal > 0) return apiTotal.toFixed(2);
                      const itemsSum = (finalOrderData?.items || []).reduce(
                        (s, it) => {
                          const q = Number(it.qty || 0);
                          const fp = Number(it.itemFinalPrice || 0);
                          const up = Number(it.itemPrice || 0);
                          return s + (fp > 0 ? fp : q * up);
                        },
                        0,
                      );
                      return itemsSum.toFixed(2);
                    })()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.2,
                  }}>
                  <Button
                    variant='contained'
                    startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                    onClick={handleBillPrint}
                    sx={{
                      py: 1.2,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: C.header,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                    }}>
                    Print Bill
                  </Button>
                  <Button
                    variant='outlined'
                    startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      window.location.href = '/admin/TableOrders';
                    }}
                    sx={{
                      py: 1.2,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: C.border,
                      color: C.text,
                    }}>
                    Orders
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => window.location.reload()}
                    sx={{
                      gridColumn: '1 / -1',
                      py: 1.3,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: C.green,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
                    }}>
                    New Order
                  </Button>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
      )}

      {/* ── Variant Selector Dialog (legacy, no longer auto-opens) ── */}
      <VariantSelectorDialog
        open={variantDialog.open}
        parentItem={variantDialog.item}
        companyId={getStoredCompanyId()}
        token={token}
        onSelect={handleVariantSelect}
        onClose={() => setVariantDialog({ open: false, item: null })}
      />

      {/* ── Manage floor layout ── */}
      <Dialog
        open={layoutEditorOpen}
        onClose={() => setLayoutEditorOpen(false)}
        fullWidth
        maxWidth='lg'
        scroll='paper'
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon sx={{ color: C.accent }} />
            <Box>
              <Typography fontWeight={800}>Manage Table Layout</Typography>
              <Typography variant='caption' color='text.secondary'>
                {areas.find((a) => a.id === selectedArea)?.areaName || 'Area'} ·
                saved per restaurant and area on this device
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setLayoutEditorOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 2,
              alignItems: 'flex-end',
            }}>
            <TextField
              size='small'
              label='Row pattern (e.g. 4-4-2)'
              value={layoutPatternInput}
              onChange={(e) => setLayoutPatternInput(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant='contained'
              sx={{ bgcolor: C.header, textTransform: 'none', fontWeight: 700 }}
              onClick={() => {
                const next = buildLayoutFromPattern(layoutPatternInput, tables);
                if (!next) {
                  setSnackbar({
                    open: true,
                    message: 'Enter a valid pattern (e.g. 4-4-2)',
                    severity: 'warning',
                  });
                  return;
                }
                setFloorLayout(next);
                setSnackbar({
                  open: true,
                  message: 'Pattern applied',
                  severity: 'success',
                });
              }}>
              Apply pattern
            </Button>
            <Button
              variant='outlined'
              sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={() => {
                if (!tables?.length) {
                  setSnackbar({
                    open: true,
                    message:
                      'No tables in this area yet — wait for tables to load',
                    severity: 'warning',
                  });
                  return;
                }
                const patternLayout = buildLayoutFromPattern(
                  layoutPatternInput,
                  tables,
                );
                const next =
                  patternLayout || layoutFromApiTablesGrid(tables, 4);
                if (!next) {
                  setSnackbar({
                    open: true,
                    message:
                      'No tables to import — enter a valid row pattern (e.g. 4-4-6) or wait for tables to load',
                    severity: 'warning',
                  });
                  return;
                }
                setFloorLayout(next);
                setSnackbar({
                  open: true,
                  message: patternLayout
                    ? 'Imported table rows using the pattern above'
                    : 'Imported tables (4 per row); set a valid pattern to use custom row sizes',
                  severity: 'success',
                });
              }}>
              Import table rows
            </Button>
            <Button
              variant='outlined'
              color='error'
              sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={clearStoredFloorLayout}>
              Clear saved layout
            </Button>
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: C.muted, mb: 2 }}>
            Drag the grip to reorder tables or move them between rows. Link each
            seat to a system table for live status and ordering.
          </Typography>

          {!floorLayout?.rows?.length ? (
            <Alert severity='info'>
              No layout yet — apply a pattern or import tables, or add a row
              below.
            </Alert>
          ) : (
            floorLayout.rows.map((row, ri) => (
              <Box
                key={row.id}
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${C.border}`,
                  bgcolor: '#FAFBFF',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                  }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                    Row {ri + 1}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Button
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={() => addTableToRow(ri)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Add table
                    </Button>
                    <Button
                      size='small'
                      color='error'
                      startIcon={<RemoveIcon />}
                      disabled={floorLayout.rows.length <= 1}
                      onClick={() => removeFloorRow(ri)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Remove row
                    </Button>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'stretch',
                  }}>
                  {row.slots.map((slot, si) => {
                    const apiTable = resolveApiTableForSlot(slot);
                    const st = apiTable?.status;
                    const statusLabel =
                      st === 0
                        ? 'Booked'
                        : st === 1
                          ? 'Billed'
                          : apiTable
                            ? 'Free'
                            : 'Not linked';
                    return (
                      <Card
                        key={slot.id}
                        variant='outlined'
                        draggable
                        onDragStart={(e) => handleSlotDragStart(e, ri, si)}
                        onDragOver={handleSlotDragOver}
                        onDrop={(e) => handleSlotDrop(e, ri, si)}
                        sx={{
                          flex: '1 1 220px',
                          maxWidth: 280,
                          borderRadius: 2,
                          borderColor: C.border,
                          cursor: 'grab',
                        }}>
                        <CardContent
                          sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 1,
                            }}>
                            <DragIndicatorIcon
                              sx={{ color: C.muted, fontSize: 20 }}
                            />
                            <Chip
                              size='small'
                              label={statusLabel}
                              sx={{ fontWeight: 700, fontSize: '10px' }}
                              color={
                                st === 1
                                  ? 'warning'
                                  : st === 0
                                    ? 'success'
                                    : 'default'
                              }
                            />
                            <IconButton
                              size='small'
                              sx={{ ml: 'auto' }}
                              onClick={() => removeSlotFromRow(ri, si)}
                              aria-label='Remove table'>
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                          <TextField
                            size='small'
                            fullWidth
                            label='Table number'
                            value={slot.tableNumber}
                            onChange={(e) =>
                              updateFloorSlot(ri, si, {
                                tableNumber: e.target.value,
                              })
                            }
                            inputProps={{ draggable: false }}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            size='small'
                            fullWidth
                            type='number'
                            label='Capacity'
                            value={slot.capacity}
                            onChange={(e) =>
                              updateFloorSlot(ri, si, {
                                capacity: Math.max(
                                  0,
                                  parseInt(e.target.value, 10) || 0,
                                ),
                              })
                            }
                            inputProps={{ draggable: false }}
                            sx={{ mb: 1 }}
                          />
                          <FormControl size='small' fullWidth sx={{ mb: 1 }}>
                            <InputLabel id={`tt-${slot.id}`}>
                              Table type
                            </InputLabel>
                            <Select
                              labelId={`tt-${slot.id}`}
                              label='Table type'
                              value={slot.tableType || 'Standard'}
                              onChange={(e) =>
                                updateFloorSlot(ri, si, {
                                  tableType: e.target.value,
                                })
                              }
                              inputProps={{ draggable: false }}>
                              {TABLE_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size='small' fullWidth>
                            <InputLabel id={`lnk-${slot.id}`}>
                              System table
                            </InputLabel>
                            <Select
                              labelId={`lnk-${slot.id}`}
                              label='System table'
                              value={
                                slot.linkedTableId == null ||
                                slot.linkedTableId === ''
                                  ? ''
                                  : (() => {
                                      const hit = tables.find(
                                        (t) =>
                                          String(t.tableId) ===
                                          String(slot.linkedTableId),
                                      );
                                      return hit ? hit.tableId : '';
                                    })()
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') {
                                  updateFloorSlot(ri, si, {
                                    linkedTableId: null,
                                  });
                                  return;
                                }
                                const tbl = tables.find(
                                  (x) => String(x.tableId) === String(v),
                                );
                                updateFloorSlot(ri, si, {
                                  linkedTableId: tbl ? tbl.tableId : null,
                                  tableNumber: tbl
                                    ? String(tbl.tableName)
                                    : slot.tableNumber,
                                });
                              }}
                              inputProps={{ draggable: false }}>
                              <MenuItem value=''>
                                <em>Unlinked</em>
                              </MenuItem>
                              {tables.map((t) => (
                                <MenuItem key={t.tableId} value={t.tableId}>
                                  {t.tableName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </CardContent>
                      </Card>
                    );
                  })}
                  <Box
                    onDragOver={handleSlotDragOver}
                    onDrop={(e) => handleSlotDrop(e, ri, row.slots.length)}
                    sx={{
                      flex: '1 1 80px',
                      minHeight: 120,
                      border: `2px dashed ${C.border}`,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: C.muted,
                      fontSize: '0.75rem',
                    }}>
                    Drop here
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${C.border}`,
            flexWrap: 'wrap',
            gap: 1,
          }}>
          <Button
            startIcon={<AddIcon />}
            onClick={addFloorRow}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Add row
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant='contained'
            onClick={() => {
              const cid = getStoredCompanyId();
              if (cid && selectedArea && floorLayout)
                saveFloorLayoutToStorage(cid, selectedArea, floorLayout);
              setLayoutEditorOpen(false);
            }}
            sx={{ bgcolor: C.accent, textTransform: 'none', fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Online payment gateway choice (CC Avenue / AllUPI) ── */}
      <Dialog
        open={gatewayOpen}
        onClose={() => setGatewayOpen(false)}
        fullWidth
        maxWidth='xs'
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Collect {company?.currency ?? '₹'}
          {Number(gatewayInfo?.amount || 0).toFixed(2)} online
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Opens the gateway in a new tab for the guest to pay.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Button
            fullWidth
            variant='contained'
            onClick={() => startOrderGateway('ccavenue')}
            sx={{ mb: 1.5, py: 1.3, borderRadius: 2, fontWeight: 700 }}>
            CC Avenue (Card / UPI / Netbanking)
          </Button>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => startOrderGateway('allupi')}
            sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}>
            AllUPI (UPI QR)
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGatewayOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick Add (new item / category, without leaving the order) ── */}
      <Dialog
        open={quickAddOpen}
        onClose={() => !qaSaving && setQuickAddOpen(false)}
        fullWidth
        maxWidth='xs'
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {quickAddMode === 'item'
            ? 'New Item'
            : quickAddMode === 'department'
              ? 'New Department'
              : 'New Category'}
        </DialogTitle>
        <DialogContent dividers>
          {quickAddMode === 'category' ? (
            <TextField
              autoFocus
              fullWidth
              label='Category Name'
              value={qaCatName}
              onChange={(e) => setQaCatName(e.target.value)}
              sx={{ mt: 1 }}
            />
          ) : quickAddMode === 'department' ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                size='small'
                label='Department Name'
                placeholder='e.g. Kitchen / Bar / Bakery'
                value={qaNewDeptName}
                onChange={(e) => setQaNewDeptName(e.target.value)}
              />
              <TextField
                fullWidth
                size='small'
                label='Printer Name'
                value={qaNewDeptPrinter}
                onChange={(e) => setQaNewDeptPrinter(e.target.value)}
              />
              <TextField
                fullWidth
                size='small'
                label='Mobile (optional)'
                value={qaNewDeptMobile}
                onChange={(e) => setQaNewDeptMobile(e.target.value)}
              />
            </Box>
          ) : (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth size='small'>
                <InputLabel>Category</InputLabel>
                <Select
                  label='Category'
                  value={qaItemCat}
                  onChange={(e) => setQaItemCat(e.target.value)}>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.category_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size='small'
                label='Item Name'
                value={qaItemName}
                onChange={(e) => setQaItemName(e.target.value)}
              />
              <TextField
                fullWidth
                size='small'
                type='number'
                label='Price (incl. GST)'
                value={qaItemPrice}
                onChange={(e) => setQaItemPrice(e.target.value)}
              />
              <TextField
                fullWidth
                size='small'
                type='number'
                label='GST %'
                value={qaItemGst}
                onChange={(e) => setQaItemGst(e.target.value)}
              />
              <FormControl fullWidth size='small'>
                <InputLabel>Department (Printer)</InputLabel>
                <Select
                  label='Department (Printer)'
                  value={qaDept}
                  onChange={(e) => setQaDept(e.target.value)}>
                  {qaDepartments.map((d) => (
                    <MenuItem key={d.id ?? d.Id} value={d.id ?? d.Id}>
                      {d.Department ||
                        d.department ||
                        d.Printername ||
                        d.printername}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setQuickAddOpen(false)} disabled={qaSaving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={qaSaving}
            onClick={
              quickAddMode === 'item'
                ? saveQuickItem
                : quickAddMode === 'department'
                  ? saveQuickDepartment
                  : saveQuickCategory
            }>
            {qaSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Active Order Dialog ── */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth='md'
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle
          sx={{
            px: 4,
            py: 2,
            bgcolor: '#F8FAFC',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RestaurantIcon sx={{ color: C.accent, fontSize: 24 }} />
            <Box>
              <Typography variant='h6' fontWeight={700}>
                Active Order
              </Typography>
              {tableData?.tableNo && (
                <Typography variant='body2' color='text.secondary'>
                  Table {tableData.tableNo} • Order #
                  {tableData?.orderId || 'N/A'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={tableData?.status || 'Active'}
              size='small'
              color={tableData?.status === 'Occupied' ? 'error' : 'success'}
            />
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {orderItems.length > 0 ? (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, pl: 4, width: '32%' }}>
                      Item
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>
                      Qty
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      Price
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>
                      KOT
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700, pr: 4 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderItems.map((item, index) => {
                    const qty = Number(item.qty || 0);
                    // `item.itemPrice` / `item.price` from older rows can
                    // contain the line-total (price × qty), so showing it
                    // raw makes unit price look 5x too high. Derive unit
                    // from the authoritative `itemFinalPrice` when present
                    // — same logic the print/KOT path uses.
                    const apiFinal = Number(
                      item.itemFinalPrice ??
                        item.ItemFinalPrice ??
                        item.finalPrice ??
                        item.totalprice ?? // active-order DTO line total (#5)
                        item.Totalprice ??
                        0,
                    );
                    const apiUnit = Number(item.itemPrice || item.price || 0);
                    const lineTotal = apiFinal > 0 ? apiFinal : qty * apiUnit;
                    const unitPrice =
                      lineTotal > 0 && qty > 0 ? lineTotal / qty : apiUnit;
                    return (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          backgroundColor: item.activestatus
                            ? '#fff'
                            : '#FFF8F8',
                        }}>
                        <TableCell sx={{ pl: 4 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              textDecoration: item.activestatus
                                ? 'none'
                                : 'line-through',
                              color: item.activestatus
                                ? 'text.primary'
                                : 'error.main',
                            }}>
                            {item.name}
                          </Typography>
                          {item.subcategoryName && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: '#1D4ED8',
                                fontWeight: 600,
                              }}>
                              {item.subcategoryName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              px: 2,
                              py: 0.5,
                              borderRadius: 1.5,
                              bgcolor: C.pill,
                              fontWeight: 700,
                            }}>
                            {item.qty}
                          </Box>
                        </TableCell>
                        <TableCell align='right'>
                          <Typography sx={{ fontWeight: 600 }}>
                            {Companycurrency}
                            {unitPrice.toFixed(0)}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: item.activestatus ? C.accent : C.muted,
                            }}>
                            {Companycurrency}
                            {lineTotal.toFixed(0)}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={`#${item.kotNumber}`}
                            size='small'
                            sx={{
                              bgcolor: '#EFF6FF',
                              color: '#1D4ED8',
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell align='right' sx={{ pr: 4 }}>
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<EditIcon />}
                            onClick={() => {
                              setSelectedItem(item);
                              setActionType('edit');
                              setRemarkOpen(true);
                            }}
                            sx={{
                              mr: 1,
                              fontSize: '0.7rem',
                              borderRadius: 1.5,
                            }}>
                            Edit
                          </Button>
                          <Button
                            variant='contained'
                            color='error'
                            size='small'
                            startIcon={<CancelIcon />}
                            disabled={!item.activestatus}
                            onClick={() => {
                              setSelectedItem(item);
                              setActionType('cancel');
                              setRemarkOpen(true);
                            }}
                            sx={{ fontSize: '0.7rem', borderRadius: 1.5 }}>
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <RestaurantIcon sx={{ fontSize: 60, color: C.border, mb: 2 }} />
              <Typography variant='h6' color='text.secondary'>
                No Active Orders
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            px: 4,
            py: 2,
            borderTop: `1px solid ${C.border}`,
            bgcolor: '#F8FAFC',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='caption' color='text.secondary'>
              Total Items:{' '}
              <span style={{ color: C.accent, fontWeight: 700 }}>
                {orderItems.filter((i) => i.activestatus !== false).length}
              </span>
            </Typography>
            <Typography variant='h6' fontWeight={800} sx={{ color: C.accent }}>
              {Companycurrency}
              {orderItems
                .reduce((sum, it) => {
                  if (it.activestatus === false) return sum;
                  const f = Number(it.itemFinalPrice);
                  if (Number.isFinite(f) && f > 0) return sum + f;
                  const q = Number(it.qty || 0);
                  const p = Number(it.itemPrice || it.price || 0);
                  return sum + q * p;
                }, 0)
                .toFixed(0)}
              <Typography
                component='span'
                variant='caption'
                color='text.secondary'
                sx={{ ml: 1, fontWeight: 500 }}>
                Running Total
              </Typography>
            </Typography>
          </Box>
          <Button
            variant='contained'
            onClick={() => setOpenModal(false)}
            sx={{ bgcolor: C.header, borderRadius: 2 }}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* ── Remark Dialog ── */}
      <Dialog
        open={remarkOpen}
        onClose={() => setRemarkOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {actionType === 'edit' ? '✏️ Edit Remark' : '❌ Cancel Remark'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Enter Remark (Mandatory)'
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            autoFocus
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box
            sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              onClick={() => setRemarkOpen(false)}
              sx={{ color: C.muted }}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleRemarkSubmit}
              sx={{ bgcolor: C.header, borderRadius: 2 }}>
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* #8: Rider Assignment popup — shown after a Delivery order is placed */}
      <Dialog
        open={riderDialog.open}
        onClose={() => setRiderDialog((d) => ({ ...d, open: false }))}
        maxWidth='xs'
        fullWidth>
        <DialogTitle>🛵 Assign Delivery Rider — {riderDialog.tableno}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label='Rider Name'
              size='small'
              fullWidth
              value={riderDialog.name}
              onChange={(e) =>
                setRiderDialog((d) => ({ ...d, name: e.target.value }))
              }
            />
            <TextField
              select
              label='Delivery Status'
              size='small'
              fullWidth
              value={riderDialog.status}
              onChange={(e) =>
                setRiderDialog((d) => ({ ...d, status: e.target.value }))
              }>
              {['Assigned', 'Picked Up', 'On the Way', 'Delivered'].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Expected Delivery Time'
              size='small'
              fullWidth
              placeholder='e.g. 30 min / 8:45 PM'
              value={riderDialog.eta}
              onChange={(e) =>
                setRiderDialog((d) => ({ ...d, eta: e.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRiderDialog((d) => ({ ...d, open: false }))}>
            Skip
          </Button>
          <Button variant='contained' onClick={saveRiderAssignment}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
